/**
 * Devis Ouverture de Compte — green-spa.pro
 * ============================================================
 * Orchestrateur du parcours en 4 étapes :
 *  1. Achat — catalogue filtré par regex variantes, min 300 € HT
 *  2. PLV — produits tagués Divers/Divers PLV, budget 10 % du HT
 *  3. Cadeaux — catalogue complet, budget 10 % du HT
 *  4. Récap panier + formulaire coordonnées + case de confirmation
 *     → "Valider mon devis" envoie au backend
 *
 * Chargement catalogue : via /collections/<handle>/products.json
 * (endpoint public Shopify, scale jusqu'à plusieurs centaines de
 * produits sans toucher à la limite de boucle Liquid).
 *
 * Validation finale : POST vers backend Railway → Draft Order
 * Shopify → invoice_url → checkout natif.
 * ============================================================
 */
(function () {
  'use strict';

  const helpers = (typeof window !== 'undefined' && window.ProDevisHelpers) || null;
  if (!helpers) {
    console.error('[prodevis] ProDevisHelpers introuvable — fichier prodevis-helpers.js non chargé.');
    return;
  }
  const {
    parseDiscountRules,
    discountPctForProduct,
    compileWhitelistRegex,
    isSafeHttpUrl,
    filterVariants,
    hasFormatVariant,
    parseHeatExceptions,
    isHeatBlockedVariant,
    hasAnyTag,
    isProductCustomerEligible,
    parseCustomerTagMap,
    computeDiscountedHt,
    classifyVariantStock,
    computeTotalCents,
    selectionToLines,
    planCartImport,
    buildCartGroups,
    parseTierDiscounts,
    tierDiscountPct,
    meetsMinimum,
    computeBudgetCents,
    exceedsBudget,
    formatEuros,
    escapeHtml,
    fillPlaceholders,
    normalizeLocale,
    isTranslated,
    lookupMessage,
    translateMessage,
    resolveSettingText,
    translateLabel,
    catalogLocaleRoot,
    pickTranslatedTitle,
    translateFormatTitle,
  } = helpers;

  /* ── Récupération catalogue Shopify via JSON endpoint public ──────── */

  /**
   * Récupère TOUS les produits d'une collection Shopify en paginant.
   * /collections/<handle>/products.json renvoie max 50 produits par page sur
   * la plupart des boutiques (mais peut être plus bas selon la config).
   * Critère d'arrêt : page vide. On NE compare PAS à la limite demandée parce
   * que Shopify peut retourner moins (rate limit, cache CDN, etc).
   *
   * Chemin pris à la RACINE, pas sous /en/ ou /it/ : on lit le catalogue
   * dans la langue principale, celle des réglages techniques de la section
   * (type liquid, non traduits). Sous /en/, Shopify traduit les intitulés
   * de variantes (« Cabine 1 L » → « 1L cabin ») et les regex ne
   * correspondraient plus. Les tags, eux, ne se traduisent jamais.
   */
  async function fetchCollection(handle, { maxPages = 20 } = {}) {
    if (!handle) return [];
    if (fetchCollection._cache.has(handle)) return fetchCollection._cache.get(handle);

    const promise = (async () => {
      const all = [];
      for (let page = 1; page <= maxPages; page++) {
        const url = `/collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
        let data;
        try {
          const res = await fetch(url, { credentials: 'same-origin' });
          if (!res.ok) {
            console.warn(`[prodevis] Impossible de charger ${url} : ${res.status}`);
            break;
          }
          data = await res.json();
        } catch (e) {
          console.warn(`[prodevis] Erreur réseau sur ${url}`, e);
          break;
        }
        const products = data?.products || [];
        if (!products.length) break;
        all.push(...products);
      }
      // Un handle qui n'existe pas ne renvoie PAS 404 : Shopify répond 200
      // avec une liste vide, exactement comme une collection réelle mais
      // vide. Sans ce warn, une faute de frappe dans le sélecteur de
      // collection donne un catalogue vide sans le moindre indice.
      if (!all.length) {
        console.warn(`[prodevis] la collection « ${handle} » ne renvoie aucun produit — handle inexistant, collection vide, ou non publiée sur la boutique en ligne`);
      }
      return all.map(normalizeProduct);
    })();

    fetchCollection._cache.set(handle, promise);
    return promise;
  }
  fetchCollection._cache = new Map();

  /** Racine des adresses de la boutique dans la langue de la page (« / », « /en/ »…). */
  function shopRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /**
   * Panier Shopify de la cliente (`/cart.js`), pour le reprendre à
   * l'étape 1 du réassort (cf. ProDevis#_importShopifyCart). Lu sous la
   * racine de la langue : le panier est le même partout, ses intitulés
   * sont ceux de la page.
   *
   * Ne rejette jamais : échec réseau, délai dépassé ou réponse illisible
   * → null, et le module garde sa sélection.
   */
  async function fetchShopifyCart({ timeoutMs = 8000 } = {}) {
    const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
    try {
      const res = await fetch(`${shopRoot()}cart.js`, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' },
        signal: ctrl ? ctrl.signal : undefined,
      });
      if (!res.ok) return null;
      const cart = await res.json();
      return cart && Array.isArray(cart.items) ? cart : null;
    } catch (e) {
      console.warn('[prodevis] lecture du panier Shopify impossible', e);
      return null;
    } finally {
      if (timer) clearTimeout(timer);
    }
  }

  /**
   * Vide le panier Shopify une fois le devis créé à partir de lui : sans
   * ça, ses produits y resteraient (double commande, ou nouvel import au
   * prochain « Valider »).
   *
   * Par `sendBeacon` plutôt que `fetch` : les applis qui surveillent le
   * panier (Regios…) écoutent fetch et XHR, et l'une d'elles peut
   * recharger la page à chaque changement. Cet écran-ci, avec ses
   * boutons de paiement, serait alors perdu. `fetch` ne sert que de repli.
   */
  function clearShopifyCart() {
    const url = `${shopRoot()}cart/clear.js`;
    try {
      if (navigator.sendBeacon && navigator.sendBeacon(url)) return;
    } catch (e) { /* repli ci-dessous */ }
    fetch(url, { method: 'POST', credentials: 'same-origin', keepalive: true }).catch(() => {});
  }

  /**
   * Noms des produits et des variantes d'une collection tels que Shopify
   * les a traduits : la même collection, lue sous la racine de la langue
   * (« /en/collections/… »). Seuls ces noms servent, et seulement à
   * l'affichage (cf. ProDevis#_productName) : tout le reste se lit dans le
   * catalogue français de fetchCollection.
   *
   * Ne rejette jamais. Échec réseau, réponse illisible ou délai dépassé →
   * ce qui a été lu (rien, au pire) : les noms restent alors en français,
   * et une réponse lente ne retient pas l'affichage du catalogue.
   *
   * @returns {Promise<{products: Map<number,string>, variants: Map<number,string>}>}
   */
  async function fetchTranslatedNames(root, handle, { maxPages = 20, timeoutMs = 8000 } = {}) {
    const key = `${root}|${handle}`;
    if (fetchTranslatedNames._cache.has(key)) return fetchTranslatedNames._cache.get(key);

    const promise = (async () => {
      const names = { products: new Map(), variants: new Map() };
      for (let page = 1; page <= maxPages; page++) {
        const url = `${root}collections/${encodeURIComponent(handle)}/products.json?limit=250&page=${page}`;
        const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = ctrl ? setTimeout(() => ctrl.abort(), timeoutMs) : null;
        let data;
        try {
          const res = await fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined });
          if (!res.ok) break;
          data = await res.json();
        } catch (e) {
          console.warn(`[prodevis] noms traduits indisponibles (${url}) : noms en français`, e);
          break;
        } finally {
          if (timer) clearTimeout(timer);
        }
        const products = data?.products || [];
        if (!products.length) break;
        for (const p of products) {
          if (p?.title) names.products.set(Number(p.id), String(p.title));
          for (const v of p?.variants || []) {
            if (v?.title) names.variants.set(Number(v.id), String(v.title));
          }
        }
      }
      return names;
    })();

    fetchTranslatedNames._cache.set(key, promise);
    return promise;
  }
  fetchTranslatedNames._cache = new Map();

  /**
   * Convertit la structure Shopify JSON en structure interne plus simple.
   * Note importante : /products.json renvoie les prix en EUROS (string),
   * pas en cents. On convertit explicitement.
   */
  function normalizeProduct(p) {
    return {
      id: Number(p.id),
      handle: String(p.handle || ''),
      title: String(p.title || ''),
      vendor: String(p.vendor || ''),
      productType: String(p.product_type || ''),
      tags: Array.isArray(p.tags) ? p.tags : (p.tags ? String(p.tags).split(',').map((t) => t.trim()) : []),
      image: p.images?.[0]?.src || '',
      variants: (p.variants || []).map((v) => ({
        id: Number(v.id),
        title: String(v.title || ''),
        sku: String(v.sku || ''),
        priceCents: Math.round(Number(v.price) * 100),
        compareAtCents: v.compare_at_price ? Math.round(Number(v.compare_at_price) * 100) : 0,
        available: Boolean(v.available),
        // Photo propre à la variante (Shopify : `featured_image`). Sur ~2/3
        // du catalogue, chaque format/senteur a son visuel — on l'affiche
        // quand la cliente clique sur la ligne variante. Vide sinon : la
        // carte garde alors la photo principale du produit.
        image: v.featured_image?.src || '',
      })),
    };
  }

  /**
   * Test si un produit doit être exclu (blacklist).
   * Match contre title, vendor et handle (case-insensitive via la regex).
   */
  function isProductExcluded(product, regex) {
    if (!regex) return false;
    return regex.test(product.title)
        || regex.test(product.vendor)
        || regex.test(product.handle);
  }

  /* ── Textes du balisage (cf. ProDevis#_applyStaticI18n) ─────────── */

  /** Texte propre d'un élément : ses nœuds texte directs, sans ses enfants. */
  function ownText(el) {
    return [...el.childNodes].filter((n) => n.nodeType === 3).map((n) => n.data).join('');
  }

  /**
   * Remplace le texte propre d'un élément par `template`, jetons remplis
   * par `vars`. Ses enfants restent en place (icône d'un bouton, astérisque
   * d'un champ, mention « (optionnel) ») ; un enfant data-i18n-slot="nom"
   * se range à l'emplacement de {nom} — l'ordre des mots change d'une
   * langue à l'autre. Les espaces autour du texte d'origine sont gardées.
   */
  function setTranslatedText(el, template, vars) {
    const values = vars || {};
    const texts = [...el.childNodes].filter((n) => n.nodeType === 3 && n.data.trim());
    const anchor = texts[0] || null;
    const frag = document.createDocumentFragment();
    let buffer = anchor ? anchor.data.match(/^\s*/)[0] : '';
    const flush = () => {
      if (buffer) frag.appendChild(document.createTextNode(buffer));
      buffer = '';
    };
    for (const part of String(template).split(/(\{\w+\})/)) {
      const token = /^\{(\w+)\}$/.exec(part);
      const slot = token && el.querySelector(`[data-i18n-slot="${token[1]}"]`);
      if (slot) {
        flush();
        frag.appendChild(slot);
      } else if (token && Object.prototype.hasOwnProperty.call(values, token[1])) {
        buffer += String(values[token[1]]);
      } else {
        buffer += part;
      }
    }
    if (texts.length) buffer += texts[texts.length - 1].data.match(/\s*$/)[0];
    flush();
    if (anchor) el.insertBefore(frag, anchor);
    else el.appendChild(frag);
    texts.forEach((n) => n.remove());
  }

  /* ── Class principale ─────────────────────────────────────────────── */

  class ProDevis {
    constructor(sectionId) {
      this.cfg = window.proDevis[sectionId];
      if (!this.cfg) return;

      this.sid = sectionId;
      this.root = document.getElementById(`pd-root-${sectionId}`);
      if (!this.root) return;

      // Langue de la page : celle que la section lit dans Shopify
      // (`request.locale`), sinon l'attribut lang du document. Les textes
      // traduits viennent de prodevis-i18n.js ; le français reste dans ce
      // fichier, en littéral de repli (cf. `_t`, `_setting`, `_label` et
      // l'invariant « Langues » de CLAUDE.md).
      this.i18n = window.ProDevisI18n || null;
      this.lang = normalizeLocale(this.cfg.locale) || normalizeLocale(document.documentElement.lang) || 'fr';

      this.step = 1;
      this.maxStepReached = 1;
      this.purchaseSelection = new Map(); // variantId -> qty
      this.plvSelection = new Map();
      this.giftsSelection = new Map();
      // Sélection tirée du panier Shopify (cf. _importShopifyCart) : ce
      // panier sera vidé une fois le devis créé. Sauvegardé avec le reste.
      this.fromCart = false;
      // Remises auto cliente (flow réassort) : remplies par _fetchApplicableDiscount
      // après l'init — array car une cliente peut cumuler plusieurs remises
      // (ancienneté + segment + …). Trié par % desc.
      this.applicableDiscounts = [];
      // Stock réel des variantes (ouverture ET réassort — les deux backends
      // exposent /inventory) : rempli par _fetchInventory après que les 3
      // catalogues sont chargés. Map<variantId, { qty|null, policy }>.
      // qty=null = pas de tracking (on traite comme "en stock"). Si la
      // requête échoue, on garde le map vide et on ne griserait rien.
      this.inventoryById = new Map();

      // Labels "Produit — Variante" mémorisés en localStorage au moment
      // du `_saveState`. Sert à pouvoir citer le nom des produits retirés
      // par `_pruneStaleSelections` quand la variante a complètement
      // disparu du catalogue (elle n'est plus dans `variantsById` au moment
      // du prune, donc impossible de récupérer son nom à ce moment-là).
      this.cartLabels = new Map();

      // Mapping tag-produit → tag-client pour le gating par cohorte.
      // Parsé une fois ici depuis le réglage textarea de la section. Format :
      //   valdys=thalassovaldys (un mapping par ligne, # pour commenter).
      // Permet `tag-produit court` (valdys) ≠ `tag-client` (thalassovaldys)
      // sans renommer côté admin. Cf. `isProductCustomerEligible`.
      this.customerTagProductTagMap = parseCustomerTagMap(this.cfg.customerTagProductTagMap);

      // Signaux légers anti-bot collectés côté client puis re-vérifiés
      // serveur-side : timestamp d'arrivée sur la page (pour détecter les
      // submit < 2 s) et flag "interaction humaine vue" (mouse / touch /
      // clavier / scroll — n'importe lequel suffit, supporte mobile).
      // Mis à jour dans _trackHumanInteraction(), envoyé dans _submitDevis().
      this.pageLoadTs = Date.now();
      this.hasHumanInteraction = false;
      this._trackHumanInteraction();

      this.purchaseProducts = [];
      this.plvProducts = [];
      this.giftsProducts = [];
      // Étapes sans collection configurée (cf. _renderUnconfiguredCatalog).
      // Leur conteneur porte un message d'aide qu'aucun re-render ne doit
      // écraser — les tableaux ci-dessus étant vides, pas undefined, ils
      // ne suffisent pas à distinguer « pas configuré » de « rien à
      // afficher ».
      this.unconfiguredKinds = new Set();
      this.variantsById = new Map(); // pour totaux
      // Noms traduits par Shopify (hors français), id → nom : remplis par
      // _loadTranslatedNames, lus seulement à l'affichage (_productName,
      // _variantName). Vides en français.
      this.translatedNames = { products: new Map(), variants: new Map() };

      this.whitelistRegex = compileWhitelistRegex(this.cfg.variantWhitelistRegex);
      this.giftsRegex = compileWhitelistRegex(this.cfg.giftsVariantWhitelistRegex);
      // voyage_variant_whitelist_regex est distinct du gifts regex pour pouvoir
      // proposer un sous-ensemble de formats voyage sur l'étape 1 (ex: 30 ml
      // seulement) tout en gardant tous les formats voyage sur l'étape 3.
      // Si l'admin laisse le champ vide, on retombe sur le filtre cadeaux.
      this.voyageRegex = compileWhitelistRegex(this.cfg.voyageVariantWhitelistRegex) || this.giftsRegex;
      this.excludedRegex = compileWhitelistRegex(this.cfg.excludedProductsRegex);
      // Règle marchande "toutes les variantes" : les produits dont le titre
      // matche cette regex bypassent la whitelist variantes de l'étape 1 et
      // affichent TOUT (Revente, packs, lots…), minQty = 1. Cas d'usage :
      // Gommage Base au Sucre — la base nue n'est plus vendue en l'état,
      // ses variantes utiles sont Cabine + packs Revente + Revente 150 ml,
      // que la whitelist Cabine/lots masquerait en majorité.
      this.allVariantsProductsRegex = compileWhitelistRegex(this.cfg.allVariantsProductsRegex);
      this.plvTags = (this.cfg.plvTags || '').split(',').map((s) => s.trim()).filter(Boolean);
      // Cadeaux hors format "ml" : le filtre variantes de l'étape 3
      // (^(Revente )?\d+ ml$) écarte les produits sans format ml — dont le
      // LINGE (serviettes, pantalon, bandeau : variantes "Lot de 3",
      // "par 1", "Default Title"). Les produits portant un de ces tags
      // échappent au filtre et proposent toutes leurs variantes en stock.
      this.giftsExtraTags = (this.cfg.giftsExtraTags || '').split(',').map((s) => s.trim()).filter(Boolean);

      // Cadeaux automatiques (réassort) : produits offerts d'office au-delà
      // d'un seuil, en plus des cadeaux choisis à l'étape 3. C'est le
      // SERVEUR qui les pose sur le devis (il résout les SKU lui-même) ;
      // ici on se contente de les ANNONCER à la cliente dans le récap.
      // Les valeurs doivent donc rester alignées avec les env vars
      // REASSORT_AUTO_GIFT_* côté Railway.
      // ── Opération Noël ────────────────────────────────────────────────
      // Un onglet saisonnier en tête de la page 1, avec ses sous-onglets.
      //
      // Le périmètre est défini par une COLLECTION Shopify, pas par un tag :
      // sur les 12 PLV de Noël, 4 ne portent aucun tag « Noël 26 » (Bon
      // Cadeau, Cracker 2025, Papier de Soie, Sac pelliculé). Un tag seul
      // laisserait donc des trous, et « Divers PLV » ramasserait en plus tout
      // le PLV non-saisonnier. L'appartenance à la collection est le seul
      // discriminant fiable — c'est d'ailleurs le schéma des URL boutique
      // (/collections/noel/offre-noel).
      //
      // Les produits de cette collection reçoivent des TAGS SYNTHÉTIQUES
      // (__noel-offre / __noel-plv) que le moteur de catégories existant
      // consomme comme n'importe quel tag : aucune modification du rendu,
      // les sous-onglets sont un groupe classique de `category_order`.
      // Grille de remises DISTRIBUTEUR. Vide sur les flows pro (ouverture,
      // réassort) : `discountPctForProduct` renvoie alors 0 partout et
      // toute la mécanique ci-dessous est inerte — les pages existantes
      // gardent exactement leur comportement.
      this.distributorRules = parseDiscountRules(this.cfg.distributorDiscountRules || '');

      // Remise ENTREPRISE par paliers sur le total (flow cadeaux
      // d'entreprise). Vide sur les quatre flows pro : `_tierDiscountRow`
      // renvoie alors null et toute la mécanique reste inerte, comme la
      // grille distributeur l'est sur les flows qui ne s'en servent pas.
      // Doit rester identique à la grille Railway du même flow — c'est
      // cette page qui annonce la remise, le serveur qui la pose.
      this.tierDiscounts = parseTierDiscounts(this.cfg.tierDiscounts || '');

      // Régime de prix affiché. Les quatre flows pro travaillent en HT sur
      // un catalogue pro ; une boutique grand public affiche des prix TTC,
      // et y écrire « Total HT » serait faux — pas seulement inesthétique.
      // Défauts = les chaînes qui étaient en dur, donc rien ne bouge là où
      // la section ne dit rien.
      //
      // Ces libellés, et tous les textes de réglage ci-dessous, passent par
      // `_setting` : hors français, un défaut français jamais traduit dans
      // Shopify (« HT », « TTC »…) prend la traduction du dictionnaire.
      this.taxLabel = this._setting((this.cfg.taxLabel || 'HT').trim(), 'tax.excl', 'HT');
      this.taxNote = this._setting((this.cfg.taxNote || 'Taxes calculées au paiement').trim(), 'tax.note', 'Taxes calculées au paiement');
      // Le champ `siret` du formulaire porte un SIRET sur les flows pro et le
      // nom de la société sur la page cadeaux d'entreprise : même transport
      // (le nom `siret` est lu en dur, ici et côté serveur), autre libellé
      // dans le PDF. Défaut = l'étiquette qui était en dur.
      this.siretLabel = this._setting((this.cfg.siretLabel || 'SIRET').trim(), 'pdf.siret', 'SIRET');

      // Formats STRICTS : un produit qui a des formats, mais aucun autorisé
      // par la whitelist, disparaît de l'étape 1 au lieu de se rabattre sur
      // toutes ses variantes. Sur une boutique grand public, ce repli
      // ressortait précisément les produits 100 % cabine (Base Neutre 5 L,
      // Rose & Oud Cabine 1 L…) que la whitelist voulait écarter. Défaut
      // false : les flows pro gardent le repli tel qu'il était.
      this.strictFormats = this.cfg.strictFormats === true;

      // Vocabulaire des messages et du PDF. Les flows pro parlent de
      // « devis » et de green-spa.pro ; la page cadeaux d'entreprise vend
      // sur green-spa.com, « sans devis ». Défauts = les chaînes qui
      // étaient en dur, donc rien ne bouge là où la section ne dit rien.
      const tx = this.cfg.texts || {};
      const pick = (k, key, d) => this._setting(String(tx[k] || d), key, d);
      this.texts = {
        resetConfirm: pick('resetConfirm', 'texts.reset_confirm', 'Vider le devis et tout recommencer à zéro ?'),
        resetDone: pick('resetDone', 'texts.reset_done', 'Devis réinitialisé.'),
        emailRequired: pick('emailRequired', 'texts.email_required', 'Email professionnel requis pour valider le devis.'),
        missingId: pick('missingId', 'texts.missing_id', 'Identifiant de devis manquant — réessaie.'),
        pdfTitle: pick('pdfTitle', 'texts.pdf_title', 'Récapitulatif de votre devis'),
        pdfRefLabel: pick('pdfRefLabel', 'texts.pdf_ref', 'Référence Green Spa Pro'),
        pdfFooter: pick('pdfFooter', 'texts.pdf_footer', 'Devis automatique — green-spa.pro'),
        pdfFilePrefix: pick('pdfFilePrefix', 'texts.pdf_file', 'devis-green-spa'),
      };

      // Titres des groupes du récap (étape 4) et du PDF. Défauts = les
      // chaînes qui étaient en dur ; la page cadeaux d'entreprise n'a pas
      // de « cadeaux d'ouverture ».
      const rt = this.cfg.reviewTitles || {};
      const title = (k, key, d) => this._setting(String(rt[k] || d).trim(), key, d);
      this.reviewTitles = {
        purchase: title('purchase', 'step.products', 'Vos produits'),
        plv: title('plv', 'step.plv', 'Votre PLV offerte'),
        gifts: title('gifts', 'step.opening_gifts', "Vos cadeaux d'ouverture"),
        pdfPlv: title('pdfPlv', 'step.pdf_plv', 'PLV offerte'),
        pdfGifts: title('pdfGifts', 'step.pdf_opening_gifts', "Cadeaux d'ouverture"),
      };
      this.tierLabel = this._setting((this.cfg.tierLabel || 'Remise entreprise').trim(), 'tier.label', 'Remise entreprise');

      const noel = this.cfg.noel || {};
      this.noelCollection = String(noel.collection || '').trim();
      this.noelOfferTags = String(noel.offerTags || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);
      // Le PLV de Noël réutilise `plvTags` : un seul endroit définit ce qui
      // est du PLV dans la boutique, pas deux listes à garder synchronisées.
      this.noelPlvTags = this.plvTags.map((t) => t.toLowerCase());
      this.noelSortPriority = String(noel.sortPriority || '').split(',').map((s) => s.trim()).filter(Boolean);
      this.noelGroupLabel = String(noel.groupLabel || 'Noël 2026').trim();
      // Rempli par _loadNoelTags() : Map<productId, ['__noel-offre', …]>.
      this.noelTagsById = new Map();

      // Exception au filtre PLV de l'étape 1 : certains produits portent un
      // tag PLV (Divers…) mais doivent aussi être ACHETABLES. Sans ça, le
      // seul moyen serait de retirer leur tag dans Shopify, ce qui les
      // sortirait de la page 2 où ils sont offerts. Ciblage par SKU : c'est
      // ce que le marchand a sous la main, et c'est stable aux renommages.
      this.purchaseForcedSkus = (this.cfg.purchaseForcedSkus || '')
        .split(',').map((s) => s.trim().toUpperCase()).filter(Boolean);

      const ag = this.cfg.autoGifts;
      this.autoGiftSkus = ag ? String(ag.skus || '').split(',').map((s) => s.trim().toUpperCase()).filter(Boolean) : [];
      this.autoGiftMinCents = ag ? Math.round((Number(ag.minHt) || 0) * 100) : 0;
      this.autoGiftLabel = this._setting((ag && ag.label) || 'Vos cadeaux offerts', 'auto_gifts.title', 'Vos cadeaux offerts');

      // Bulle « précommande » sous le bouton panier (cf. _updatePreorderNote),
      // réglée par les sections ouverture et réassort. Réglage vide → pas de
      // bulle, dans TOUTES les langues : un texte vidé en français ne doit
      // pas ressortir traduit ailleurs (resolveSettingText traduirait le vide).
      const preorderRaw = String(this.cfg.preorderNote || '').trim();
      this.preorderNoteText = preorderRaw
        ? this._setting(preorderRaw, 'preorder.note', 'En moyenne, les produits en précommande sont livrés sous 30 jours.')
        : '';

      // Masquage des variantes "flacon vide" en rupture. Règle green-spa
      // (ouverture + réassort) : un flacon PET vide 250 ml n'a de sens que
      // s'il est en stock ; sinon on le retire du catalogue au lieu de le
      // griser. Deux regex pilotent la règle :
      //   - hideOosProductRegex : sur le titre PRODUIT (défaut : "huile")
      //   - hideOosVariantRegex : sur le titre VARIANTE (défaut : "250 ml")
      // Une variante est masquée SSI produit match ET variante match ET
      // rupture SÈCHE (out-of-stock, PAS preorder). Les deux regex doivent
      // être présentes pour activer la règle (sinon comportement inchangé).
      this.hideOosProductRegex = compileWhitelistRegex(this.cfg.hideOosProductRegex);
      this.hideOosVariantRegex = compileWhitelistRegex(this.cfg.hideOosVariantRegex);

      // Variantes exclues, TOUTES ÉTAPES confondues. S'applique après les
      // filtres par étape, et surtout aux produits SANS format (linge,
      // vêtements, accessoires) qui échappent au filtre variantes et
      // affichent donc tout : c'est le seul levier pour les restreindre.
      // Distributeur : écarte les « Lot de 3 / 5 / 10 » des serviettes et
      // pantalons, dont le prix contient déjà une remise de volume — la
      // remise distributeur viendrait s'y cumuler. Absent de la config
      // (flows pro) → null → aucun filtrage, comportement inchangé.
      this.excludedVariantRegex = compileWhitelistRegex(this.cfg.excludedVariantRegex);

      // Chaleur (saisonnier, ouverture + réassort). Deux niveaux, pilotés
      // par 2 toggles indépendants dans la section, ciblant les MÊMES
      // produits (heatWarning.productRegex, défaut "beurre") :
      //   • enabled → AVERTISSEMENT : popup à la 1re transition 0→positif
      //     sur une variante d'un produit chaleur-sensible, la cliente peut
      //     "Continuer quand même" (cf. _setQty + _showHeatWarning).
      //   • block   → BLOCAGE TOTAL : la carte produit est rendue
      //     non-achetable (aucun contrôle, message d'indisponibilité) dans
      //     _renderProductCard. L'ajout étant impossible, block prime
      //     naturellement sur le popup.
      // On compile la regex une fois si au moins un des deux est actif.
      // Côté flow sans réglage : cfg.heatWarning undefined → rien à faire.
      this.heatWarningRegex = null;
      this.heatBlockRegex = null;
      this.heatWarningShownProducts = new Set();
      const hw = this.cfg.heatWarning;
      if (hw && (hw.enabled || hw.block)) {
        const raw = String(hw.productRegex || '').trim();
        let compiled = null;
        if (raw) {
          try { compiled = new RegExp(raw, 'i'); }
          catch (e) { compiled = null; }
        }
        if (hw.enabled) this.heatWarningRegex = compiled;
        if (hw.block) this.heatBlockRegex = compiled;
      }
      // Réouverture PAR FORMAT (cf. isHeatBlockedVariant). Quand les
      // températures baissent, la gamme ne repart pas d'un bloc : les
      // petits contenants redeviennent vendables avant les gros, et
      // quelques références dont le beurre ne s'est pas resolidifié
      // restent fermées malgré un format rouvert. Vide → blocage
      // tout-ou-rien, comportement historique.
      this.heatAllowRegex = compileWhitelistRegex(this.cfg.heatWarning?.variantAllowRegex);
      this.heatAllowPairs = parseHeatExceptions(this.cfg.heatWarning?.allowPairs);
      this.heatExceptions = parseHeatExceptions(this.cfg.heatWarning?.exceptions);

      // Parse une seule fois la config de catégories — utilisée à la fois pour
      // le rendu et pour calculer voyageEligibleTags (les tags des produits
      // autorisés à proposer du voyage en étape 1).
      this.parsedCategoryConfig = this._parseCategoryConfig(
        this._withNoelGroup(this.cfg.categoryOrder || '')
      );
      this.voyageEligibleTags = this._computeVoyageEligibleTags();

      // Auto-save localStorage : restaure le panier précédent s'il est récent,
      // puis sauvegarde silencieusement à chaque interaction. L'état complet
      // (sélections, étape courante, formulaire client) est restauré avant
      // que _init ne déclenche le rendu, pour que la cliente retrouve son
      // devis directement au chargement de la page.
      this._stateVersion = 1;
      this._stateMaxAgeMs = 30 * 24 * 60 * 60 * 1000; // 30 jours
      this._restoreState();

      // Panier Shopify repris à l'étape 1 (réassort). Demandé par l'adresse
      // (`?panier=1`, que pose le bouton « Valider » du panier : cf.
      // theme/liquide-personnalise/alerte-reassort-panier.html) et permis
      // par la section (`importCart`). Lancé dès maintenant, en parallèle du
      // catalogue ; appliqué par _importShopifyCart.
      this._cartRequest = this._cartImportRequested() ? fetchShopifyCart() : null;

      this._init();
    }

    /* ── Détection interaction humaine (anti-bot léger) ─────────────── */

    /**
     * Installe des listeners passifs sur `document` pour repérer toute
     * interaction humaine (clavier, souris, touch, scroll, pointer). Dès
     * qu'on en voit une, on flip le flag et on déconnecte les listeners
     * pour ne plus rien payer en perfs. Sur mobile, `touchstart` et
     * `pointerdown` couvrent les cas où la cliente n'a pas de souris.
     */
    _trackHumanInteraction() {
      const events = ['mousemove', 'touchstart', 'pointerdown', 'keydown', 'scroll', 'wheel'];
      const onInteraction = () => {
        this.hasHumanInteraction = true;
        events.forEach((evt) => document.removeEventListener(evt, onInteraction, { passive: true, capture: true }));
      };
      events.forEach((evt) => document.addEventListener(evt, onInteraction, { passive: true, capture: true }));
    }

    /* ── Langues ────────────────────────────────────────────────────── */

    /**
     * Message dans la langue de la page. `fr` est le texte français
     * historique, affiché tel quel en français, et en repli partout où la
     * traduction manque (fichier, langue ou clé absents). Chaîne, ou
     * { one, other } quand le message s'accorde avec `vars.count`.
     */
    _t(key, fr, vars) {
      return translateMessage(this.i18n, this.lang, key, fr, vars);
    }

    /**
     * Texte d'un réglage de section (cf. resolveSettingText) : la valeur
     * reçue l'emporte, sauf si c'est un défaut français jamais traduit.
     * `value` est passée exactement comme le code historique l'affichait :
     * en français elle ressort intacte.
     */
    _setting(value, key, fr) {
      return resolveSettingText(this.i18n, this.lang, value, key, fr);
    }

    /** Nom d'onglet du catalogue traduit À L'AFFICHAGE (cf. translateLabel). */
    _label(label) {
      return translateLabel(this.i18n, this.lang, label);
    }

    /**
     * Nom d'un produit À L'AFFICHAGE : celui que Shopify a traduit, s'il
     * porte les mêmes nombres que le français (cf. pickTranslatedTitle).
     * Filtres, regex et remises lisent toujours `fr`, le titre du catalogue.
     */
    _productName(productId, fr) {
      return pickTranslatedTitle(fr, this.translatedNames.products.get(Number(productId)));
    }

    /**
     * Intitulé d'une variante À L'AFFICHAGE : un format (« Cabine 1 L »)
     * par le dictionnaire, le reste (senteurs, lots…) comme les produits.
     * « Default Title » n'est pas un nom : renvoyé tel quel.
     */
    _variantName(v) {
      const fr = String(v?.title || '');
      if (!fr || fr === 'Default Title') return fr;
      const format = translateFormatTitle(this.i18n, this.lang, fr);
      if (format != null) return format;
      return pickTranslatedTitle(fr, this.translatedNames.variants.get(Number(v.id)));
    }

    /** Noms affichés d'une entrée de `variantsById` : { product, variant }. */
    _lineNames(v) {
      return { product: this._productName(v.productId, v.productTitle || ''), variant: this._variantName(v) };
    }

    /**
     * Charge les noms traduits des collections du catalogue (cf.
     * fetchTranslatedNames). Rien à faire en langue principale : la racine
     * vaut « / ». Ne rejette jamais.
     */
    async _loadTranslatedNames(handles) {
      const root = catalogLocaleRoot(window.Shopify?.routes?.root);
      if (!root) return;
      const unique = [...new Set(handles.filter(Boolean))];
      const all = await Promise.all(unique.map((h) => fetchTranslatedNames(root, h)));
      for (const names of all) {
        names.products.forEach((title, id) => this.translatedNames.products.set(id, title));
        names.variants.forEach((title, id) => this.translatedNames.variants.set(id, title));
      }
    }

    /**
     * Valeurs des jetons des textes du balisage (data-i18n) : le régime
     * de taxe de la page, et les nombres que le Liquid y écrit, relus dans
     * la config (mêmes réglages). {tiers} est la phrase des paliers de la
     * remise entreprise, précédée d'une espace (vide sans grille).
     */
    _i18nVars() {
      return {
        tax: this.taxLabel,
        min: this.cfg.minOrderHt,
        plvPct: this.cfg.plvBudgetPct,
        giftPct: this.cfg.giftBudgetPct,
        tiers: this._tierSentence(),
      };
    }

    /** « Remise entreprise automatique : −15 % dès 200 €, … » ou ''. */
    _tierSentence() {
      if (!this.tierDiscounts.length) return '';
      const list = [...this.tierDiscounts]
        .sort((a, b) => a.minCents - b.minCents)
        .map((tier) => this._t('hint.tier', '−{pct} % dès {amount} €', { pct: tier.pct, amount: tier.minCents / 100 }))
        .join(', ');
      return ' ' + this._t('hint.tiers', 'Remise entreprise automatique : {list}.', { list });
    }

    /**
     * Textes écrits dans la section, hors français. Une seule passe, au
     * démarrage, avant que la barre récap, le toast et les popups ne
     * partent dans <body>.
     *
     *  • data-i18n="clé" : texte en dur dans le Liquid, remplacé par sa
     *    traduction. Les icônes restent en place ; un enfant marqué
     *    data-i18n-slot="nom" vient se ranger à l'emplacement de {nom}.
     *  • data-i18n-setting="id_du_réglage" : texte d'un réglage, rendu
     *    par le Liquid. Traduit SEULEMENT s'il vaut encore un défaut
     *    français connu (`sources` du dictionnaire) : une valeur
     *    personnalisée, ou déjà traduite dans Shopify, reste telle quelle.
     *  • data-i18n-aria-label / -title / -placeholder="clé" : l'attribut.
     *
     * Le français du Liquid est le repli : sans JS, en français, ou quand
     * la traduction manque, rien ne bouge.
     */
    _applyStaticI18n() {
      if (!isTranslated(this.i18n, this.lang)) return;
      const vars = this._i18nVars();
      for (const el of this.root.querySelectorAll('[data-i18n]')) {
        const tpl = lookupMessage(this.i18n, this.lang, el.getAttribute('data-i18n'));
        if (typeof tpl === 'string') setTranslatedText(el, tpl, vars);
      }
      for (const el of this.root.querySelectorAll('[data-i18n-setting]')) {
        const own = ownText(el);
        const tpl = this._setting(own);
        if (tpl !== own) setTranslatedText(el, tpl, vars);
      }
      for (const attr of ['aria-label', 'title', 'placeholder']) {
        for (const el of this.root.querySelectorAll(`[data-i18n-${attr}]`)) {
          const tpl = lookupMessage(this.i18n, this.lang, el.getAttribute(`data-i18n-${attr}`));
          if (typeof tpl === 'string') el.setAttribute(attr, fillPlaceholders(tpl, vars) || tpl);
        }
      }
    }

    /* ── Persistance localStorage ──────────────────────────────────── */

    _storageKey() {
      return `prodevis:state:${this.sid}`;
    }

    _restoreState() {
      let data;
      try {
        const raw = localStorage.getItem(this._storageKey());
        if (!raw) return;
        data = JSON.parse(raw);
      } catch { return; }
      if (!data || data.v !== this._stateVersion) return;
      if (typeof data.ts !== 'number' || Date.now() - data.ts > this._stateMaxAgeMs) {
        try { localStorage.removeItem(this._storageKey()); } catch {}
        return;
      }

      // Reconstruit les Maps depuis les objets sérialisés. On filtre toutes
      // les valeurs ineptes pour ne pas se faire injecter de bidule via
      // localStorage (un onglet malveillant pourrait y avoir écrit).
      const toMap = (obj) => {
        const m = new Map();
        if (obj && typeof obj === 'object') {
          for (const [k, v] of Object.entries(obj)) {
            const id = Number(k);
            const qty = Math.max(0, Math.min(999, Number(v) || 0));
            if (Number.isInteger(id) && id > 0 && qty > 0) m.set(id, qty);
          }
        }
        return m;
      };
      this.purchaseSelection = toMap(data.purchase);
      this.plvSelection = toMap(data.plv);
      this.giftsSelection = toMap(data.gifts);
      this.fromCart = data.fromCart === true;

      // Labels "Produit — Variante" persistés (cf. _saveState). Format :
      // { [variantId]: "Produit — Variante" }. Optionnel — paniers
      // sauvegardés avant ce mécanisme n'auront pas de `data.labels`.
      this.cartLabels = new Map();
      if (data.labels && typeof data.labels === 'object') {
        for (const [k, v] of Object.entries(data.labels)) {
          const id = Number(k);
          if (Number.isInteger(id) && id > 0 && typeof v === 'string') {
            this.cartLabels.set(id, v.slice(0, 200));
          }
        }
      }

      if (Number.isInteger(data.step)) {
        const step = Math.max(1, Math.min(4, data.step));
        this.step = step;
        this.maxStepReached = step;
      }

      // Formulaire client (l'élément est déjà dans le DOM via le liquid).
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      if (form && data.customer && typeof data.customer === 'object') {
        ['email', 'firstName', 'lastName', 'siret', 'siren', 'vatNumber', 'phone', 'poNumber'].forEach((name) => {
          const value = data.customer[name];
          if (typeof value !== 'string') return;
          const input = form.querySelector(`[name="${name}"]`);
          if (input) input.value = value.slice(0, 254);
        });
      }
    }

    _scheduleStateSave() {
      // Debounce 400 ms : évite de hammer localStorage sur chaque tick d'input
      // tout en gardant la latence imperceptible côté cliente.
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this._saveState(), 400);
    }

    _saveState() {
      try {
        const fromMap = (m) => {
          const obj = {};
          for (const [k, v] of m) {
            if (v > 0) obj[k] = v;
          }
          return obj;
        };
        const customer = {};
        const form = document.getElementById(`pd-customer-form-${this.sid}`);
        if (form) {
          ['email', 'firstName', 'lastName', 'siret', 'siren', 'vatNumber', 'phone', 'poNumber'].forEach((name) => {
            const v = (form.querySelector(`[name="${name}"]`)?.value || '').trim();
            if (v) customer[name] = v.slice(0, 254);
          });
        }
        // Snapshot des noms "Produit — Variante" pour les variantes du
        // panier au moment où on sauve. Permet de citer le nom dans le
        // toast de purge si la variante a disparu du catalogue au prochain
        // load (cf. `_pruneStaleSelections`). On ne stocke que les variant
        // IDs présents dans une des 3 sélections — pas d'effet de bord
        // sur la taille du localStorage pour les paniers vides.
        const labels = {};
        for (const sel of [this.purchaseSelection, this.plvSelection, this.giftsSelection]) {
          for (const id of sel.keys()) {
            const v = this.variantsById.get(id);
            if (!v) continue;
            const names = this._lineNames(v);
            const variantTitle = v.title && v.title !== 'Default Title' ? names.variant : '';
            const label = variantTitle ? `${names.product} — ${variantTitle}` : names.product;
            if (label) labels[id] = label.slice(0, 200);
          }
        }
        const data = {
          v: this._stateVersion,
          ts: Date.now(),
          step: this.step,
          purchase: fromMap(this.purchaseSelection),
          plv: fromMap(this.plvSelection),
          gifts: fromMap(this.giftsSelection),
          labels,
          customer,
        };
        if (this.fromCart) data.fromCart = true;
        localStorage.setItem(this._storageKey(), JSON.stringify(data));
      } catch {
        // Quota dépassé, mode privé Safari, localStorage désactivé… ignoré.
      }
    }

    _clearState() {
      try { localStorage.removeItem(this._storageKey()); } catch {}
    }

    /* ── Reprise du panier Shopify (réassort) ───────────────────────── */

    /**
     * La page s'ouvre-t-elle pour reprendre le panier Shopify ? Il faut la
     * demande (`panier` dans l'adresse) ET l'accord de la section
     * (`importCart`, réassort seulement) : ailleurs, une adresse bricolée
     * ne change rien.
     */
    _cartImportRequested() {
      if (this.cfg.importCart !== true) return false;
      try {
        return new URLSearchParams(window.location.search).has('panier');
      } catch {
        return false;
      }
    }

    /**
     * Retire `panier` de l'adresse : recharger la page ne doit pas reprendre
     * le panier une seconde fois, par-dessus ce que la cliente a changé.
     */
    _forgetCartImportFlag() {
      try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has('panier')) return;
        url.searchParams.delete('panier');
        window.history.replaceState(window.history.state, '', url.pathname + url.search + url.hash);
      } catch { /* adresse inchangée : sans conséquence */ }
    }

    /**
     * Variantes que l'étape 1 propose, avec leur minimum : règles de
     * `_displayablePurchaseVariants` et exclusion de variantes du rendu
     * (cf. _renderProductCard). Une ligne de panier hors de cette liste
     * n'aurait aucune carte où la cliente la verrait ou la retirerait.
     */
    _purchasableVariants() {
      const out = new Map();
      for (const product of this.purchaseProducts) {
        for (const v of this._displayablePurchaseVariants(product)) {
          if (!v || (this.excludedVariantRegex && this.excludedVariantRegex.test(v.title || ''))) continue;
          out.set(Number(v.id), Math.max(1, Number(v.minQty) || 1));
        }
      }
      return out;
    }

    /**
     * Inventaire demandé une seule fois, que l'appel vienne de la reprise
     * du panier (qui l'attend) ou de _init (qui ne l'attend pas).
     */
    _loadInventory() {
      if (!this._inventoryPromise) this._inventoryPromise = this._fetchInventory();
      return this._inventoryPromise;
    }

    /**
     * Reprend le panier Shopify à l'étape 1, une fois les catalogues
     * chargés (bouton « Valider » du panier, cf. _cartImportRequested).
     *
     * Choix de Geoffrey (29/10/2026) : le panier REMPLACE la sélection du
     * module, PLV et cadeaux compris, et la cliente arrive à l'étape 2 si
     * le minimum est atteint. Chaque ligne passe les règles d'une saisie à
     * la main : variante proposée à l'étape 1, pas bloquée par la chaleur,
     * pas en rupture sèche (une précommande se reprend). Le message nomme
     * ce qui n'a pas pu l'être.
     *
     * Panier vide ou illisible : rien ne change.
     */
    async _importShopifyCart() {
      const cart = await this._cartRequest;
      this._cartRequest = null;
      this._forgetCartImportFlag();
      try {
        if (!cart || !cart.items.length) return;

        this.purchaseSelection.clear();
        this.plvSelection.clear();
        this.giftsSelection.clear();

        // Stock réel avant de trier, 5 s au plus. Au-delà, /inventory finit
        // seul et _pruneStaleSelectionsByStock retire ensuite les ruptures,
        // avec son propre message.
        await Promise.race([this._loadInventory(), new Promise((resolve) => setTimeout(resolve, 5000))]);

        const purchasable = this._purchasableVariants();
        const { selection, refused } = planCartImport(cart.items, ({ variantId }) => {
          if (!purchasable.has(variantId)) return 'unavailable';
          if (this._isVariantBlocked(variantId)) return 'blocked';
          if (this._variantStockState(variantId) === 'out-of-stock') return 'stock';
          return 'ok';
        });
        // Format à minimum (voyage) : la quantité y monte, comme au premier
        // clic sur « + » (cf. _handleQtyButton).
        for (const [id, qty] of selection) this.purchaseSelection.set(id, Math.max(qty, purchasable.get(id)));
        this.fromCart = true;

        // Les cartes portent encore les quantités d'avant.
        for (const kind of ['purchase', 'plv', 'gifts']) {
          if (!this.unconfiguredKinds.has(kind)) this._renderCatalog(kind);
        }
        this.step = 1;
        this.maxStepReached = 1;
        this._renderStep();
        this._updateSummary();
        this._saveState();

        const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
        const ready = this.purchaseSelection.size > 0 && meetsMinimum(purchaseHt, this.cfg.minOrderHt);
        if (ready) this._goToStep(2);
        this._showToast(this._cartImportMessage(refused, ready), 12000);
      } finally {
        this._showLoading(false);
      }
    }

    /** Message de la reprise : ce qui est repris, ce qui ne l'est pas, la suite. */
    _cartImportMessage(refused, ready) {
      const count = this.purchaseSelection.size;
      const parts = [count
        ? this._t('import.done', { one: 'Votre panier a été repris : {count} produit.', other: 'Votre panier a été repris : {count} produits.' }, { count })
        : this._t('import.none', "Aucun produit de votre panier n'a pu être repris.")];
      if (refused.length) {
        const reasons = {
          stock: this._t('import.reason_stock', 'rupture de stock'),
          blocked: this._t('import.reason_blocked', 'indisponible pour le moment'),
          unavailable: this._t('import.reason_unavailable', 'non proposé sur cette page'),
        };
        const labels = refused.map(({ variantId, title, reason }) => {
          const v = this.variantsById.get(variantId);
          let label = title;
          if (v) {
            const names = this._lineNames(v);
            label = v.title && v.title !== 'Default Title' ? `${names.product} — ${names.variant}` : names.product;
          }
          return `${label || this._t('cart.variant', 'Variante {id}', { id: variantId })} (${reasons[reason]})`;
        });
        parts.push(this._t('import.refused', { one: 'Non repris : {list}.', other: 'Non repris : {list}.' }, {
          list: this._removedList(labels),
          count: labels.length,
        }));
      }
      if (ready) {
        parts.push(this._t('import.next_plv', 'Choisissez maintenant votre PLV offerte.'));
      } else if (count) {
        parts.push(this._t('import.below_min', 'Complétez-le pour atteindre le minimum de {min} € {tax}.', { min: this.cfg.minOrderHt, tax: this.taxLabel }));
      }
      return parts.join(' ');
    }

    /**
     * Retourne le set des tags Shopify (lowercase) dont les produits sont
     * autorisés à proposer des formats voyage en étape 1.
     *  • Si voyageEligibleGroup pointe sur un groupe défini → tags de ce groupe
     *  • Si vide ou groupe introuvable → null (aucune restriction)
     */
    _computeVoyageEligibleTags() {
      const wanted = String(this.cfg.voyageEligibleGroup || '').trim().toLowerCase();
      if (!wanted) return null;
      const group = this.parsedCategoryConfig.groups.find((g) => (g.name || '').toLowerCase() === wanted);
      if (!group) return new Set();
      const set = new Set();
      for (const cat of group.categories) {
        for (const t of cat.tags) set.add(t.toLowerCase());
      }
      return set;
    }

    /**
     * Applique les toggles d'affichage sur la config parsée :
     *  • Si show_nouveautes_tab est faux, retire la catégorie 'nouveautés'
     *    (transversale) et tout groupe qui en résulte vide.
     * Le résultat est une copie défensive — l'original reste intact pour
     * que voyageEligibleTags (calculé une fois en constructeur) ne change pas.
     */
    _filterParsedConfig(parsed) {
      if (this.cfg.showNouveautesTab) return parsed;
      const isNouveautes = (cat) => cat.tags.some((t) => t.toLowerCase() === 'nouveautés');
      const allCategories = parsed.allCategories.filter((c) => !isNouveautes(c));
      const groups = parsed.groups
        .map((g) => ({ ...g, categories: g.categories.filter((c) => !isNouveautes(c)) }))
        .filter((g) => g.categories.length);
      return { groups, allCategories };
    }

    /**
     * Charge la collection Noël et calcule les tags synthétiques.
     *
     * Doit être await AVANT les autres catalogues : le filtre PLV de la
     * page 1 et le tagging des cartes en dépendent.
     *
     * Un produit peut recevoir les DEUX tags (la Trousse Velours Noir est à
     * la fois une offre et un PLV) — les deux sous-onglets étant
     * non-exclusifs, elle apparaît dans les deux, ce qui est voulu.
     *
     * Échec réseau ou collection absente → Map vide : l'onglet Noël ne
     * s'affiche simplement pas, le reste du catalogue est intact.
     */
    async _loadNoelTags() {
      if (!this.noelCollection) return;
      if (!this.noelOfferTags.length && !this.noelPlvTags.length) return;
      try {
        const raw = await fetchCollection(this.noelCollection);
        for (const prod of raw || []) {
          const tags = (prod.tags || []).map((t) => String(t).toLowerCase());
          const synth = [];
          if (this.noelOfferTags.some((t) => tags.includes(t))) synth.push('__noel-offre');
          if (this.noelPlvTags.some((t) => tags.includes(t))) synth.push('__noel-plv');
          if (synth.length) this.noelTagsById.set(prod.id, synth);
        }
      } catch (e) {
        console.warn('[prodevis] collection Noël indisponible', e);
      }
    }

    /**
     * Insère le groupe Noël en tête de la config catégories.
     *
     * Indispensable : « Catégories groupées » est un champ que le marchand a
     * édité puis ENREGISTRÉ dans le customizer. Une valeur sauvegardée écrase
     * toujours le défaut du fichier de section, donc ajouter le groupe au
     * défaut ne suffisait pas — l'onglet n'apparaissait jamais en prod.
     *
     * On n'écrase rien : si la config mentionne déjà les tags Noël (le
     * marchand a ajouté ou personnalisé sa propre ligne), on la respecte.
     */
    _withNoelGroup(rawCategoryOrder) {
      const raw = String(rawCategoryOrder || '');
      if (!this.noelCollection) return raw;               // opération désactivée
      if (/__noel-(offre|plv)/.test(raw)) return raw;     // déjà configuré à la main
      const line = `${this.noelGroupLabel}: *__noel-offre=Offre de Noël, *__noel-plv=PLV`;
      return raw.trim() ? `${line}\n${raw}` : line;
    }

    /**
     * Catégories du groupe Noël (objets parsés).
     *
     * Repéré par ses TAGS synthétiques et non par le nom du groupe : le
     * marchand peut ainsi renommer l'onglet dans « Catégories groupées »
     * sans perdre l'habillage rouge ni l'ordre marchand.
     */
    _noelCategories(parsed) {
      const isNoelCat = (c) => c.tags.some((t) => String(t).toLowerCase().startsWith('__noel-'));
      const group = (parsed.groups || []).find((g) => g.categories.some(isNoelCat));
      return group ? group.categories.filter(isNoelCat) : [];
    }

    /** Vrai si ce groupe parsé est le groupe Noël (habillage rouge). */
    _isNoelGroup(group) {
      return (group.categories || []).some(
        (c) => c.tags.some((t) => String(t).toLowerCase().startsWith('__noel-'))
      );
    }

    /** Noms d'affichage de tous les onglets Noël — sert à l'habillage rouge. */
    _noelCategoryDisplays(parsed) {
      return this._noelCategories(parsed).map((c) => c.display);
    }

    /**
     * Onglets Noël recevant l'ordre marchand : uniquement ceux alimentés par
     * le tag synthétique des OFFRES. Le sous-onglet PLV garde le tri
     * alphabétique — y appliquer la même priorité remonterait par exemple la
     * Trousse en tête, alors qu'elle n'y est qu'un support parmi d'autres.
     */
    _noelSortedCategoryDisplays(parsed) {
      return this._noelCategories(parsed)
        .filter((c) => c.tags.some((t) => t.toLowerCase() === '__noel-offre'))
        .map((c) => c.display);
    }

    /** Produit appartenant à l'opération Noël (cf. _loadNoelTags). */
    _isNoelProduct(product) {
      return this.noelTagsById.has(product.id);
    }

    /**
     * Ajoute les tags synthétiques Noël à un produit, sans muter l'objet
     * d'origine (le cache de fetchCollection est partagé entre les étapes).
     */
    _withNoelTags(product) {
      const synth = this.noelTagsById.get(product.id);
      if (!synth) return product;
      return { ...product, tags: [...(product.tags || []), ...synth] };
    }

    async _init() {
      // Avant le portail : barre récap, toast et popups sont encore dans
      // la racine de la section, une seule passe les traduit tous.
      this._applyStaticI18n();
      this._portalFloatingElements();
      this._ensureCartUi();
      this._bindEvents();
      this._renderStep();
      this._updateSummary();
      // Reprise du panier Shopify : l'écran attend panier, catalogue et
      // stock (cf. _importShopifyCart) plutôt que de montrer la sélection
      // d'avant et de la remplacer sous les yeux de la cliente.
      if (this._cartRequest) this._showLoading(true, this._t('loading.cart', 'Reprise de votre panier…'));
      // Remise auto cliente (réassort) : fetch en arrière-plan, ne bloque
      // pas le rendu. Si pas d'email (flow ouverture) ou pas de backend,
      // skip silencieusement. Cf. _fetchApplicableDiscount.
      this._fetchApplicableDiscount();
      // Lecteur musique : tâche indépendante du catalogue, on la lance en parallèle
      // sans bloquer le rendu (échec silencieux si PocketBase est down ou désactivé).
      this._initMusic();

      const purchaseHandle = this.cfg.purchaseCollection;
      const plvHandle      = this.cfg.plvCollection || purchaseHandle;
      const giftsHandle    = this.cfg.giftsCollection || purchaseHandle;

      // Noms traduits (hors français) : lancés tout de suite, en parallèle
      // du catalogue français. Chaque étape les attend avant son premier
      // rendu, pour trier et afficher d'emblée dans la langue de la page.
      // Promesse jamais rejetée ; immédiate en français.
      const namesReady = this._loadTranslatedNames([purchaseHandle, plvHandle, giftsHandle]).catch(() => {});

      // Collection Noël d'abord : le filtre PLV de la page 1 et le tagging
      // des cartes en dépendent (cf. _loadNoelTags). Une seule requête, et
      // le cache de fetchCollection la partage si la collection Noël est
      // aussi la collection d'achat.
      await this._loadNoelTags();

      // Le cache de fetchCollection garantit qu'on ne re-télécharge pas
      // une même collection plusieurs fois, même si plusieurs étapes
      // pointent dessus.
      const tasks = [];

      // Filtre commun à toutes les étapes : blacklist par titre/vendor/handle.
      const notBlacklisted = (prod) => !isProductExcluded(prod, this.excludedRegex);

      // Filtre gating par tag client : produits taggés `<prefix><X>` ne sont
      // visibles que si la cliente connectée a `<X>` dans ses tags. Indépendant
      // de Locksmith — utile parce que Locksmith ne strippe pas l'endpoint
      // JSON `/collections/<handle>/products.json` qu'on consomme (testé en
      // prod sur green-spa.pro). Si pas de prefix configuré ou pas de tag
      // gating sur un produit, le filtre laisse passer.
      const customerEligible = (prod) => isProductCustomerEligible(
        prod.tags,
        this.cfg.customerTags,
        {
          prefix: this.cfg.customerOnlyTagPrefix,
          productToCustomerMap: this.customerTagProductTagMap,
        }
      );

      // Tri alphabétique (accent-insensible) — appliqué à tous les
      // catalogues pour une présentation cohérente entre les 3 étapes. Sur
      // le nom AFFICHÉ, dans la langue de la page : en français, le titre
      // du catalogue et l'ordre d'avant.
      const byTitleAsc = (a, b) => this._productName(a.id, a.title)
        .localeCompare(this._productName(b.id, b.title), this.lang, { sensitivity: 'base' });

      if (purchaseHandle) {
        tasks.push(Promise.all([fetchCollection(purchaseHandle), namesReady]).then(([raw]) => {
          // Page 1 : blacklist + gating client + (optionnellement) exclure
          // PLV + au moins 1 variante affichable.
          //
          // Le filtre PLV est piloté par `includePlvInPurchase` :
          //   • false (défaut ouverture) → PLV exclus du catalogue d'achat,
          //     ils ne sont disponibles qu'en page 2 comme cadeaux.
          //   • true (défaut réassort) → PLV inclus comme produits normaux,
          //     achetables en page 1. Ils restent dispos en page 2 comme
          //     cadeaux offerts (5 % du HT remisé). Les deux usages coexistent.
          //
          // Variantes affichables = régulières (Cabine, x3, Lot de...) +
          // voyage (30 ml MIN 3) pour les produits du groupe Corps uniquement.
          // Voir _displayablePurchaseVariants pour les règles complètes.
          this.purchaseProducts = raw
            .filter(notBlacklisted)
            .filter(customerEligible)
            // Les produits de l'opération Noël échappent au filtre PLV :
            // crackers, étuis, sacs et sachets sont des articles que la
            // cliente ACHÈTE pour sa boutique de Noël, pas seulement du PLV
            // offert. Sans cette exception, le sous-onglet « PLV » de
            // l'onglet Noël serait vide sur l'ouverture. Ils restent
            // évidemment disponibles en page 2.
            .filter((prod) => this.cfg.includePlvInPurchase
              || !hasAnyTag(prod.tags, this.plvTags)
              || this._isForcedIntoPurchase(prod)
              || this._isNoelProduct(prod))
            .filter((prod) => this._displayablePurchaseVariants(prod).length > 0)
            .sort(byTitleAsc)
            .map((prod) => this._withNoelTags(prod));
          this._indexVariants(this.purchaseProducts);
          this._renderCatalog('purchase');
          // Recompute totals : si un panier a été restauré depuis localStorage,
          // ses variantes ne sont indexées qu'ici, donc la summary bar passait
          // de "0,00 €" à la valeur réelle au moment où ce .then se résout.
          this._updateSummary();
        }));
      } else {
        this._renderUnconfiguredCatalog('purchase', "Collection — Catalogue d'achat (étape 1)");
      }

      if (plvHandle) {
        tasks.push(Promise.all([fetchCollection(plvHandle), namesReady]).then(([raw]) => {
          // Page 2 : blacklist + gating client + collection PLV (ou tag fallback)
          const filtered = raw.filter(notBlacklisted).filter(customerEligible);
          this.plvProducts = (this.cfg.plvCollection
            ? filtered
            : filtered.filter((prod) => hasAnyTag(prod.tags, this.plvTags))
          ).sort(byTitleAsc);
          this._indexVariants(this.plvProducts);
          this._renderCatalog('plv');
          this._updateSummary();
        }));
      } else {
        this._renderUnconfiguredCatalog('plv', 'Collection — PLV (étape 2)');
      }

      if (giftsHandle) {
        tasks.push(Promise.all([fetchCollection(giftsHandle), namesReady]).then(([raw]) => {
          // Page 3 : blacklist + gating client + sans PLV + variantes
          // voyage/revente uniquement.
          this.giftsProducts = raw
            .filter(notBlacklisted)
            .filter(customerEligible)
            .filter((prod) => !hasAnyTag(prod.tags, this.plvTags))
            .filter((prod) => this._displayableGiftsVariants(prod).length > 0)
            .sort(byTitleAsc);
          this._indexVariants(this.giftsProducts);
          this._renderCatalog('gifts');
          this._updateSummary();
        }));
      } else {
        this._renderUnconfiguredCatalog('gifts', 'Collection — Cadeaux (étape 3)');
      }

      await Promise.allSettled(tasks);

      // Panier Shopify repris : AVANT les nettoyages ci-dessous, qui
      // s'appliquent alors à cette sélection comme à une sélection restaurée.
      if (this._cartRequest) await this._importShopifyCart();

      // Auto-clean des sélections obsolètes : si une variante du panier
      // restauré (localStorage 30 jours) n'existe plus dans le catalogue
      // chargé — produit dépublié, archivé, supprimé — on la retire
      // silencieusement. Évite l'erreur backend "Variante X introuvable"
      // au moment de la soumission, qui bloquait la cliente sans recours.
      this._pruneStaleSelections();

      // Si le blocage chaleur est actif, on retire aussi les produits
      // bloqués d'un panier restauré (localStorage d'avant l'activation du
      // blocage) — sinon ils resteraient comptés sans contrôle pour les
      // enlever, et pourraient être soumis au backend.
      this._pruneBlockedSelections();

      // Idem pour les variantes exclues par la règle marchande (lots du
      // linge sur le distributeur) : elles ne s'affichent plus, elles ne
      // doivent donc plus compter dans un panier restauré.
      this._pruneExcludedVariantSelections();

      // Stock réel : fire-and-forget. Quand la réponse arrive on re-render
      // le catalogue en cours pour afficher les badges précommande / rupture.
      // Pas bloquant : si Shopify est lent ou KO, le catalogue reste
      // utilisable mais sans la distinction état stock.
      this._loadInventory();

      // Si l'utilisateur a été restauré directement sur l'étape 4 (récap),
      // le 1er rendu de _renderReview tournait sans variantsById. On le
      // refait une fois tous les catalogues chargés pour afficher les vrais
      // intitulés produit + prix.
      if (this.step === 4) this._renderReview();
    }

    /**
     * Purge des 3 sélections (purchase, PLV, cadeaux) les variant IDs qui
     * ne sont pas présents dans `variantsById` après le chargement du
     * catalogue. Sauve le state nettoyé en localStorage et re-render la
     * summary bar pour refléter les nouveaux totaux. Toast pour prévenir
     * la cliente — sans nom de produit ici car la variante a complètement
     * disparu du catalogue (productTitle / title non récupérables).
     */
    _pruneStaleSelections() {
      const isKnown = (id) => this.variantsById.has(Number(id));
      // On utilise le map `cartLabels` mémorisé au save précédent pour
      // citer les noms des produits retirés. Variant qui n'est ni dans
      // le nouveau catalogue ni dans les labels stockés (cart d'avant ce
      // mécanisme) → fallback "Variante <id>".
      const removedLabels = [];
      for (const sel of [this.purchaseSelection, this.plvSelection, this.giftsSelection]) {
        for (const id of [...sel.keys()]) {
          if (!isKnown(id)) {
            const label = this.cartLabels.get(id) || this._t('cart.variant', 'Variante {id}', { id });
            removedLabels.push(label);
            sel.delete(id);
          }
        }
      }
      if (!removedLabels.length) return;
      console.warn(`[prodevis] ${removedLabels.length} variante(s) obsolète(s) retirée(s) du panier restauré.`);
      this._saveState();
      this._updateSummary();
      if (this.step === 4) this._renderReview();
      this._showToast(this._t('toast.removed_unavailable', {
        one: 'Article retiré du panier (plus disponible) : {list}',
        other: 'Articles retirés du panier (plus disponibles) : {list}',
      }, { list: this._removedList(removedLabels), count: removedLabels.length }));
    }

    /**
     * Noms des articles retirés, pour un toast : trois au plus, puis
     * « et N autres ». Au-delà, le toast deviendrait illisible.
     */
    _removedList(labels) {
      const MAX_LISTED = 3;
      if (labels.length <= MAX_LISTED) return labels.join(', ');
      return this._t('list.more', { one: '{list}, et {count} autre', other: '{list}, et {count} autres' }, {
        list: labels.slice(0, MAX_LISTED).join(', '),
        count: labels.length - MAX_LISTED,
      });
    }

    /**
     * Retire des 3 sélections les variantes de produits actuellement
     * bloqués à l'achat (option chaleur "block"). Appelé après le
     * chargement du catalogue (variantsById peuplé). Ne fait rien si le
     * blocage n'est pas actif.
     */
    _pruneBlockedSelections() {
      if (!this.heatBlockRegex) return;
      let removed = 0;
      for (const sel of [this.purchaseSelection, this.plvSelection, this.giftsSelection]) {
        for (const id of [...sel.keys()]) {
          // Par VARIANTE : un beurre dont le 180 ml est rouvert mais le
          // 1 L toujours fermé ne doit perdre que sa ligne 1 L.
          if (this._isVariantBlocked(id)) {
            sel.delete(id);
            removed += 1;
          }
        }
      }
      if (!removed) return;
      this._saveState();
      this._updateSummary();
      if (this.step === 4) this._renderReview();
      this._showToast(this._t('toast.blocked_removed', 'Certains produits sont temporairement indisponibles à la commande et ont été retirés de votre panier.'));
    }

    /**
     * Retire des 3 sélections les variantes désormais exclues par
     * `excludedVariantRegex`.
     *
     * Le panier survit 30 jours en localStorage : sans ce nettoyage, un
     * « Lot de 3 » choisi avant l'activation de la règle resterait compté
     * dans les totaux et parti au devis, alors qu'aucune carte ne
     * l'affiche plus — une ligne fantôme, impossible à retirer par la
     * cliente puisqu'elle ne la voit nulle part.
     */
    _pruneExcludedVariantSelections() {
      if (!this.excludedVariantRegex) return;
      let removed = 0;
      for (const sel of [this.purchaseSelection, this.plvSelection, this.giftsSelection]) {
        for (const id of [...sel.keys()]) {
          const v = this.variantsById.get(Number(id));
          if (v && this.excludedVariantRegex.test(v.title || '')) {
            sel.delete(id);
            removed += 1;
          }
        }
      }
      if (!removed) return;
      this._saveState();
      this._updateSummary();
      if (this.step === 4) this._renderReview();
      this._showToast(this._t('toast.formats_removed', 'Certains formats ne sont plus proposés et ont été retirés de votre panier.'));
    }

    /**
     * Récupère le stock réel des variantes via le backend
     * (`POST /reassort/inventory`). Sert au front à distinguer rupture
     * sèche (= bloquer l'ajout) et rupture précommandable (= griser +
     * label, ajout possible).
     *
     * Fire-and-forget :
     *  • Pas de backendUrl ou pas de variantes connues → skip silencieux
     *  • Erreur réseau / 4xx / 5xx → on ne crash pas, on garde l'inventory
     *    vide et le rendu standard (toutes variantes achetables)
     *
     * Une fois le map rempli, on re-render le catalogue en cours pour
     * intégrer les badges.
     */
    async _fetchInventory() {
      const backend = (this.cfg.backendUrl || '').trim();
      if (!backend) return;
      const variantIds = [...this.variantsById.keys()];
      if (!variantIds.length) return;
      try {
        const url = backend.replace(/\/$/, '') + '/inventory';
        const res = await fetch(url, {
          method: 'POST',
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sectionId: this.cfg.sectionId,
            signature: this.cfg.requestSignature,
            signatureTs: this.cfg.requestSignatureTs,
            variantIds,
          }),
        });
        if (!res.ok) {
          console.warn(`[prodevis] inventory endpoint: ${res.status}`);
          return;
        }
        const data = await res.json();
        const inv = data?.inventory || {};
        for (const [k, v] of Object.entries(inv)) {
          const id = Number(k);
          if (!Number.isInteger(id)) continue;
          // v = { qty: number|null, policy: 'DENY'|'CONTINUE' }
          if (v && typeof v === 'object') {
            this.inventoryById.set(id, {
              qty: v.qty === null ? null : Number(v.qty),
              policy: v.policy === 'CONTINUE' ? 'CONTINUE' : 'DENY',
            });
          }
        }
        // Une fois l'inventory connu, on vérifie les variantes déjà dans
        // le panier restauré : rupture sèche retirée partout, précommande
        // retirée en plus de la PLV et des cadeaux (on n'offre pas ce
        // qu'on n'a pas). Cf. _pruneStaleSelectionsByStock.
        this._pruneStaleSelectionsByStock();
        // Re-render des TROIS catalogues, pas seulement l'étape en cours.
        //
        // Les trois sont rendus au chargement, avant que /inventory ait
        // répondu — donc avec un inventaire vide, où tout passe pour en
        // stock. `_renderStep` ne fait ensuite que basculer des classes
        // CSS : il ne re-rend rien. Ne rafraîchir que l'étape courante
        // (l'achat, dans le cas normal) laissait donc la PLV et les
        // cadeaux figés sur leur HTML d'origine, précommandes comprises.
        // Le filtre `_isOfferable` était bien appliqué — à un rendu que
        // personne ne relançait.
        //
        // On saute les étapes qui n'ont jamais chargé : sans collection
        // configurée, leur conteneur porte le message d'aide de
        // `_renderUnconfiguredCatalog`, et `_renderCatalog` le
        // remplacerait par un « Aucun produit disponible » qui n'explique
        // plus rien.
        for (const kind of ['purchase', 'plv', 'gifts']) {
          if (!this.unconfiguredKinds.has(kind)) this._renderCatalog(kind);
        }
        // Les états précommande viennent d'arriver : la bulle peut s'afficher
        // pour un panier restauré qui en contenait déjà.
        this._updatePreorderNote();
      } catch (e) {
        console.warn('[prodevis] inventory fetch a échoué', e);
      }
    }

    /**
     * Purge des 3 sélections les variantes qui sont passées en rupture
     * SÈCHE depuis que la cliente les a ajoutées (qty ≤ 0 + policy DENY).
     * Cible :
     *   • Cliente qui a un panier restauré depuis localStorage (TTL 30j)
     *     et dont une variante est aujourd'hui en rupture.
     *   • Cliente qui a ajouté une variante en début de session et dont
     *     le stock est tombé à 0 (race ultra rare).
     *
     * Le seuil dépend de l'étape :
     *   • ACHAT — seule la rupture sèche part. Les **précommandes**
     *     (CONTINUE + qty ≤ 0) restent : elles sont précommandables,
     *     c'est le choix merchant.
     *   • PLV et CADEAUX — tout ce qui n'est pas en stock réel part,
     *     précommandes comprises. On n'offre pas ce qu'on n'a pas.
     *
     * Sans cette purge, une bougie choisie avant l'arrivée de
     * l'inventory (ou dans un panier restauré, TTL 30 j) resterait
     * comptée dans les budgets et partirait au devis alors qu'aucune
     * carte ne l'affiche plus.
     *
     * Si quelque chose a été purgé, on prévient la cliente avec un toast
     * pour qu'elle sache pourquoi son panier a baissé. Sauve le state +
     * met à jour la summary bar.
     */
    _pruneStaleSelectionsByStock() {
      // On collecte les labels avant de supprimer pour pouvoir les citer
      // dans la toast. Format "Produit — Variante" si on a les deux, sinon
      // juste le nom du produit. La variante reste dans variantsById même
      // quand elle est en rupture (available: false), donc les noms sont
      // toujours disponibles ici (contrairement au _pruneStaleSelections
      // où la variante a complètement disparu du catalogue).
      const removedLabels = [];
      // Deux seuils selon l'étape : l'achat ne retire que la rupture sèche
      // (la précommande reste achetable), la PLV et les cadeaux exigent du
      // stock réel — on n'offre pas une précommande (cf. _isOfferable).
      for (const [sel, offeredOnly] of [
        [this.purchaseSelection, false],
        [this.plvSelection, true],
        [this.giftsSelection, true],
      ]) {
        for (const id of [...sel.keys()]) {
          const state = this._variantStockState(id);
          if (offeredOnly ? state !== 'in-stock' : state === 'out-of-stock') {
            const v = this.variantsById.get(id);
            const names = v ? this._lineNames(v) : { product: '', variant: '' };
            const productTitle = names.product || this._t('cart.variant', 'Variante {id}', { id });
            const variantTitle = v?.title && v.title !== 'Default Title' ? names.variant : '';
            removedLabels.push(variantTitle ? `${productTitle} — ${variantTitle}` : productTitle);
            sel.delete(id);
          }
        }
      }
      if (!removedLabels.length) return;
      console.warn(`[prodevis] ${removedLabels.length} variante(s) en rupture retirée(s) du panier.`);
      this._saveState();
      this._updateSummary();
      if (this.step === 4) this._renderReview();
      // Cap visuel à 3 entries (cf. _removedList) pour éviter une toast
      // géante quand le panier restauré contient beaucoup d'items obsolètes.
      this._showToast(this._t('toast.removed_stock', {
        one: 'Article retiré du panier (rupture de stock) : {list}',
        other: 'Articles retirés du panier (rupture de stock) : {list}',
      }, { list: this._removedList(removedLabels), count: removedLabels.length }));
    }

    /**
     * Wrapper d'instance autour du helper pur `classifyVariantStock` :
     * lit le stock + l'inventoryPolicy dans le map fetché par
     * `_fetchInventory` et délègue. Avant la résolution du fetch (map
     * vide), on retourne 'in-stock' → le catalogue reste utilisable même
     * si Shopify est lent à répondre.
     */
    _variantStockState(variantId) {
      if (!this.inventoryById.size || !this.inventoryById.has(variantId)) {
        return 'in-stock';
      }
      const entry = this.inventoryById.get(variantId);
      return classifyVariantStock(entry?.qty, entry?.policy);
    }

    /**
     * Une variante peut-elle être OFFERTE (étapes PLV et cadeaux) ?
     *
     * Plus strict que l'achat : il faut du stock RÉEL. Une précommande
     * s'achète — la cliente accepte d'attendre ce qu'elle paye — mais on
     * ne peut pas offrir ce qu'on n'a pas : le cadeau retiendrait toute
     * la commande, ou partirait en reliquat.
     *
     * `v.available` de Shopify ne suffit pas à le dire : il reste à true
     * sur une variante en précommande (qty ≤ 0 + « continuer à vendre »).
     *
     * Tant que `/inventory` n'a pas répondu, `_variantStockState` répond
     * 'in-stock' pour tout : le filtre se resserre au re-render qui suit
     * la réponse, et `_pruneStaleSelectionsByStock` nettoie ce qui avait
     * déjà été choisi. Si le fetch échoue, rien n'est filtré — on préfère
     * un catalogue trop permissif à une page vide.
     */
    _isOfferable(variantId) {
      return this._variantStockState(variantId) === 'in-stock';
    }

    /**
     * Déplace summary-bar, loading et toast dans <body> pour échapper aux
     * ancêtres transformés (Palo Alto, Dawn) qui cassent position:fixed.
     * Copie les variables CSS pour préserver le styling.
     */
    _portalFloatingElements() {
      const cs = getComputedStyle(this.root);
      const cssVars = [
        '--pd-primary', '--pd-primary-hover', '--pd-primary-light',
        '--pd-accent', '--pd-accent-dark', '--pd-bg', '--pd-white',
        '--pd-text', '--pd-text-muted', '--pd-border', '--pd-border-focus',
        '--pd-radius', '--pd-radius-sm', '--pd-radius-xs',
        '--pd-shadow', '--pd-shadow-hover', '--pd-transition',
      ];
      const portal = (id) => {
        const el = document.getElementById(id);
        if (!el) return null;
        cssVars.forEach((v) => {
          const val = cs.getPropertyValue(v).trim();
          if (val) el.style.setProperty(v, val);
        });
        document.body.appendChild(el);
        return el;
      };
      this.summaryBar = portal(`pd-summary-${this.sid}`);
      this.loadingEl = portal(`pd-loading-${this.sid}`);
      this.toastEl = portal(`pd-toast-${this.sid}`);
      this.musicEl = portal(`pd-music-${this.sid}`);
      this.heatWarningEl = portal(`pd-heat-warning-${this.sid}`);
      this.heatBlockEl = portal(`pd-heat-block-${this.sid}`);
    }

    /**
     * Cherche un élément d'abord dans la summary-bar (portalée) puis dans
     * la racine. Les éléments dont la cible vit dans la summary-bar
     * (purchase-total, plv-total, etc, prev-step, next-step) ne sont plus
     * accessibles via this.root.querySelector après le portal.
     */
    _q(selector) {
      return this.summaryBar?.querySelector(selector) || this.root.querySelector(selector);
    }

    _indexVariants(products) {
      for (const p of products) {
        for (const v of p.variants) {
          if (!this.variantsById.has(v.id)) {
            // productTags : nécessaire au calcul de la remise distributeur,
            // qui se décide sur les tags (et le titre) du PRODUIT parent.
            this.variantsById.set(v.id, { ...v, productId: p.id, productTitle: p.title, productTags: p.tags, productImage: p.image });
          }
        }
      }
    }

    /* ── Render ─────────────────────────────────────────────────────── */

    _renderStep() {
      this.root.querySelectorAll('[data-step]').forEach((el) => {
        const s = Number(el.dataset.step);
        el.classList.toggle('pd-step--active', s === this.step);
        el.classList.toggle('pd-stepper__step--active', s === this.step && el.classList.contains('pd-stepper__step'));
      });
      this.root.querySelectorAll('.pd-stepper__step').forEach((btn) => {
        const target = Number(btn.dataset.target);
        btn.disabled = target > this.maxStepReached;
      });
      if (this.step === 4) {
        this._renderReview();
        // Force la cliente à recocher la confirmation à chaque entrée :
        // si elle revient en arrière modifier le panier, on ne veut pas
        // qu'elle puisse valider sans relire le récap.
        const cb = document.getElementById(`pd-confirm-${this.sid}`);
        if (cb) cb.checked = false;
      }
      this._updateCta();
      this._updateSummary();
    }

    /**
     * Étape dont la collection n'est pas configurée.
     *
     * Sans ça, `_renderCatalog` n'est jamais appelé pour l'étape (cf. les
     * `if (handle)` de `_init`) : le « Chargement du catalogue… » du Liquid
     * tourne indéfiniment, et RIEN n'est loggé — une page fraîchement créée
     * dont on n'a pas encore rempli le sélecteur de collection ressemble
     * alors à un plantage du script, alors que c'est un réglage vide.
     *
     * À distinguer du handle FAUX : Shopify répond 200 avec zéro produit sur
     * `/collections/<handle-inexistant>/products.json`, donc ce cas-là passe
     * par `_renderCatalog` et affiche « Aucun produit disponible ».
     */
    _renderUnconfiguredCatalog(kind, settingLabel) {
      this.unconfiguredKinds.add(kind);
      const container = document.getElementById(`pd-catalog-${kind}-${this.sid}`);
      if (!container) return;
      container.removeAttribute('data-loading');
      // Le nom du réglage reste en français : c'est celui de l'éditeur de
      // thème du marchand.
      const what = this._t('catalog.unconfigured', "Catalogue non configuré : aucune collection n'est sélectionnée pour cette étape.");
      const where = this._t('catalog.unconfigured_setting', 'Réglage « {setting} », dans Personnaliser le thème.', { setting: settingLabel });
      container.innerHTML = `<div class="pd-empty-state">${escapeHtml(what)}<br>${escapeHtml(where)}</div>`;
      console.warn(`[prodevis] ${kind} : aucune collection configurée (réglage « ${settingLabel} »)`);
    }

    _renderCatalog(kind) {
      const container = document.getElementById(`pd-catalog-${kind}-${this.sid}`);
      if (!container) return;
      container.removeAttribute('data-loading');

      const products = kind === 'purchase'
        ? this.purchaseProducts
        : kind === 'plv' ? this.plvProducts : this.giftsProducts;

      if (!products || !products.length) {
        container.innerHTML = `<div class="pd-empty-state">${escapeHtml(this._t('catalog.empty', "Aucun produit disponible pour cette étape pour l'instant."))}</div>`;
        return;
      }

      // PLV : pas de groupement par catégorie (offre limitée et dédiée)
      if (kind === 'plv') {
        container.classList.remove('pd-catalog--grouped');
        container.innerHTML = products.map((p) => this._renderProductCard(p, kind)).filter(Boolean).join('');
        return;
      }

      container.classList.add('pd-catalog--grouped');

      // Pages 1 et 3 : groupement par tag avec syntaxe étendue.
      // On part de la config parsée en constructeur ; si le toggle Nouveautés
      // est désactivé, on filtre la catégorie transversale "nouveautés" (et
      // les groupes vides résultants) avant de rendre la nav.
      const parsed = this._filterParsedConfig(this.parsedCategoryConfig);
      const flatCategories = parsed.allCategories; // [{ tags, display, nonExclusive }]

      // Bucket par display label.
      //
      // Règle de matching :
      //  • Catégories "normales" (cat.nonExclusive = false) : first-match-wins
      //    classique. Un produit n'apparaît que dans la première catégorie
      //    exclusive qu'il matche.
      //  • Catégories "transversales" (nonExclusive = true, préfixe * dans
      //    la config) : un produit qui matche y est ajouté ET continue à
      //    être testé contre les catégories suivantes. Permet d'avoir un
      //    bandeau "Nouveauté" où un produit apparaît à la fois sous
      //    Nouveauté et sous Huiles de Massage.
      //  • Un produit qui ne matche aucune catégorie tombe dans "Autres".
      //
      // Les noms de catégories restent ceux, en français, de « Catégories
      // groupées » : clés des buckets, data-category, bascule d'onglet.
      // Seul le texte AFFICHÉ passe par `_label` (traduction d'affichage).
      const buckets = new Map();
      for (const cat of flatCategories) buckets.set(cat.display, []);
      const orphans = [];
      for (const p of products) {
        const productTags = (p.tags || []).map((t) => String(t).toLowerCase());
        let exclusiveAssigned = false;
        let anyAssigned = false;
        for (const cat of flatCategories) {
          const wanted = cat.tags.map((t) => t.toLowerCase());
          const matches = wanted.some((t) => productTags.includes(t));
          if (!matches) continue;
          if (cat.nonExclusive) {
            buckets.get(cat.display).push(p);
            anyAssigned = true;
          } else if (!exclusiveAssigned) {
            buckets.get(cat.display).push(p);
            anyAssigned = true;
            exclusiveAssigned = true;
          }
        }
        if (!anyAssigned) orphans.push(p);
      }
      if (orphans.length) {
        if (!buckets.has('Autres')) buckets.set('Autres', []);
        buckets.get('Autres').push(...orphans);
      }

      // Ordre marchand dans les onglets Noël. Ailleurs le tri alphabétique
      // convient, mais ici « Bougie » passerait avant « Offre 1 » alors que
      // les offres sont la tête de gondole. On applique donc des paliers :
      // un produit dont le titre commence par le 1er motif passe avant ceux
      // du 2e, etc. ; à palier égal on retombe sur l'alphabétique, et les
      // titres ne matchant aucun motif ferment la marche.
      if (this.noelSortPriority.length) {
        const tier = (p) => {
          const title = String(p.title || '').toLowerCase();
          const i = this.noelSortPriority.findIndex((m) => title.startsWith(m.toLowerCase()));
          return i === -1 ? this.noelSortPriority.length : i;
        };
        for (const cat of this._noelSortedCategoryDisplays(parsed)) {
          const list = buckets.get(cat);
          if (!list || list.length < 2) continue;
          // À palier égal, ordre des titres FRANÇAIS dans toutes les langues :
          // c'est le français qui numérote les offres, et un nom resté en
          // français (pas de traduction sûre) ne doit pas sortir du rang.
          list.sort((a, b) => (tier(a) - tier(b))
            || String(a.title || '').localeCompare(String(b.title || ''), 'fr', { sensitivity: 'base' }));
        }
      }

      const navGroups = parsed.groups.map((g) => ({ ...g, categories: g.categories.slice() }));
      if (orphans.length && !navGroups.some((g) => g.categories.some((c) => c.display === 'Autres'))) {
        navGroups.push({ name: '', categories: [{ tags: ['autres'], display: 'Autres' }] });
      }

      // Première catégorie active = première qui a des produits
      let firstActiveCat = null;
      for (const g of navGroups) {
        for (const c of g.categories) {
          if ((buckets.get(c.display) || []).length && !firstActiveCat) {
            firstActiveCat = c.display;
          }
        }
      }

      // Nav HTML : groupes + onglets. Factorisé : utilisé pour le top nav
      // (active = firstActiveCat) et pour le bottom nav de chaque section
      // (active = la catégorie de cette section).
      const buildNavHtml = (activeCategoryDisplay) => navGroups.map((group) => {
        const validCats = group.categories.filter((c) => (buckets.get(c.display) || []).length > 0);
        if (!validCats.length) return '';

        // Cas "LINGE > LINGE" : 1 seule catégorie avec un nom identique au
        // groupe → on collapse les deux pour éviter la redondance visuelle.
        const isRedundantSolo = group.name
          && validCats.length === 1
          && validCats[0].display.trim().toLowerCase() === group.name.trim().toLowerCase();

        const isSolo = !group.name || isRedundantSolo;
        const pill = (group.name && !isRedundantSolo)
          ? `<span class="pd-cat-group__pill">${escapeHtml(this._label(group.name))}</span>`
          : '';
        const tabs = validCats.map((c) => `
          <button type="button" class="pd-cat-tab${c.display === activeCategoryDisplay ? ' pd-cat-tab--active' : ''}"
                  data-category="${escapeHtml(c.display)}">${escapeHtml(this._label(c.display))}</button>
        `).join('');
        // Groupe Noël : habillage rouge saisonnier (pastille + onglets).
        const isNoel = this._isNoelGroup(group);
        return `
          <div class="pd-cat-group${isSolo ? ' pd-cat-group--solo' : ''}${isNoel ? ' pd-cat-group--noel' : ''}">
            ${pill}
            <div class="pd-cat-group__tabs">${tabs}</div>
          </div>
        `;
      }).filter(Boolean).join('');

      const noelDisplays = new Set(this._noelCategoryDisplays(parsed));

      const sectionsHtml = [];
      for (const [displayLabel, prods] of buckets) {
        if (!prods.length) continue;
        const cards = prods.map((p) => this._renderProductCard(p, kind)).filter(Boolean).join('');
        if (!cards) continue;
        const isActive = displayLabel === firstActiveCat;
        const noelClass = noelDisplays.has(displayLabel) ? ' pd-category--noel' : '';

        // Bottom nav : reprend la structure du top nav avec la catégorie
        // courante soulignée. Permet de switcher de catégorie sans remonter
        // en haut. Wrapper carte pour la séparer visuellement du contenu.
        const bottomNavHtml = `
          <div class="pd-cat-bottom-nav">
            <div class="pd-cat-bottom-nav__label">${escapeHtml(this._t('catalog.what_next', 'Et après ?'))}</div>
            <nav class="pd-cat-nav pd-cat-nav--bottom" aria-label="${escapeHtml(this._t('catalog.nav_bottom', 'Autres catégories'))}">${buildNavHtml(displayLabel)}</nav>
          </div>
        `;

        sectionsHtml.push(`
          <section class="pd-category${noelClass}" data-category="${escapeHtml(displayLabel)}"${isActive ? '' : ' hidden'}>
            <h4 class="pd-category__title">${escapeHtml(this._label(displayLabel))}</h4>
            <div class="pd-category__grid">${cards}</div>
            ${bottomNavHtml}
          </section>
        `);
      }

      container.innerHTML = `
        <nav class="pd-cat-nav" aria-label="${escapeHtml(this._t('catalog.nav', 'Catégories'))}">${buildNavHtml(firstActiveCat)}</nav>
        ${sectionsHtml.join('')}
      `;

      // Fonction partagée : collapse la catégorie courante, déplie la cible,
      // sync l'état actif sur TOUS les onglets (top + bottom de chaque
      // section). `scroll` est utilisé quand on clique depuis un bottom nav.
      const switchCategory = (cat, { scroll = false } = {}) => {
        container.querySelectorAll('.pd-category').forEach((sec) => {
          sec.hidden = sec.dataset.category !== cat;
        });
        container.querySelectorAll('.pd-cat-tab').forEach((t) => {
          t.classList.toggle('pd-cat-tab--active', t.dataset.category === cat);
        });
        if (scroll) {
          const newSection = container.querySelector(`.pd-category[data-category="${CSS.escape(cat)}"]`);
          if (newSection) {
            // Décalage de 80 px pour ne pas coller à un éventuel header sticky
            // du thème et laisser respirer le titre.
            const top = newSection.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
          }
        }
      };

      // Délégation unique : tous les .pd-cat-tab (top + bottom) sont
      // gérés ici. Le scroll vers la nouvelle section n'est déclenché
      // que si le clic vient d'un bottom nav (.pd-cat-nav--bottom) —
      // depuis le top nav la cliente est déjà en haut du catalogue.
      container.addEventListener('click', (e) => {
        const tab = e.target.closest('.pd-cat-tab');
        if (!tab) return;
        const isBottomClick = !!tab.closest('.pd-cat-nav--bottom');
        switchCategory(tab.dataset.category, { scroll: isBottomClick });
      });
    }

    /**
     * Parse la string de configuration des catégories.
     * Syntaxe :
     *   • Lignes séparées par \n ou ;
     *   • Chaque ligne : "GroupName: cat1, cat2, ..." OU "cat1, cat2, ..."
     *   • Chaque cat : "tag" | "tag=Label" | "tag1|tag2" | "tag1|tag2=Label"
     *   • Préfixe "*" sur une cat → matching non-exclusif : un produit qui
     *     match cette catégorie est ajouté ICI ET continue à être testé
     *     contre les autres catégories. Utile pour des bandeaux transverses
     *     comme "Nouveauté" qui doivent apparaître à la fois dans leur
     *     onglet dédié et dans la catégorie métier du produit.
     *     Ex : "*Nouveauté", "*new=Nouveauté", "*Promo|Sale=Promotions".
     *
     * Retourne { groups: [{name, categories: [{tags, display, nonExclusive}]}], allCategories: [...] }
     */
    _parseCategoryConfig(raw) {
      const lines = String(raw || '')
        .split(/[\n;]+/)
        .map((l) => l.trim())
        .filter(Boolean);
      const groups = [];
      const allCategories = [];
      for (const line of lines) {
        const colonIdx = line.indexOf(':');
        let name = '';
        let rawCats;
        if (colonIdx > 0) {
          name = line.slice(0, colonIdx).trim();
          rawCats = line.slice(colonIdx + 1);
        } else {
          rawCats = line;
        }
        const cats = [];
        for (let entry of rawCats.split(',').map((c) => c.trim()).filter(Boolean)) {
          // Préfixe * = non-exclusif. On le strip avant le reste du parsing.
          let nonExclusive = false;
          if (entry.startsWith('*')) {
            nonExclusive = true;
            entry = entry.slice(1).trim();
          }
          const eqIdx = entry.indexOf('=');
          let tagPart, display;
          if (eqIdx > 0) {
            tagPart = entry.slice(0, eqIdx).trim();
            display = entry.slice(eqIdx + 1).trim();
          } else {
            tagPart = entry;
            display = entry;
          }
          const tags = tagPart.split('|').map((t) => t.trim()).filter(Boolean);
          if (tags.length && display) {
            cats.push({ tags, display, nonExclusive });
            allCategories.push({ tags, display, nonExclusive });
          }
        }
        if (cats.length) groups.push({ name, categories: cats });
      }
      return { groups, allCategories };
    }

    /**
     * Pour la page 1 (achat), retourne les variantes à afficher pour un
     * produit, annotées d'un `minQty` (minimum de commande par ligne).
     *
     * Trois cas :
     *  1. Variantes "régulières" (whitelist match : Cabine, x3, par 3, Lot de...) :
     *     minQty = 1.
     *  2. Variantes "voyage" (voyage regex match : 30 ml unitaire) :
     *     minQty = voyage_min_qty (défaut 3). Restreintes aux produits du
     *     groupe `voyage_eligible_group` (défaut "Corps") — les produits
     *     Visage/Linge/Autres n'ont pas accès au voyage en étape 1.
     *  3. Fallback "produit sans format" (linge, actifs, enveloppements
     *     avec variantes XS/M/L ou Default Title) : toutes les variantes
     *     disponibles, minQty = 1.
     *
     * Les variantes apparaissent dans l'ordre : régulières d'abord, puis
     * voyage. Une variante qui matche les deux regex est annotée régulière
     * (minQty = 1) pour ne pas pénaliser inutilement la cliente.
     */
    _displayablePurchaseVariants(product) {
      const variants = product.variants || [];
      const voyageMinQty = Math.max(1, Number(this.cfg.voyageMinQty) || 3);

      // NB : on NE filtre PAS sur `v.available`. Les variantes en rupture
      // sèche (qty ≤ 0 + inventoryPolicy DENY → available: false côté
      // Shopify) doivent rester VISIBLES dans le catalogue, mais grisées
      // avec un badge "Rupture de stock" et l'input qty bloqué. Le rendu
      // dans `_renderProductCard` + le helper `classifyVariantStock`
      // gèrent ces 3 états (in-stock / preorder / out-of-stock).

      // Détecte si le produit a des variantes AVEC FORMAT (huiles, gommages,
      // beurres, etc.). La whitelist/voyage regex existe pour distinguer
      // Cabine 500 ml de Revente 30 ml : sans format dans aucune variante, ce
      // filtrage n'a aucun sens (linge, actifs, accessoires) et exclurait
      // à tort des variantes valides type "Lot de 3" si la regex marchand
      // ne les couvre pas. Dans ce cas on affiche tout, minQty=1.
      //
      // On teste ml, L, g ET kg. Ne chercher que « ml » laissait passer les
      // formats en litres et en grammes — les 5 L, 1 L et 150 g tombaient
      // dans le fallback, donc leurs multipacks « par 3 - remise de 10 % »
      // s'affichaient malgré une whitelist qui les écarte. Sur le
      // distributeur, la remise catégorie venait alors se cumuler avec la
      // remise multipack déjà incluse dans le prix.
      // Règle marchande "toutes les variantes" (cf. constructeur) : bypass
      // de la whitelist pour les produits ciblés. Prioritaire sur le
      // filtrage ml/voyage — le produit est vendu tel quel, minQty = 1.
      // Les ruptures restent gérées par le grisage habituel au rendu.
      if (this.allVariantsProductsRegex && this.allVariantsProductsRegex.test(product.title || '')) {
        return variants.filter(Boolean).map((v) => ({ ...v, minQty: 1 }));
      }

      if (!hasFormatVariant(variants)) {
        return variants.filter(Boolean).map((v) => ({ ...v, minQty: 1 }));
      }

      // voyageEligibleTags = null → aucune restriction (admin a vidé le champ)
      // voyageEligibleTags = Set vide → groupe introuvable, aucun produit éligible
      // sinon → on n'autorise le voyage que pour les produits avec au moins
      // un de ces tags (matching case-insensitive).
      let isVoyageEligible = true;
      if (this.voyageEligibleTags) {
        if (this.voyageEligibleTags.size === 0) {
          isVoyageEligible = false;
        } else {
          const productTags = (product.tags || []).map((t) => String(t).toLowerCase());
          isVoyageEligible = productTags.some((t) => this.voyageEligibleTags.has(t));
        }
      }

      const matchesRegular = (v) =>
        this.whitelistRegex && v.title !== 'Default Title' && this.whitelistRegex.test(v.title);
      const matchesVoyage = (v) => isVoyageEligible
        && this.voyageRegex && v.title !== 'Default Title' && this.voyageRegex.test(v.title);

      const regular = [];
      const voyage = [];
      for (const v of variants) {
        if (!v) continue;
        if (matchesRegular(v)) {
          regular.push({ ...v, minQty: 1 });
        } else if (matchesVoyage(v)) {
          voyage.push({ ...v, minQty: voyageMinQty });
        }
      }

      const combined = [...regular, ...voyage];
      if (combined.length) return combined;

      // Formats stricts (page grand public) : des formats existent mais aucun
      // n'est autorisé → produit retiré du catalogue (filtre `length > 0` à
      // l'appel). Sinon, repli historique : toutes les variantes, sans min.
      if (this.strictFormats) return [];
      return variants.filter(Boolean).map((v) => ({ ...v, minQty: 1 }));
    }

    /**
     * Produit forcé dans le catalogue d'achat malgré un tag PLV (cf.
     * constructeur). Vrai dès qu'UNE de ses variantes porte un des SKU
     * configurés — un produit mono-variante comme la trousse suffit donc.
     *
     * N'a aucun effet quand `includePlvInPurchase` est actif (réassort) :
     * les PLV y sont déjà achetables.
     */
    _isForcedIntoPurchase(product) {
      if (!this.purchaseForcedSkus.length) return false;
      return (product.variants || []).some(
        (v) => this.purchaseForcedSkus.includes(String(v.sku || '').trim().toUpperCase())
      );
    }

    /**
     * Variantes affichables sur l'étape 3 (cadeaux).
     *
     * Par défaut : filtre "format voyage / revente unitaire" (giftsRegex),
     * qui exclut Cabine et multipacks.
     *
     * Exception "cadeaux hors format ml" (cf. constructeur) : un produit
     * portant un des tags `giftsExtraTags` (défaut : Linge) propose toutes
     * ses variantes EN STOCK, y compris "Default Title" (bandeau) et les
     * lots ("Lot de 3"), que giftsRegex écarterait. Sans cette exception
     * le linge n'apparaît pas du tout sur la page cadeaux : aucune de ses
     * variantes ne matche le format ml, donc le produit entier est filtré.
     */
    _displayableGiftsVariants(product) {
      if (this.giftsExtraTags.length && hasAnyTag(product.tags, this.giftsExtraTags)) {
        return (product.variants || []).filter((v) => v && v.available);
      }
      return filterVariants(product.variants, this.giftsRegex);
    }

    /**
     * HT après remises automatiques cliente (ancienneté, grossiste…).
     * Même formule que le serveur (`discountedHt`) et que l'affichage du
     * récap : purchaseHt − somme des remises, en cents.
     */
    _discountedHtCents(purchaseHt) {
      const discounts = this.applicableDiscounts || [];
      if (!discounts.length || purchaseHt <= 0) return purchaseHt;
      let total = 0;
      for (const d of discounts) total += Math.round(purchaseHt * d.percentage / 100);
      return Math.max(0, purchaseHt - total);
    }

    /**
     * Résout les SKU des cadeaux automatiques en lignes affichables, à
     * partir des catalogues déjà chargés (achat / PLV / cadeaux).
     *
     * Les jumeaux « 🎁 … (100% off) » sont ignorés, comme côté serveur :
     * on annonce le produit réel et sa valeur offerte. Un SKU introuvable
     * est simplement omis (le serveur log de son côté).
     */
    _autoGiftLines() {
      if (!this.autoGiftSkus.length) return [];
      const wanted = new Set(this.autoGiftSkus);
      const found = new Map();
      for (const pool of [this.purchaseProducts, this.plvProducts, this.giftsProducts]) {
        for (const prod of pool || []) {
          if (/\(\s*100\s*%\s*off\s*\)/i.test(prod.title || '')) continue;
          for (const v of prod.variants || []) {
            const sku = String(v.sku || '').trim().toUpperCase();
            if (!sku || !wanted.has(sku) || found.has(sku)) continue;
            // Lignes seulement affichées (récap, PDF) : noms dans la langue de la page.
            found.set(sku, { productTitle: this._productName(prod.id, prod.title), variantTitle: this._variantName(v), priceCents: v.priceCents });
          }
        }
      }
      // On respecte l'ordre configuré des SKU.
      return this.autoGiftSkus.map((s) => found.get(s)).filter(Boolean);
    }

    /**
     * Note du bloc « cadeaux automatiques », à l'écran comme dans le PDF :
     * seuil atteint, ou montant qui manque. `fmt` formate les montants
     * (formatEuros à l'écran, sa variante sans espace insécable au PDF).
     */
    _autoGiftNote(reached, missingCents, fmt) {
      const vars = {
        amount: fmt(this.autoGiftMinCents),
        missing: fmt(missingCents),
        threshold: fmt(this.autoGiftMinCents),
        tax: this.taxLabel,
      };
      return reached
        ? this._t('review.auto_gifts_reached', 'Offerts : votre commande atteint {amount} {tax}.', vars)
        : this._t('review.auto_gifts_missing', 'Encore {missing} {tax} pour en bénéficier (dès {threshold}).', vars);
    }

    /**
     * Conserve l'ancienne API pour les autres usages (PLV, cadeaux) où il n'y
     * a pas de notion de voyage / minimum. Retourne juste les variantes
     * filtrées par le regex, sans annotation.
     */
    _displayableVariants(product, regex) {
      const filtered = filterVariants(product.variants, regex);
      if (filtered.length) return filtered;
      return (product.variants || []).filter((v) => v.available);
    }

    _renderProductCard(product, kind) {
      let variants = kind === 'purchase'
        ? this._displayablePurchaseVariants(product)
        : kind === 'gifts'
          ? this._displayableGiftsVariants(product)
          : product.variants.filter((v) => v.available);

      // Étapes 2 (PLV) et 3 (cadeaux) : STOCK RÉEL exigé, cf. _isOfferable.
      // `v.available` ne suffit pas — Shopify le laisse à true sur une
      // variante en précommande, donc les bougies parfumées à -3 en stock
      // restaient offrables.
      if (kind !== 'purchase') {
        variants = variants.filter((v) => v && v.available && this._isOfferable(v.id));
      }

      // Règle "flacon vide en rupture" (cf. constructeur) : sur un produit
      // ciblé (défaut : huile), on retire les variantes ciblées (défaut :
      // 250 ml) qui sont en rupture sèche. Contrairement au grisage OOS
      // habituel, ici la variante disparaît complètement. Si le produit
      // n'a plus aucune variante affichable, la carte entière est masquée
      // par le `return ''` juste en dessous.
      if (this.hideOosProductRegex && this.hideOosVariantRegex && this.hideOosProductRegex.test(product.title || '')) {
        variants = variants.filter((v) => !(
          this.hideOosVariantRegex.test(v.title || '') &&
          this._variantStockState(v.id) === 'out-of-stock'
        ));
      }

      // Variantes exclues (cf. constructeur) : dernier filtre, appliqué aux
      // trois étapes. Un produit qui n'a plus rien à proposer disparaît via
      // le `return ''` ci-dessous — voulu : s'il ne reste que des lots qu'on
      // refuse, le produit n'est pas commandable.
      if (this.excludedVariantRegex) {
        variants = variants.filter((v) => !this.excludedVariantRegex.test(v.title || ''));
      }

      if (!variants.length) return '';

      // Blocage chaleur (saisonnier) : le produit reste VISIBLE avec ses
      // variantes et ses boutons, toute tentative d'ajout ouvrant un popup
      // d'indisponibilité (cf. _handleQtyButton / _showHeatBlock).
      // Volontairement PAS un simple grisage : la cliente voit le produit
      // et comprend pourquoi via le popup.
      //
      // Désormais PAR VARIANTE : un beurre peut avoir son 180 ml rouvert et
      // son 1 L encore fermé. Le bandeau rouge de la carte n'a donc de sens
      // que si TOUTES les variantes affichées sont bloquées ; sinon on
      // marque les lignes concernées, une par une.
      const blockedIds = new Set(
        variants.filter((v) => this._isVariantBlocked(v.id)).map((v) => v.id)
      );
      const isBlocked = blockedIds.size === variants.length && blockedIds.size > 0;

      const selection = this._selectionFor(kind);
      // Nom affiché (traduit hors français) ; les règles ci-dessus ont lu
      // le titre français du catalogue.
      const productName = this._productName(product.id, product.title);

      const variantRows = variants.map((v) => {
        const qty = selection.get(v.id) || 0;
        const minQty = Math.max(1, Number(v.minQty) || 1);
        const minBadge = minQty > 1
          ? `<span class="pd-variant-row__min" title="${escapeHtml(this._t('card.min_title', 'Minimum {count} unités par commande', { count: minQty }))}">${escapeHtml(this._t('card.min_badge', 'min {count}', { count: minQty }))}</span>`
          : '';

        // État stock (réassort) : in-stock / preorder / out-of-stock.
        // - preorder : grisé + label, mais ajout au panier autorisé
        // - out-of-stock : grisé + label, ajout bloqué (input désactivé)
        const stockState = this._variantStockState(v.id);
        const stateClass = stockState !== 'in-stock' ? ` pd-variant-row--${stockState}` : '';
        const stateBadge = stockState === 'preorder'
          ? `<span class="pd-variant-row__state pd-variant-row__state--preorder">${escapeHtml(this._t('card.preorder', 'Précommande'))}</span>`
          : stockState === 'out-of-stock'
            ? `<span class="pd-variant-row__state pd-variant-row__state--out-of-stock">${escapeHtml(this._t('card.out_of_stock', 'Rupture de stock'))}</span>`
            : '';
        const isDisabled = stockState === 'out-of-stock';
        const disabledAttr = isDisabled ? ' disabled' : '';

        // Blocage chaleur de CETTE variante. On ne l'affiche que sur une
        // carte partiellement bloquée : quand tout l'est, le bandeau rouge
        // de la carte le dit déjà, et répéter le badge sur chaque ligne
        // alourdirait pour rien.
        const isVariantBlocked = blockedIds.has(v.id);
        const blockedBadge = isVariantBlocked && !isBlocked
          ? `<span class="pd-variant-row__state pd-variant-row__state--blocked">${escapeHtml(this._t('card.blocked', 'Indisponible'))}</span>`
          : '';
        const blockedClass = isVariantBlocked && !isBlocked ? ' pd-variant-row--blocked' : '';

        // Photo de la variante : posée en data-* pour que le clic sur la
        // ligne bascule le visuel de la carte (cf. _previewVariantImage).
        // Absente → la ligne fait revenir la photo principale du produit.
        const variantImg = isSafeHttpUrl(v.image) ? v.image : '';
        const variantImgAttr = variantImg ? ` data-variant-image="${escapeHtml(variantImg)}"` : '';

        return `
          <div class="pd-variant-row${stateClass}${blockedClass}" data-variant-id="${v.id}" data-kind="${kind}" data-stock-state="${stockState}"${variantImgAttr}>
            <div class="pd-variant-row__title">${escapeHtml(this._variantName(v))}${minBadge}${stateBadge}${blockedBadge}</div>
            <div class="pd-variant-row__price">${(() => {
              // Flow distributeur : on montre le prix pro barré et le prix
              // réellement payé. Sur les flows pro, pct vaut 0 et on retombe
              // sur l'affichage historique, à l'identique.
              //
              // UNIQUEMENT à l'étape achat. La PLV et les cadeaux sont
              // offerts : ils ne se payent pas, ils CONSOMMENT un budget —
              // et ce budget est compté au prix pro, côté serveur comme
              // côté front (`applyDistributeurBusinessRules` n'applique la
              // remise ligne à ligne que sur `purchase`). Afficher un prix
              // remisé sur ces deux étapes annonçait donc une valeur que
              // rien ne consomme : la cliente lisait 25,80 € et son budget
              // s'amputait de 43,00 €.
              const pct = kind === 'purchase' ? this._distributorPct(v.id) : 0;
              if (pct <= 0) return `${formatEuros(v.priceCents)} ${this.taxLabel}`;
              const net = v.priceCents - Math.round(v.priceCents * pct / 100);
              return `<span class="pd-variant-row__price-pro">${formatEuros(v.priceCents)}</span> ${formatEuros(net)} ${this.taxLabel}`;
            })()}</div>
            <div class="pd-qty" data-variant-id="${v.id}" data-kind="${kind}" data-price-cents="${v.priceCents}" data-min-qty="${minQty}">
              <button class="pd-qty__btn" data-action="qty-dec" type="button" aria-label="${escapeHtml(this._t('card.qty_dec', 'Retirer une unité'))}"${disabledAttr}>−</button>
              <input class="pd-qty__input" type="number" inputmode="numeric" min="0" max="999" value="${qty}" aria-label="${escapeHtml(this._t('card.qty', 'Quantité {product} {variant}', { product: productName, variant: this._variantName(v) }))}"${disabledAttr}>
              <button class="pd-qty__btn" data-action="qty-inc" type="button" aria-label="${escapeHtml(this._t('card.qty_inc', 'Ajouter une unité'))}"${disabledAttr}>+</button>
            </div>
          </div>
        `;
      }).join('');

      const safeImg = isSafeHttpUrl(product.image) ? product.image : '';
      // `data-default-src` mémorise la photo principale : cliquer une
      // variante sans visuel dédié y revient (cf. _previewVariantImage).
      const imgHtml = safeImg
        ? `<img class="pd-product-card__img" src="${escapeHtml(safeImg)}" data-default-src="${escapeHtml(safeImg)}" alt="${escapeHtml(productName)}" data-default-alt="${escapeHtml(productName)}" loading="lazy" decoding="async" referrerpolicy="no-referrer">`
        : `<div class="pd-product-card__img pd-product-card__img--placeholder" aria-hidden="true"></div>`;

      return `
        <article class="pd-product-card${isBlocked ? ' pd-product-card--blocked' : ''}" data-product-id="${product.id}"${isBlocked ? ' data-blocked="1"' : ''}>
          <div class="pd-product-card__media">${imgHtml}</div>
          <div class="pd-product-card__body">
            <h4 class="pd-product-card__title">${escapeHtml(productName)}</h4>
            ${isBlocked ? `<p class="pd-product-card__unavailable">${escapeHtml(this._t('product.unavailable', 'Produit temporairement indisponible à la vente'))}</p>` : ''}
            <div class="pd-product-card__variants">${variantRows}</div>
          </div>
        </article>
      `;
    }

    /**
     * true si la variante appartient à un produit actuellement bloqué à
     * l'achat (option chaleur "block"). Utilisé pour intercepter les
     * tentatives d'ajout (bouton + / saisie input) et ouvrir le popup
     * d'indisponibilité au lieu d'ajouter.
     */
    _isVariantBlocked(variantId) {
      if (!this.heatBlockRegex) return false;
      const v = this.variantsById.get(Number(variantId));
      if (!v) return false;
      return isHeatBlockedVariant(v.productTitle, v.title, {
        blockRegex: this.heatBlockRegex,
        allowRegex: this.heatAllowRegex,
        allowPairs: this.heatAllowPairs,
        exceptions: this.heatExceptions,
      });
    }

    /* ── Étape 4 : récap panier ─────────────────────────────────────── */

    /**
     * Rendu du récapitulatif panier sur l'étape 4.
     * 3 sections (Achat / PLV offerte / Cadeaux d'ouverture), chacune avec
     * ses lignes (produit + variante + qté + prix), un sous-total, puis un
     * total HT global qui ne compte QUE l'achat (PLV et cadeaux étant offerts).
     */
    _renderReview() {
      const container = document.getElementById(`pd-review-${this.sid}`);
      if (!container) return;

      const purchaseLines = selectionToLines(this.purchaseSelection);
      const plvLines      = selectionToLines(this.plvSelection);
      const giftsLines    = selectionToLines(this.giftsSelection);

      const purchaseHt = computeTotalCents(purchaseLines, this.variantsById);
      const plvHt      = computeTotalCents(plvLines, this.variantsById);
      const giftsHt    = computeTotalCents(giftsLines, this.variantsById);

      const isEmpty = !purchaseLines.length && !plvLines.length && !giftsLines.length;
      if (isEmpty) {
        container.dataset.empty = 'true';
        container.innerHTML = `<div class="pd-review__empty">${escapeHtml(this._t('review.empty', 'Votre panier est vide. Revenez aux étapes précédentes pour ajouter des produits.'))}</div>`;
        return;
      }
      container.dataset.empty = 'false';

      const sections = [
        { title: this.reviewTitles.purchase, lines: purchaseLines, subtotal: purchaseHt, kind: 'purchase' },
        { title: this.reviewTitles.plv, lines: plvLines, subtotal: plvHt, kind: 'plv', offered: true },
        { title: this.reviewTitles.gifts, lines: giftsLines, subtotal: giftsHt, kind: 'gifts', offered: true },
      ];

      // Cadeaux automatiques : bloc informatif rendu à part (ces lignes ne
      // font pas partie des sélections — c'est le serveur qui les ajoute au
      // devis). On affiche l'état "acquis" ou le montant restant à atteindre.
      const autoGiftLines = this._autoGiftLines();
      let autoGiftHtml = '';
      if (autoGiftLines.length && this.autoGiftMinCents > 0) {
        const discountedHt = this._discountedHtCents(purchaseHt);
        const reached = discountedHt >= this.autoGiftMinCents;
        const missing = Math.max(0, this.autoGiftMinCents - discountedHt);
        const itemsHtml = autoGiftLines.map((g) => `
          <li class="pd-review-line">
            <div class="pd-review-line__title">
              <span class="pd-review-line__product">${escapeHtml(g.productTitle)}</span>
              <span class="pd-review-line__variant">${escapeHtml(g.variantTitle)}</span>
            </div>
            <div class="pd-review-line__qty">×1</div>
            <div class="pd-review-line__price pd-review-line__price--offered">${formatEuros(g.priceCents)}</div>
          </li>
        `).join('');
        autoGiftHtml = `
          <section class="pd-review-section pd-review-section--offered pd-review-section--auto-gift" data-reached="${reached}">
            <h4 class="pd-review-section__title">${escapeHtml(this.autoGiftLabel)}</h4>
            <p class="pd-review-section__note">${escapeHtml(this._autoGiftNote(reached, missing, formatEuros))}</p>
            <ul class="pd-review-lines">${itemsHtml}</ul>
          </section>
        `;
      }

      const sectionsHtml = sections.map((section) => {
        if (!section.lines.length) return '';
        const linesHtml = section.lines.map((line) => {
          const v = this.variantsById.get(Number(line.variantId));
          if (!v) return '';
          const qty = Number(line.quantity) || 0;
          const lineTotal = (Number(v.priceCents) || 0) * qty;
          const names = this._lineNames(v);
          return `
            <li class="pd-review-line">
              <div class="pd-review-line__title">
                <span class="pd-review-line__product">${escapeHtml(names.product)}</span>
                <span class="pd-review-line__variant">${escapeHtml(names.variant)}</span>
              </div>
              <div class="pd-review-line__qty">×${qty}</div>
              <div class="pd-review-line__price${section.offered ? ' pd-review-line__price--offered' : ''}">${formatEuros(lineTotal)}</div>
            </li>
          `;
        }).filter(Boolean).join('');
        const subtotalLabel = section.offered
          ? this._t('review.offered_value', 'Valeur offerte')
          : this._t('review.subtotal', 'Sous-total {tax}', { tax: this.taxLabel });
        return `
          <section class="pd-review-section${section.offered ? ' pd-review-section--offered' : ''}">
            <h4 class="pd-review-section__title">${escapeHtml(section.title)}</h4>
            <ul class="pd-review-lines">${linesHtml}</ul>
            <div class="pd-review-section__subtotal">
              <span>${escapeHtml(subtotalLabel)}</span>
              <strong>${formatEuros(section.subtotal)}</strong>
            </div>
          </section>
        `;
      }).filter(Boolean).join('');

      // Remises auto cliente (réassort) : si au moins une remise, on insère
      // un sous-total HT avant remise + une ligne par remise applicable.
      // Le total final reflète purchaseHt - somme(remises) — math en cents
      // pour aligner avec ce que Shopify pose sur le draft.
      const discounts = this.applicableDiscounts || [];
      // Remise par paliers : null tant qu'aucune grille n'est configurée,
      // donc la condition ci-dessous vaut exactement ce qu'elle valait sur
      // les quatre flows pro.
      const tierRow = this._tierDiscountRow(purchaseHt);
      let discountHtml = '';
      let finalHt = purchaseHt;
      if ((discounts.length || tierRow) && purchaseHt > 0) {
        let totalDiscountCents = 0;
        const lines = discounts.map((d) => {
          const cents = Math.round(purchaseHt * d.percentage / 100);
          totalDiscountCents += cents;
          const pct = d.percentage.toFixed(2).replace('.', ',');
          return `
            <div class="pd-review-total__row pd-review-total__row--discount">
              <span>${escapeHtml(d.title)} (-${pct} %)</span>
              <strong>-${formatEuros(cents)}</strong>
            </div>
          `;
        }).join('') + (tierRow ? `
            <div class="pd-review-total__row pd-review-total__row--discount">
              <span>${escapeHtml(tierRow.label)}</span>
              <strong>-${formatEuros(tierRow.cents)}</strong>
            </div>
          ` : '');
        if (tierRow) totalDiscountCents += tierRow.cents;
        finalHt = Math.max(0, purchaseHt - totalDiscountCents);
        discountHtml = `
          <div class="pd-review-total__row">
            <span>${escapeHtml(this._t('review.subtotal', 'Sous-total {tax}', { tax: this.taxLabel }))}</span>
            <strong>${formatEuros(purchaseHt)}</strong>
          </div>
          ${lines}
        `;
      }

      // Remise distributeur (flow distributeur uniquement — 0 ailleurs).
      const distributorCents = this._distributorDiscountCents();
      let distributorHtml = '';
      if (distributorCents > 0) {
        finalHt = Math.max(0, finalHt - distributorCents);
        distributorHtml = `
          <div class="pd-review-total__row pd-review-total__row--discount">
            <span>${escapeHtml(this._t('discount.distributor', 'Remise distributeur'))}</span>
            <strong>-${formatEuros(distributorCents)}</strong>
          </div>
        `;
      }

      container.innerHTML = `
        ${sectionsHtml}
        ${autoGiftHtml}
        <div class="pd-review-total">
          ${discountHtml}
          ${distributorHtml}
          <div class="pd-review-total__row pd-review-total__row--final">
            <span>${escapeHtml(this._t('review.total', 'Total {tax}', { tax: this.taxLabel }))}</span>
            <strong>${formatEuros(finalHt)}</strong>
          </div>
          <div class="pd-review-total__note">${escapeHtml(this.taxNote)}</div>
        </div>
      `;
    }

    /* ── Events ─────────────────────────────────────────────────────── */

    _bindEvents() {
      const handleClick = (e) => {
        // Photo de variante : AVANT le early-return sur [data-action], car
        // un clic sur le titre ou le prix d'une ligne n'a pas d'action.
        // Couvre aussi les boutons +/− (ils sont dans la ligne) : ajouter
        // une quantité montre donc la photo correspondante.
        const variantRow = e.target.closest('.pd-variant-row');
        if (variantRow) this._previewVariantImage(variantRow);

        // Clic sur la photo de la carte → lightbox. La photo est dans
        // .pd-product-card__media, hors des lignes variantes : aucun
        // conflit avec la bascule ci-dessus.
        const cardImg = e.target.closest('img.pd-product-card__img');
        if (cardImg) {
          this._openLightbox(cardImg);
          return;
        }

        const action = e.target.closest('[data-action]')?.dataset.action;
        if (!action) return;

        if (action === 'goto-step') {
          const target = Number(e.target.closest('[data-target]').dataset.target);
          if (target <= this.maxStepReached) this._goToStep(target);
          return;
        }
        if (action === 'next-step') return this._handleNext();
        if (action === 'prev-step') return this._goToStep(Math.max(1, this.step - 1));
        if (action === 'qty-inc') return this._handleQtyButton(e.target.closest('.pd-qty'), +1);
        if (action === 'qty-dec') return this._handleQtyButton(e.target.closest('.pd-qty'), -1);
        if (action === 'close-toast') return this._hideToast();
        if (action === 'heat-warning-accept') return this._acceptHeatWarning();
        if (action === 'heat-warning-cancel') return this._cancelHeatWarning();
        if (action === 'heat-block-close') return this._hideHeatBlock();
        if (action === 'music-toggle') return this._musicToggle();
        if (action === 'music-next') return this._musicNext();
        if (action === 'reset-cart') return this._resetCart();
      };

      // this.root contient les steps + le stepper
      this.root.addEventListener('click', handleClick);
      this.root.addEventListener('input', (e) => {
        const input = e.target.closest('.pd-qty__input');
        if (input) {
          const wrap = input.closest('.pd-qty');
          const variantId = Number(wrap.dataset.variantId);
          const qty = Math.max(0, Math.min(999, parseInt(input.value, 10) || 0));
          // Produit bloqué (chaleur) : on refuse toute quantité > 0, on
          // remet l'input à 0 et on ouvre le popup d'indisponibilité.
          if (qty > 0 && this._isVariantBlocked(variantId)) {
            input.value = 0;
            this._showHeatBlock();
            return;
          }
          this._setQty(wrap.dataset.kind, variantId, qty, { fromInput: input });
          return;
        }
        // Formulaire client : déclenche la persistance localStorage debouncée
        // pour qu'un rechargement de page retrouve email/nom/société/etc.
        const formField = e.target.closest(`#pd-customer-form-${this.sid} input`);
        if (formField) this._scheduleStateSave();
      });

      // Case "Je certifie..." — toggle l'état du bouton "Valider mon devis".
      this.root.addEventListener('change', (e) => {
        if (e.target?.dataset?.role === 'confirm-checkbox') {
          this._updateCta();
        }
      });

      // summary-bar, toast, music-bar et heat-warning ont été portalés dans
      // <body> — on bind en plus dessus pour capter les clics (Annuler /
      // Continuer / fermer toast / etc).
      const summaryBar = document.getElementById(`pd-summary-${this.sid}`);
      const toast = document.getElementById(`pd-toast-${this.sid}`);
      const music = document.getElementById(`pd-music-${this.sid}`);
      const heatWarning = document.getElementById(`pd-heat-warning-${this.sid}`);
      const heatBlock = document.getElementById(`pd-heat-block-${this.sid}`);
      summaryBar?.addEventListener('click', handleClick);
      toast?.addEventListener('click', handleClick);
      music?.addEventListener('click', handleClick);
      heatWarning?.addEventListener('click', handleClick);
      heatBlock?.addEventListener('click', handleClick);
    }

    /**
     * Affiche la photo de la variante cliquée sur la carte produit.
     *
     * Vaut pour les 3 étapes (achat, PLV, cadeaux) et les 2 flows : le
     * rendu des cartes est commun, donc le comportement l'est aussi.
     *
     * Une variante sans visuel dédié (~1/3 du catalogue) fait revenir la
     * photo principale du produit plutôt que de laisser celle d'une autre
     * variante, qui serait trompeuse. La ligne cliquée est marquée
     * `--previewed` — un simple repère de "photo affichée", volontairement
     * distinct du surlignage de sélection (piloté, lui, par la quantité).
     */
    _previewVariantImage(row) {
      const card = row.closest('.pd-product-card');
      const img = card?.querySelector('img.pd-product-card__img');
      // Produit sans photo : la carte n'a qu'un placeholder <div>, rien à
      // basculer (une variante ne peut pas avoir de visuel dans ce cas).
      if (!img) return;

      const variantSrc = row.getAttribute('data-variant-image') || '';
      const nextSrc = variantSrc || img.getAttribute('data-default-src') || '';
      if (!nextSrc) return;

      if (img.getAttribute('src') !== nextSrc) {
        img.setAttribute('src', nextSrc);
        const baseAlt = img.getAttribute('data-default-alt') || '';
        const variantTitle = row.querySelector('.pd-variant-row__title')?.textContent.trim() || '';
        img.setAttribute('alt', variantSrc && variantTitle ? `${baseAlt} — ${variantTitle}` : baseAlt);
      }

      for (const r of card.querySelectorAll('.pd-variant-row')) {
        r.classList.toggle('pd-variant-row--previewed', r === row);
      }
    }

    _handleQtyButton(wrap, delta) {
      if (!wrap) return;
      const variantId = Number(wrap.dataset.variantId);
      // Produit bloqué (chaleur) : un clic "+" n'ajoute rien et ouvre le
      // popup d'indisponibilité. Le "-" reste inoffensif (qté déjà 0).
      if (delta > 0 && this._isVariantBlocked(variantId)) {
        this._showHeatBlock();
        return;
      }
      const kind = wrap.dataset.kind;
      const minQty = Math.max(1, Number(wrap.dataset.minQty) || 1);
      const sel = this._selectionFor(kind);
      const current = sel.get(variantId) || 0;

      let next;
      if (delta > 0) {
        // Premier ajout depuis 0 → saute directement au minimum (typiquement 3
        // pour les formats voyage), puis +1 par clic. Évite à la cliente de
        // cliquer 3 fois pour atteindre le seuil.
        next = current === 0 ? minQty : current + delta;
      } else {
        // Retrait sous le minimum → on déselectionne complètement (qty=0).
        next = current <= minQty ? 0 : current + delta;
      }
      this._setQty(kind, variantId, next);
    }

    _setQty(kind, variantId, qty, { fromInput } = {}) {
      const sel = this._selectionFor(kind);
      const previous = sel.get(variantId) || 0;
      qty = Math.max(0, Math.min(999, Number(qty) || 0));

      // Enforce per-variant minimum (formats voyage en étape 1). Si la cliente
      // saisit manuellement une valeur entre 1 et minQty-1, on remonte au
      // minimum et on l'informe via un toast.
      const qtyEl = this.root.querySelector(`.pd-qty[data-variant-id="${variantId}"][data-kind="${kind}"]`);
      const minQty = Math.max(1, Number(qtyEl?.dataset.minQty) || 1);
      if (qty > 0 && qty < minQty) {
        if (fromInput) {
          this._showToast(this._t('toast.travel_min', 'Minimum {count} unités pour ce format voyage.', { count: minQty }));
        }
        qty = minQty;
      }

      if (kind === 'plv' || kind === 'gifts') {
        const variant = this.variantsById.get(variantId);
        const budgetCents = this._budgetCents(kind);
        const otherSelected = computeTotalCents(
          selectionToLines(sel).filter((l) => l.variantId !== variantId),
          this.variantsById
        );
        const addCents = (variant?.priceCents || 0) * qty;
        if (exceedsBudget(otherSelected, addCents, budgetCents)) {
          const remaining = Math.max(0, budgetCents - otherSelected);
          const maxQty = variant?.priceCents ? Math.floor(remaining / variant.priceCents) : 0;
          qty = Math.max(0, Math.min(qty, maxQty));
          this._showToast(
            kind === 'plv'
              ? this._t('toast.plv_budget_reached', 'Budget PLV atteint (max {max} pour cet article).', { max: maxQty })
              : this._t('toast.gifts_budget_reached', 'Budget cadeaux atteint (max {max} pour cet article).', { max: maxQty })
          );
        }
      }

      if (qty > 0) sel.set(variantId, qty);
      else sel.delete(variantId);

      const inputs = this.root.querySelectorAll(`.pd-qty[data-variant-id="${variantId}"][data-kind="${kind}"] .pd-qty__input`);
      inputs.forEach((inp) => { if (inp !== fromInput) inp.value = qty; });

      this._updateSummary();
      this._scheduleStateSave();

      // Avertissement chaleur : déclencher le popup à la transition 0→positif
      // sur une variante d'un produit chaleur-sensible, sauf si la cliente
      // a déjà confirmé pour ce produit dans la session. On ne re-déclenche
      // pas sur les incréments 1→2→3 (utile pour ne pas spammer).
      if (previous === 0 && qty > 0 && this.heatWarningRegex) {
        const variant = this.variantsById.get(variantId);
        if (
          variant &&
          this.heatWarningRegex.test(variant.productTitle || '') &&
          !this.heatWarningShownProducts.has(variant.productId)
        ) {
          this._showHeatWarning(kind, variantId);
        }
      }
    }

    _selectionFor(kind) {
      if (kind === 'plv') return this.plvSelection;
      if (kind === 'gifts') return this.giftsSelection;
      return this.purchaseSelection;
    }

    /**
     * Taux de remise distributeur d'une variante, via les tags/titre de son
     * produit. 0 sur les flows pro (grille vide).
     */
    _distributorPct(variantId) {
      if (!this.distributorRules.length) return 0;
      const v = this.variantsById.get(Number(variantId));
      if (!v) return 0;
      return discountPctForProduct({ title: v.productTitle, tags: v.productTags }, this.distributorRules);
    }

    /**
     * Remise distributeur totale sur le panier d'achat, en cents.
     *
     * Arrondi PAR LIGNE, comme le serveur (`applyDistributeurBusinessRules`)
     * et comme Shopify sur le draft order : arrondir sur le total donnerait
     * un écart de quelques centimes entre l'écran et la facture.
     */
    _distributorDiscountCents() {
      if (!this.distributorRules.length) return 0;
      let total = 0;
      for (const line of selectionToLines(this.purchaseSelection)) {
        const v = this.variantsById.get(Number(line.variantId));
        if (!v) continue;
        const pct = this._distributorPct(line.variantId);
        if (pct > 0) total += Math.round(v.priceCents * line.quantity * pct / 100);
      }
      return total;
    }

    /**
     * Ligne « remise entreprise » à afficher, ou null.
     *
     * UNE seule méthode calcule ce montant, et les trois surfaces qui
     * montrent de l'argent — barre du bas, récap de l'étape 4, tiroir —
     * l'appellent. Elles assemblent encore leurs lignes chacune de leur
     * côté (dette antérieure, laissée en l'état pour ne pas toucher au
     * rendu des flows pro), mais le montant des paliers, lui, n'existe
     * qu'ici : il ne peut pas diverger d'un écran à l'autre.
     *
     * Grille vide — c'est le cas des quatre flows pro — : renvoie null, et
     * les trois appelants retombent exactement sur leur comportement
     * d'avant.
     */
    _tierDiscountRow(purchaseHt) {
      if (!this.tierDiscounts.length) return null;
      const pct = tierDiscountPct(purchaseHt, this.tierDiscounts);
      if (!pct) return null;
      const cents = Math.round(purchaseHt * pct / 100);
      if (!cents) return null;
      const pctStr = pct.toFixed(2).replace('.', ',');
      return { pct, cents, label: `${this.tierLabel} (-${pctStr} %)` };
    }

    _budgetCents(kind) {
      const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
      // Base des budgets PLV/cadeaux : le HT NET, après TOUTES les remises
      // qui portent sur l'achat — remises clientes, remise distributeur par
      // ligne, remise entreprise par paliers. C'est ce que la cliente paye
      // réellement, et c'est la règle unique depuis l'arbitrage du
      // 05/10/2026 : le distributeur assoyait ses budgets sur le prix pro,
      // il était le seul à s'en écarter.
      //
      // ⚠️ `applyDistributeurBusinessRules` et `applyReassortBusinessRules`
      // appliquent la même base côté serveur. Les deux doivent bouger
      // ensemble : la page plus permissive que le serveur, c'est une toast
      // d'erreur à la soumission ; l'inverse, du budget que personne ne
      // peut dépenser.
      //
      // Sans aucune remise (flow ouverture), la base vaut purchaseHt et
      // rien ne change.
      const baseHt = Math.max(0, computeDiscountedHt(purchaseHt, this.applicableDiscounts)
        - this._distributorDiscountCents()
        - (this._tierDiscountRow(purchaseHt)?.cents || 0));
      if (kind === 'plv') return computeBudgetCents(baseHt, this.cfg.plvBudgetPct);
      if (kind === 'gifts') return computeBudgetCents(baseHt, this.cfg.giftBudgetPct);
      return 0;
    }

    /* ── Summary / CTA ──────────────────────────────────────────────── */

    _updateSummary() {
      const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
      const plvHt = computeTotalCents(selectionToLines(this.plvSelection), this.variantsById);
      const giftsHt = computeTotalCents(selectionToLines(this.giftsSelection), this.variantsById);

      const purchaseEl = this._q('[data-role="purchase-total"]');
      if (purchaseEl) purchaseEl.textContent = formatEuros(purchaseHt);

      this._updateDiscountRow(purchaseHt);

      // Bouton Réinitialiser : visible seulement quand au moins un item dans
      // un des 3 paniers — inutile sinon.
      const resetBtn = this._q('[data-action="reset-cart"]');
      if (resetBtn) resetBtn.hidden = (purchaseHt + plvHt + giftsHt) === 0;

      this._updateBudgetBanner('plv', plvHt, this._budgetCents('plv'));
      this._updateBudgetBanner('gifts', giftsHt, this._budgetCents('gifts'));

      // Pastille du bouton flottant + tiroir s'il est ouvert. Ici plutôt
      // qu'aux ~15 endroits qui touchent une sélection : _updateSummary est
      // déjà le passage obligé de tout changement de panier.
      this._updateCartUi();

      this._updateCta();
    }

    /**
     * Met à jour les lignes "remise client" et "total HT après remise" dans
     * la summary bar. Visible uniquement si :
     *  • Au moins une remise auto est applicable (this.applicableDiscounts non vide)
     *  • ET le panier d'achat n'est pas vide (rien à remiser sinon)
     *
     * Toutes les remises se cumulent (somme des %) — c'est ce que Shopify
     * fait nativement au checkout via les Regios Functions quand
     * `combinesWith.orderDiscounts: true`. La remise s'applique sur
     * purchaseHt seulement — pas sur les PLV ou cadeaux qui sont déjà
     * 100 % offerts au niveau ligne.
     */
    _updateDiscountRow(purchaseHt) {
      const container = this._q('[data-role="discount-rows"]');
      const finalRow = this._q('[data-role="final-row"]');
      if (!container || !finalRow) return; // markup absent → flow ouverture, skip

      const discounts = this.applicableDiscounts || [];
      const distributorCents = this._distributorDiscountCents();
      const tierRow = this._tierDiscountRow(purchaseHt);
      if ((!discounts.length && !distributorCents && !tierRow) || purchaseHt === 0) {
        container.innerHTML = '';
        finalRow.hidden = true;
        return;
      }

      // Math : on calcule chaque montant en cents indépendamment (sur la
      // base purchaseHt, pas en cascade) pour aligner l'affichage sur ce
      // que Shopify affiche au checkout. Total = somme des arrondis.
      let totalDiscountCents = 0;
      const rowsHtml = discounts.map((d) => {
        const cents = Math.round(purchaseHt * d.percentage / 100);
        totalDiscountCents += cents;
        const pctStr = d.percentage.toFixed(2).replace('.', ',');
        return `
          <div class="pd-summary-bar__row pd-summary-bar__row--discount">
            <span>${escapeHtml(d.title)} (-${pctStr} %)</span>
            <strong>-${formatEuros(cents)}</strong>
          </div>
        `;
      }).join('');
      // Remise distributeur : une seule ligne agrégée. Détailler par taux
      // (45 %, 50 %, 40 %) alourdirait la barre sans aider — le détail par
      // ligne figure sur le devis Shopify.
      const distributorRow = distributorCents ? `
          <div class="pd-summary-bar__row pd-summary-bar__row--discount">
            <span>${escapeHtml(this._t('discount.distributor', 'Remise distributeur'))}</span>
            <strong>-${formatEuros(distributorCents)}</strong>
          </div>
        ` : '';
      // Remise par paliers : null partout sauf là où une grille est
      // configurée, donc les quatre flows pro rendent exactement ce
      // qu'ils rendaient.
      const tierHtml = tierRow ? `
          <div class="pd-summary-bar__row pd-summary-bar__row--discount">
            <span>${escapeHtml(tierRow.label)}</span>
            <strong>-${formatEuros(tierRow.cents)}</strong>
          </div>
        ` : '';
      if (tierRow) totalDiscountCents += tierRow.cents;
      container.innerHTML = rowsHtml + tierHtml + distributorRow;

      const finalCents = Math.max(0, purchaseHt - totalDiscountCents - distributorCents);
      const finalEl = this._q('[data-role="final-total"]');
      if (finalEl) finalEl.textContent = formatEuros(finalCents);
      finalRow.hidden = false;
    }

    /**
     * Va chercher TOUTES les remises auto applicables à la cliente connectée
     * auprès du backend (`GET /reassort/applicable-discount`).
     * Fire-and-forget :
     *  • Pas d'email dans la config → flow ouverture, on skip silencieusement.
     *  • Pas de backendUrl → mode preview Shopify, idem.
     *  • Erreur réseau / 4xx / 5xx → on ne crash pas le rendu, on laisse
     *    la summary bar afficher juste le panier HT comme avant.
     *
     * Le backend renvoie `{ discounts: [{ title, percentage }, ...] }` trié
     * par percentage descendant. Stocké dans this.applicableDiscounts et un
     * _updateSummary est déclenché pour révéler les lignes "remise" et
     * "total HT".
     */
    async _fetchApplicableDiscount() {
      const email = (this.cfg.customerEmail || '').trim();
      const backend = (this.cfg.backendUrl || '').trim();
      if (!email || !backend) return;
      try {
        const url = new URL(backend.replace(/\/$/, '') + '/applicable-discount', location.origin);
        url.searchParams.set('email', email);
        url.searchParams.set('sectionId', String(this.cfg.sectionId || ''));
        url.searchParams.set('signature', String(this.cfg.requestSignature || ''));
        url.searchParams.set('signatureTs', String(this.cfg.requestSignatureTs || ''));
        const res = await fetch(url.toString(), { credentials: 'omit' });
        if (!res.ok) {
          console.warn(`[prodevis] discount endpoint: ${res.status}`);
          return;
        }
        const data = await res.json();
        const list = Array.isArray(data?.discounts) ? data.discounts : [];
        const valid = list.filter((d) => d && typeof d.percentage === 'number' && d.percentage > 0)
          .map((d) => ({ title: String(d.title || ''), percentage: Number(d.percentage) }));
        if (!valid.length) return;
        this.applicableDiscounts = valid;
        this._updateSummary();
        // L'étape 4 est déjà peut-être en cours d'affichage (restauration
        // depuis localStorage) — re-render pour intégrer les remises.
        if (this.step === 4) this._renderReview();
      } catch (e) {
        console.warn('[prodevis] discount fetch a échoué', e);
      }
    }

    /**
     * Réinitialise le devis à zéro : vide les 3 sélections, reset les
     * compteurs de quantité dans le DOM, remet la cliente à l'étape 1,
     * vide le formulaire client et la confirmation, et purge le state
     * sauvegardé en localStorage. Demande confirmation pour éviter les
     * clics accidentels — l'action n'est pas réversible.
     */
    _resetCart() {
      if (!window.confirm(this.texts.resetConfirm)) return;

      this.purchaseSelection.clear();
      this.plvSelection.clear();
      this.giftsSelection.clear();

      // Reset visuel : tous les inputs qty repassent à 0.
      this.root.querySelectorAll('.pd-qty__input').forEach((inp) => { inp.value = 0; });

      // Vide aussi le formulaire client + la confirmation, pour un vrai
      // "fresh start". Si la cliente revient et veut juste retoucher le
      // panier, elle a déjà le bouton Retour des étapes.
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      if (form) {
        // On NE vide PAS les champs cachés. Côté réassort, l'email est un
        // input `type="hidden"` pré-rempli par Liquid ({{ customer.email }})
        // et jamais saisi par la cliente : le vider le rendait
        // IRRÉCUPÉRABLE (champ invisible, aucun moyen de le re-remplir sans
        // recharger la page), et toute soumission suivante échouait sur
        // « Email professionnel requis pour valider le devis. ».
        // Les champs visibles (ouverture : email, nom, société… ; réassort :
        // PO number) sont bien remis à zéro pour un vrai "fresh start".
        form.querySelectorAll('input:not([type="hidden"])').forEach((inp) => { inp.value = ''; });
      }
      const cb = document.getElementById(`pd-confirm-${this.sid}`);
      if (cb) cb.checked = false;

      // Retour à l'étape 1, max reset aussi pour bloquer les sauts de stepper.
      this.step = 1;
      this.maxStepReached = 1;
      this._renderStep();
      this._updateSummary();

      // Purge le state sauvegardé sinon le prochain reload restaurerait
      // un panier déjà vidé (no-op mais bruit inutile en console).
      this._clearState();

      this._showToast(this.texts.resetDone);
    }

    _updateBudgetBanner(kind, usedCents, budgetCents) {
      const banner = this.root.querySelector(`.pd-budget-banner[data-context="${kind}"]`);
      if (!banner) return;
      const pct = budgetCents > 0 ? Math.min(100, Math.round((usedCents / budgetCents) * 100)) : 0;
      banner.querySelector('[data-role="used"]').textContent = formatEuros(usedCents);
      banner.querySelector('[data-role="total"]').textContent = formatEuros(budgetCents);
      const fill = banner.querySelector('.pd-budget-banner__fill');
      if (fill) fill.style.width = pct + '%';
      banner.classList.toggle('pd-budget-banner--full', pct >= 100);
      banner.classList.toggle('pd-budget-banner--locked', budgetCents === 0);
    }

    _updateCta() {
      const nextBtn = this._q('[data-action="next-step"]');
      const prevBtn = this._q('[data-action="prev-step"]');
      const ctaLabel = this._q('[data-role="cta-label"]');
      if (!nextBtn) return;

      prevBtn.hidden = this.step === 1;

      // Reset des états "en attente de confirmation" (étape 4) — ils sont
      // ré-appliqués plus bas si on est encore à l'étape 4 sans case cochée.
      nextBtn.classList.remove('pd-btn--awaiting');
      nextBtn.removeAttribute('aria-disabled');
      const confirmBox = this.root.querySelector('.pd-confirm');
      if (confirmBox) confirmBox.classList.remove('pd-confirm--pending');

      const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
      const purchaseOK = meetsMinimum(purchaseHt, this.cfg.minOrderHt);

      if (this.step === 1) {
        nextBtn.disabled = !purchaseOK;
        const remaining = Math.max(0, Math.round((this.cfg.minOrderHt) * 100) - purchaseHt);
        if (!purchaseOK) {
          // Libellé de la section (`remaining_label`), donc traduisible
          // comme `next` et `validate`. Absent, vide ou traduit sans son
          // {amount} → libellé historique (dictionnaire hors français).
          const amount = formatEuros(remaining);
          const vars = { amount, tax: this.taxLabel };
          const label = this._setting(this.cfg.labels?.remaining, 'cta.remaining', 'Encore {amount} {tax} pour valider');
          ctaLabel.textContent = fillPlaceholders(label, vars, ['amount'])
            || this._t('cta.remaining', 'Encore {amount} {tax} pour valider', vars);
        } else {
          ctaLabel.textContent = this._setting(this.cfg.labels.next || 'Continuer', 'cta.next', 'Continuer');
        }
      } else if (this.step === 2 || this.step === 3) {
        nextBtn.disabled = false;
        ctaLabel.textContent = this._setting(this.cfg.labels.next || 'Continuer', 'cta.next', 'Continuer');
      } else {
        // Étape 4 : validation finale. On NE désactive PAS le bouton tant que
        // la case n'est pas cochée — sinon il reste grisé et inerte, et les
        // clientes ne comprennent pas ce qui bloque. On le laisse cliquable
        // mais visuellement "en attente" (pd-btn--awaiting) ; le clic
        // déclenche un scroll + surbrillance vers la case (cf. _handleNext →
        // _highlightConfirm). La case elle-même est mise en avant en continu
        // tant qu'elle n'est pas cochée (pd-confirm--pending).
        const confirmed = this._isConfirmed();
        nextBtn.disabled = false;
        nextBtn.classList.toggle('pd-btn--awaiting', !confirmed);
        nextBtn.setAttribute('aria-disabled', String(!confirmed));
        if (confirmBox) confirmBox.classList.toggle('pd-confirm--pending', !confirmed);
        ctaLabel.textContent = this._setting(this.cfg.labels.validate || 'Valider mon devis', 'cta.validate', 'Valider mon devis');
      }
    }

    _isConfirmed() {
      const cb = document.getElementById(`pd-confirm-${this.sid}`);
      return Boolean(cb && cb.checked);
    }

    /* ── Navigation ─────────────────────────────────────────────────── */

    _handleNext() {
      if (this.step === 4) {
        // Le formulaire client (email pro requis) vit dans l'étape 4 :
        // la cliente le voit en ce moment, donc on valide au clic final.
        // Sur le réassort l'email est en hidden pré-rempli → check passe
        // toujours, comportement inchangé.
        if (!this._validateCustomerForm()) return;
        if (!this._isConfirmed()) {
          this._highlightConfirm();
          return;
        }
        return this._submitDevis();
      }
      this._goToStep(this.step + 1);
    }

    /**
     * Attire l'attention sur la case "Je certifie…" quand la cliente clique
     * sur "Valider" sans l'avoir cochée. Le bouton paraît grisé (donc on
     * comprend qu'il manque quelque chose) mais reste cliquable pour pouvoir
     * expliquer : scroll doux vers la case (centrée pour ne pas passer sous
     * la barre récap fixe), pulse visuel, et toast explicite.
     */
    _highlightConfirm() {
      const box = this.root.querySelector('.pd-confirm');
      if (box) {
        try {
          box.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } catch {
          box.scrollIntoView();
        }
        // Retire puis ré-ajoute la classe (avec reflow) pour rejouer
        // l'animation même si la cliente re-clique plusieurs fois.
        box.classList.remove('pd-confirm--flash');
        void box.offsetWidth;
        box.classList.add('pd-confirm--flash');
        clearTimeout(this._confirmFlashTimer);
        this._confirmFlashTimer = setTimeout(() => box.classList.remove('pd-confirm--flash'), 1300);
      }
      this._showToast(this._t('toast.confirm_box', 'Cochez la case « Je certifie… » en bas pour valider votre commande.'));
    }

    _goToStep(target) {
      target = Math.max(1, Math.min(4, target));

      if (target > this.step) {
        // Vérifs sur l'état COURANT du panier, indépendamment du `this.step`
        // de départ. Sans ça, un saut via stepper (step 1 → step 4) ou un
        // retour-modif-avance (step 4 → step 1 → réduit le panier → step 4)
        // contourne les gates et le backend rejette à la soumission.
        const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
        // Réassort : budgets PLV/cadeaux calculés sur le HT remisé (et pas
        // sur le brut) — pour matcher la règle métier serveur. Sans remise
        // applicable, `computeDiscountedHt` renvoie purchaseHt → idem.
        const budgetBaseHt = computeDiscountedHt(purchaseHt, this.applicableDiscounts);

        if (target >= 2 && !meetsMinimum(purchaseHt, this.cfg.minOrderHt)) {
          // « HT » écrit en dur, y compris sur la page TTC : texte
          // historique, gardé à l'identique ; ses traductions le suivent.
          // {tax} : « HT » sur les flows pro (texte inchangé), « TTC » sur la
          // page cadeaux d'entreprise, qui écrivait « HT » à tort.
          this._showToast(this._t('toast.min_order', "Minimum {min} € {tax} requis pour passer à l'étape suivante.", { min: this.cfg.minOrderHt, tax: this.taxLabel }));
          return;
        }

        if (target >= 3) {
          const plvHt = computeTotalCents(selectionToLines(this.plvSelection), this.variantsById);
          const plvBudget = computeBudgetCents(budgetBaseHt, this.cfg.plvBudgetPct);
          if (plvHt > plvBudget) {
            this._showToast(this._t('toast.plv_budget_over', 'Budget PLV dépassé ({used} sur {budget}). Ajustez votre sélection PLV avant de continuer.', {
              used: formatEuros(plvHt),
              budget: formatEuros(plvBudget),
            }));
            return;
          }
        }

        if (target >= 4) {
          const giftsHt = computeTotalCents(selectionToLines(this.giftsSelection), this.variantsById);
          const giftsBudget = computeBudgetCents(budgetBaseHt, this.cfg.giftBudgetPct);
          if (giftsHt > giftsBudget) {
            this._showToast(this._t('toast.gifts_budget_over', 'Budget cadeaux dépassé ({used} sur {budget}). Ajustez votre sélection de cadeaux avant de continuer.', {
              used: formatEuros(giftsHt),
              budget: formatEuros(giftsBudget),
            }));
            return;
          }
          // Le formulaire client vit dans l'étape 4 — pas de validation
          // ici, sinon on bloquerait l'accès à l'étape où le form
          // est censé être rempli. La validation finale se fait dans
          // _handleNext + _submitDevis au clic "Valider mon devis".
        }
      }

      this.step = target;
      this.maxStepReached = Math.max(this.maxStepReached, target);
      this._renderStep();
      this._scheduleStateSave();
      window.scrollTo({ top: this.root.offsetTop - 60, behavior: 'smooth' });
    }

    /**
     * Email de la cliente pour la soumission.
     *
     * Flow ouverture : champ visible, saisi par la prospecte → on prend ce
     * qu'elle a tapé, et rien d'autre (pas de valeur devinée).
     *
     * Flow réassort : la page est gatée, l'email n'est JAMAIS saisi — c'est
     * un input `type="hidden"` pré-rempli par Liquid. Si cette valeur venait
     * à disparaître du DOM (reset du devis, extension, state corrompu…), la
     * cliente serait bloquée sur « Email professionnel requis » sans aucun
     * champ à corriger. On retombe donc sur `cfg.customerEmail`, la même
     * source Liquid, et on re-remplit l'input pour que la persistance
     * localStorage reparte sur de bonnes bases.
     */
    _customerEmail() {
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      const input = form?.querySelector('[name="email"]');
      const typed = input?.value.trim() || '';
      if (typed) return typed;
      if (input && input.type === 'hidden') {
        const fallback = String(this.cfg.customerEmail || '').trim();
        if (fallback) {
          input.value = fallback;
          return fallback;
        }
      }
      return '';
    }

    /**
     * Message d'erreur email adapté au flow. Sur le réassort le champ est
     * caché : demander « saisissez votre email » à une cliente qui n'a
     * aucun champ à remplir est une impasse — on l'oriente vers une action
     * qui a une chance de marcher.
     */
    _emailErrorMessage(input) {
      if (input && input.type === 'hidden') {
        return this._t('toast.account_email', "Votre email de compte n'a pas pu être lu. Rechargez la page et revalidez ; si le problème persiste, contactez-nous.");
      }
      return this.texts.emailRequired;
    }

    _validateCustomerForm() {
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      const emailInput = form?.querySelector('[name="email"]');
      const email = this._customerEmail();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this._showToast(this._emailErrorMessage(emailInput));
        if (emailInput && emailInput.type !== 'hidden') emailInput.focus();
        return false;
      }
      return true;
    }

    /* ── Submit devis ───────────────────────────────────────────────── */

    async _submitDevis() {
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      const customer = {
        email: this._customerEmail(),
        firstName: form?.querySelector('[name="firstName"]')?.value.trim() || '',
        lastName: form?.querySelector('[name="lastName"]')?.value.trim() || '',
        siret: form?.querySelector('[name="siret"]')?.value.trim() || '',
        phone: form?.querySelector('[name="phone"]')?.value.trim() || '',
        poNumber: form?.querySelector('[name="poNumber"]')?.value.trim() || '',
        // Honeypot anti-spam : un humain ne le voit pas, un bot le remplit
        // → backend rejette si non-vide. La cliente ne remarque rien.
        website: form?.querySelector('[name="website"]')?.value || '',
      };
      // SIREN : seulement là où la section propose le champ (cadeaux
      // d'entreprise). Ailleurs la clé n'est pas envoyée du tout : le
      // payload des pages pro reste celui d'avant.
      const sirenInput = form?.querySelector('[name="siren"]');
      if (sirenInput) customer.siren = sirenInput.value.trim();
      // TVA intracommunautaire : même principe, seulement là où la section
      // propose le champ (ouverture de compte pro, pour l'étranger).
      const vatInput = form?.querySelector('[name="vatNumber"]');
      if (vatInput) customer.vatNumber = vatInput.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customer.email)) {
        const emailInput = form?.querySelector('[name="email"]');
        this._showToast(this._emailErrorMessage(emailInput));
        if (emailInput && emailInput.type !== 'hidden') emailInput.focus();
        return;
      }
      // Même règle que le serveur (qui refait le contrôle) : 9 chiffres,
      // ou 14 si la cliente saisit son SIRET ; espaces et points tolérés.
      if (customer.siren && !/^\d{9}(\d{5})?$/.test(customer.siren.replace(/[\s.]/g, ''))) {
        this._showToast(this._t('toast.siren', 'Le numéro SIREN doit comporter 9 chiffres (ou 14 pour un SIRET).'));
        sirenInput.focus();
        return;
      }
      // Même règle que le serveur : code pays en 2 lettres, puis 2 à 12
      // caractères ; espaces, points et tirets tolérés. Vide = accepté.
      if (customer.vatNumber && !/^[A-Z]{2}[0-9A-Z+*]{2,12}$/.test(customer.vatNumber.toUpperCase().replace(/[\s.\-]/g, ''))) {
        this._showToast(this._t('toast.vat', "Le numéro de TVA intracommunautaire doit commencer par le code du pays, par exemple ES B12345678. Laissez le champ vide si vous ne l'avez pas."));
        vatInput.focus();
        return;
      }

      if (!this.cfg.backendUrl) {
        this._showToast(this._t('toast.backend_missing', "Configuration manquante : URL backend devis non définie dans l'éditeur de section."));
        return;
      }

      const payload = {
        // Signature de page calculée par Liquid au rendu — prouve au backend
        // que la requête provient d'un vrai rendu de la section. Le secret
        // sous-jacent n'est jamais exposé côté client.
        sectionId: String(this.cfg.sectionId),
        signature: this.cfg.requestSignature,
        signatureTs: this.cfg.requestSignatureTs,
        // Signaux légers anti-bot : webdriver flag (Selenium/Puppeteer
        // default = true), interaction humaine vue (mouse/touch/keyboard/
        // scroll), durée sur la page. Trivialement spoofables mais éliminent
        // les scrapers amateurs et la majorité des bots opportunistes qui
        // submit sans interagir.
        clientSignals: {
          webdriver: !!navigator.webdriver,
          interacted: this.hasHumanInteraction === true,
          timeOnPageMs: Date.now() - this.pageLoadTs,
        },
        customer,
        purchase: selectionToLines(this.purchaseSelection),
        plv: selectionToLines(this.plvSelection),
        gifts: selectionToLines(this.giftsSelection),
      };

      this._showLoading(true);
      try {
        const res = await fetch(this.cfg.backendUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          // `data.error` vient du serveur, en français (hors périmètre du
          // dictionnaire) ; seul le repli local se traduit.
          this._showToast(data?.error || this._t('toast.backend_status', 'Erreur backend ({status}).', { status: res.status }));
          return;
        }
        if (!data?.invoiceUrl || !isSafeHttpUrl(data.invoiceUrl)) {
          this._showToast(this._t('toast.bad_response', 'Réponse inattendue du serveur — URL de paiement invalide.'));
          return;
        }
        // Stocke le résultat pour les actions des 2 boutons (email / checkout).
        // Le mailToken est l'autorisation cryptographique de relancer l'email
        // pour ce draft précis — sans lui, le backend refuse (Fix B).
        this.devisResult = {
          draftOrderId: data.draftOrderId,
          invoiceUrl: data.invoiceUrl,
          mailToken: data.mailToken,
          email: customer.email,
        };
        this._showSuccess();
      } catch (e) {
        console.error('[prodevis] submit error', e);
        this._showToast(this._t('toast.network', 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.'));
      } finally {
        this._showLoading(false);
      }
    }

    /* ── Lecteur musique (nano flottant) ───────────────────────────── */

    /**
     * Initialise le nano lecteur Green Spa Music.
     *
     * Pipeline :
     *  1. Fetch des playlists actives sur PocketBase, tri par sort_order.
     *  2. Sélection de la playlist : override par config OU première active.
     *  3. Fetch des tracks de cette playlist.
     *  4. Création d'un <audio> single-instance (portalé dans le body pour
     *     ne pas être détruit par les changements de step) et bind des
     *     contrôles play/pause/next.
     *  5. Tentative d'autoplay si demandé. Le navigateur peut rejeter
     *     (Promise rejection) — on tombe alors en pause manuelle, ce qui
     *     est la cas attendu sur première visite sans interaction.
     *
     * Échec silencieux à toutes les étapes : si PocketBase est down ou
     * la playlist vide, le lecteur reste caché. Pas de bruit en console
     * en dehors du log warn pour aider le debug en cas de pépin.
     */
    async _initMusic() {
      if (!this.cfg.music?.enabled) return;
      if (!this.musicEl) return;

      // Bail sur mobile : la pill est cachée en CSS et l'autoplay sans
      // contrôle visible serait intrusif. Évite aussi un fetch PocketBase
      // inutile au chargement mobile.
      if (typeof window !== 'undefined' && window.matchMedia
          && window.matchMedia('(max-width: 760px)').matches) {
        return;
      }

      const baseUrl = this._musicBaseUrl();
      if (!baseUrl) return;

      try {
        const playlist = await this._musicPickPlaylist(baseUrl);
        if (!playlist) return;
        const tracks = await this._musicFetchTracks(baseUrl, playlist.id);
        if (!tracks.length) return;

        this.music = {
          playlist,
          tracks,
          index: 0,
          audio: new Audio(),
          isPlaying: false,
        };
        // Préserve l'audio entre re-renders en l'attachant au body (invisible)
        // — sans cela, le DOM du player suffirait à le détruire si on touche
        // au markup, et HTMLAudioElement détaché continue de jouer mais
        // perd ses listeners.
        this.music.audio.preload = 'none';
        this.music.audio.addEventListener('ended', () => this._musicNext());
        this.music.audio.addEventListener('play', () => this._musicRender());
        this.music.audio.addEventListener('pause', () => this._musicRender());

        this._musicLoadTrack(0);
        this.musicEl.hidden = false;
        this.musicEl.dataset.state = 'ready';

        // Opt-in STRICT : on ne démarre la musique que si la section le
        // demande explicitement (réglage "Démarrer la musique
        // automatiquement", décoché par défaut). Toute autre valeur
        // (absente, null, false) laisse le lecteur en pause — la cliente
        // clique sur la pochette pour lancer.
        if (this.cfg.music.autoplay === true) {
          this._musicPlay({ silentFail: true });
        }
      } catch (e) {
        console.warn('[prodevis] music init error', e);
      }
    }

    _musicBaseUrl() {
      const raw = String(this.cfg.music?.pocketbaseUrl || '').trim().replace(/\/+$/, '');
      if (!raw) return '';
      if (!isSafeHttpUrl(raw)) return '';
      return raw;
    }

    async _musicPickPlaylist(baseUrl) {
      // Si une playlist précise est demandée par config, on la cherche
      // par id directement — c'est plus prévisible pour le merchant.
      const wantedId = String(this.cfg.music?.playlistId || '').trim();
      if (wantedId) {
        const url = `${baseUrl}/api/collections/playlists/records/${encodeURIComponent(wantedId)}`;
        const res = await fetch(url, { credentials: 'omit' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.id ? data : null;
      }
      // Sinon, première playlist active triée par sort_order.
      const url = `${baseUrl}/api/collections/playlists/records?filter=(is_active=true)&sort=sort_order&perPage=1`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.items?.[0] || null;
    }

    async _musicFetchTracks(baseUrl, playlistId) {
      // perPage=200 couvre largement les playlists raisonnables ; au-delà on
      // accepte la troncature plutôt que de paginer (c'est une playlist
      // d'ambiance, pas un catalogue produit).
      const filter = encodeURIComponent(`playlist_id="${playlistId}"`);
      const url = `${baseUrl}/api/collections/tracks/records?filter=${filter}&perPage=200`;
      const res = await fetch(url, { credentials: 'omit' });
      if (!res.ok) return [];
      const data = await res.json();
      return (data?.items || []).filter((t) => isSafeHttpUrl(t.audio_url));
    }

    _musicLoadTrack(index) {
      if (!this.music) return;
      const tracks = this.music.tracks;
      if (!tracks.length) return;
      const i = ((index % tracks.length) + tracks.length) % tracks.length;
      this.music.index = i;
      const track = tracks[i];
      this.music.audio.src = track.audio_url;
      this._musicRender();
    }

    _musicPlay({ silentFail = false } = {}) {
      if (!this.music) return;
      const p = this.music.audio.play();
      if (p && typeof p.then === 'function') {
        p.catch((err) => {
          // Navigateur bloque l'autoplay sans interaction → état pause, le
          // bouton play prend le relais. Ce n'est pas une erreur applicative.
          if (!silentFail) console.warn('[prodevis] music play rejected', err);
          this._musicRender();
        });
      }
    }

    _musicToggle() {
      if (!this.music) return;
      if (this.music.audio.paused) {
        this._musicPlay();
      } else {
        this.music.audio.pause();
      }
    }

    _musicNext() {
      if (!this.music) return;
      this._musicLoadTrack(this.music.index + 1);
      // Si on était déjà en lecture, on enchaîne. Sinon on respecte l'état
      // actuel (la cliente avait choisi de mettre en pause).
      if (!this.music.audio.paused) {
        this._musicPlay();
      } else if (this.music.audio.currentTime === 0) {
        // Cas "fin de piste" : ended déclenche _musicNext mais l'audio
        // passe d'abord en paused. On force le replay pour enchaîner.
        this._musicPlay({ silentFail: true });
      }
    }

    _musicRender() {
      if (!this.music || !this.musicEl) return;
      const track = this.music.tracks[this.music.index];
      const isPlaying = !this.music.audio.paused;

      const titleEl = this.musicEl.querySelector('[data-role="music-title"]');
      const artistEl = this.musicEl.querySelector('[data-role="music-artist"]');
      const coverImg = this.musicEl.querySelector('.pd-music__cover-img');
      const coverIcon = this.musicEl.querySelector('[data-role="music-icon"]');

      if (titleEl) titleEl.textContent = track?.title || '';
      if (artistEl) artistEl.textContent = track?.artist || '';
      if (coverImg) {
        const cover = this.music.playlist?.cover_image_url;
        if (cover && isSafeHttpUrl(cover)) {
          coverImg.src = cover;
          coverImg.alt = this._t('music.playlist', 'Playlist {title}', { title: this.music.playlist?.title || '' });
          coverImg.hidden = false;
        } else {
          coverImg.hidden = true;
        }
      }
      if (coverIcon) {
        // Icône play / pause SVG injecté dans le cover-icon overlay
        coverIcon.innerHTML = isPlaying
          ? '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M6 5h4v14H6zM14 5h4v14h-4z"/></svg>'
          : '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>';
      }
      this.musicEl.dataset.playing = isPlaying ? 'true' : 'false';
    }

    /* ── UI helpers ─────────────────────────────────────────────────── */

    /**
     * Écran d'attente. `text` remplace le libellé le temps d'une attente
     * (reprise du panier) ; sans lui, c'est celui de la section
     * (« Création de votre devis… »).
     */
    _showLoading(on, text) {
      if (!this.loadingEl) return;
      const label = this.loadingEl.querySelector('.pd-loading__text');
      if (label) {
        if (this._loadingLabel === undefined) this._loadingLabel = label.textContent;
        label.textContent = on && text ? text : this._loadingLabel;
      }
      this.loadingEl.hidden = !on;
      this.loadingEl.setAttribute('aria-hidden', on ? 'false' : 'true');
    }

    _showSuccess() {
      const el = document.getElementById(`pd-success-${this.sid}`);
      if (!el) return;

      // Devis validé : on efface le panier sauvegardé pour qu'un retour sur
      // la page ne propose pas de recharger un état déjà soumis.
      this._clearState();
      // Devis tiré du panier Shopify : ce panier-là est vidé aussi.
      if (this.fromCart) {
        this.fromCart = false;
        clearShopifyCart();
      }

      // Masque les étapes et la summary bar pour focus complet sur l'écran final.
      this.root.querySelectorAll('.pd-step, .pd-stepper').forEach((node) => { node.hidden = true; });
      if (this.summaryBar) this.summaryBar.hidden = true;
      // Le panier n'est plus modifiable à ce stade : le tiroir et son bouton
      // n'ont plus lieu d'être (et le récap du devis est dans l'email).
      this._closeCartDrawer();
      if (this.cartFab) this.cartFab.hidden = true;

      el.hidden = false;
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });

      // Bind les 2 actions (idempotent grâce au flag _bound)
      if (el._actionsBound) return;
      el._actionsBound = true;

      el.querySelector('[data-action="checkout-now"]')?.addEventListener('click', () => {
        if (this.devisResult && isSafeHttpUrl(this.devisResult.invoiceUrl)) {
          window.location.assign(this.devisResult.invoiceUrl);
        }
      });

      el.querySelector('[data-action="email-devis"]')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        if (!this.devisResult?.draftOrderId || !this.devisResult?.mailToken) {
          this._showToast(this.texts.missingId);
          return;
        }
        btn.disabled = true;
        const originalLabel = btn.innerHTML;
        btn.innerHTML = escapeHtml(this._t('success.sending', 'Envoi en cours…'));
        try {
          const url = `${this.cfg.backendUrl.replace(/\/$/, '')}/${this.devisResult.draftOrderId}/email`;
          const res = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // PAS de `to` : le backend l'ignore et envoie au customer.email
            // du draft. mailToken = autorisation cryptographique (Fix B).
            body: JSON.stringify({ mailToken: this.devisResult.mailToken }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            this._showToast(data?.error || this._t('toast.email_status', 'Échec envoi email ({status}).', { status: res.status }));
            btn.disabled = false;
            btn.innerHTML = originalLabel;
            return;
          }
          // Confirmation visuelle
          const confirmEl = el.querySelector('[data-role="email-confirm"]');
          const addrEl = el.querySelector('[data-role="email-confirm-address"]');
          if (addrEl) addrEl.textContent = this.devisResult.email;
          if (confirmEl) confirmEl.hidden = false;
          // Rend le bouton "Envoyé ✓" et le garde disabled
          btn.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg> '
            + escapeHtml(this._t('success.email_sent', 'Email envoyé'));
        } catch (err) {
          console.error('[prodevis] email error', err);
          this._showToast(this._t('toast.network_retry', 'Impossible de joindre le serveur. Réessaie.'));
          btn.disabled = false;
          btn.innerHTML = originalLabel;
        }
      });

      // Téléchargement PDF du récap — capture l'état au moment du clic
      // (avant que _clearState ne wipe les sélections au reload).
      el.querySelector('[data-action="download-pdf"]')?.addEventListener('click', async (e) => {
        const btn = e.currentTarget;
        const original = btn.innerHTML;
        btn.disabled = true;
        btn.innerHTML = escapeHtml(this._t('success.generating', 'Génération en cours…'));
        try {
          await this._downloadPdf();
        } catch (err) {
          console.error('[prodevis] pdf error', err);
          this._showToast(this._t('toast.pdf_failed', 'Impossible de générer le PDF. Réessaie.'));
        } finally {
          btn.disabled = false;
          btn.innerHTML = original;
        }
      });
    }

    /* ── PDF récap ──────────────────────────────────────────────── */

    /**
     * Lazy-load jsPDF depuis unpkg. ~100 ko, chargé uniquement au 1er clic
     * sur "Télécharger le récap PDF" pour ne pas peser sur le rendu initial.
     */
    async _loadJsPdf() {
      if (window.jspdf?.jsPDF) return window.jspdf.jsPDF;
      await new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-prodevis-jspdf]');
        if (existing) {
          existing.addEventListener('load', resolve, { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }
        const s = document.createElement('script');
        s.src = 'https://unpkg.com/jspdf@2.5.2/dist/jspdf.umd.min.js';
        s.async = true;
        s.dataset.prodevisJspdf = '1';
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
      });
      if (!window.jspdf?.jsPDF) throw new Error('jsPDF non chargé');
      return window.jspdf.jsPDF;
    }

    /**
     * Charge le logo noir Green Spa en Image pour insertion dans le PDF.
     * crossOrigin='anonymous' pour que le canvas qui sert d'intermédiaire
     * dans jsPDF ne soit pas tainted. Si CORS échoue → null, le PDF est
     * généré sans logo (texte seul).
     */
    async _loadPdfLogo() {
      const url = 'https://green-spa.com/cdn/shop/files/logoN.webp?v=1763563839&width=320';
      return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    }

    async _downloadPdf() {
      const JsPDF = await this._loadJsPdf();
      const logo = await this._loadPdfLogo();
      const doc = new JsPDF({ unit: 'mm', format: 'a4' });

      const pageWidth = 210;
      const marginX = 18;
      const contentWidth = pageWidth - marginX * 2;
      let y = 18;

      // En-tête : logo centré (sinon titre seul un peu plus haut)
      if (logo) {
        const logoW = 50;
        const ratio = logo.height / logo.width;
        const logoH = logoW * (isFinite(ratio) && ratio > 0 ? ratio : 0.4);
        doc.addImage(logo, 'WEBP', (pageWidth - logoW) / 2, y, logoW, logoH);
        y += logoH + 8;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.text(this.texts.pdfTitle, pageWidth / 2, y, { align: 'center' });
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(110);
      // Mois écrit dans la langue de la page (« 28. Oktober 2026 ») ; les
      // montants, eux, gardent le format français partout.
      const dateLocale = (isTranslated(this.i18n, this.lang) && this.i18n.dateLocales?.[this.lang]) || 'fr-FR';
      const dateStr = new Date().toLocaleDateString(dateLocale, { year: 'numeric', month: 'long', day: 'numeric' });
      doc.text(this._t('pdf.issued', 'Émis le {date}', { date: dateStr }), pageWidth / 2, y, { align: 'center' });
      doc.setTextColor(30);
      y += 10;

      // Coordonnées client
      const form = document.getElementById(`pd-customer-form-${this.sid}`);
      const get = (name) => (form?.querySelector(`[name="${name}"]`)?.value || '').trim();
      const customerLines = [];
      // Email : via _customerEmail() et non `get('email')`, pour que le PDF
      // affiche bien l'adresse du compte sur le réassort (champ caché rempli
      // par Liquid) même si l'input a été vidé — cf. le fix du reset devis.
      const email = this._customerEmail(); if (email) customerLines.push([this._t('form.email', 'Email'), email]);
      const siret = get('siret'); if (siret) customerLines.push([this.siretLabel, siret]);
      const siren = get('siren'); if (siren) customerLines.push([this._t('pdf.siren', 'SIREN'), siren]);
      const vat = get('vatNumber'); if (vat) customerLines.push([this._t('pdf.vat', 'TVA intracom.'), vat]);
      const fullName = [get('firstName'), get('lastName')].filter(Boolean).join(' ');
      if (fullName) customerLines.push([this._t('pdf.contact', 'Contact'), fullName]);
      const phone = get('phone'); if (phone) customerLines.push([this._t('form.phone', 'Téléphone'), phone]);
      const po = get('poNumber'); if (po) customerLines.push([this._t('pdf.po', 'Bon de commande'), po]);

      if (customerLines.length) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(this._t('pdf.contact_details', 'Coordonnées'), marginX, y);
        y += 5;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        for (const [label, value] of customerLines) {
          // « Libellé : valeur » en français, « Label: value » ailleurs.
          doc.text(this._t('pdf.field', '{label} : {value}', { label, value }), marginX, y);
          y += 5;
        }
        y += 4;
      }

      // Helpers de rendu d'une section panier
      // Intl écrit « 1 162,00 € » avec une espace fine insécable (U+202F)
      // entre les milliers et une insécable (U+00A0) avant « € ». L'Helvetica
      // de jsPDF (WinAnsi) ne sait encoder ni l'une ni l'autre : ramenées à
      // une espace simple. Avant, seule U+00A0 l'était et les montants de
      // 1 000 € et plus sortaient abîmés.
      const formatEurosPdf = (cents) => formatEuros(cents).replace(/[  ]/g, ' ');
      const ensurePageRoom = (need = 12) => {
        if (y + need > 280) { doc.addPage(); y = 20; }
      };
      // Prix barré : jsPDF n'a pas de text-decoration, on trace le trait à
      // la main. Le prix est aligné à droite sur `rightX`, donc il s'étend
      // de rightX-largeur à rightX. Le 1,2 mm au-dessus de la baseline
      // place le trait au milieu des chiffres (police 9 pt).
      const strikeRightAligned = (text, rightX, baselineY) => {
        const w = doc.getTextWidth(text);
        const midY = baselineY - 1.2;
        doc.setLineWidth(0.3);
        doc.line(rightX - w, midY, rightX, midY);
      };

      const drawSection = (title, lines, subtotalCents, subtotalLabel, offered = false) => {
        if (!lines.length) return;
        ensurePageRoom(20);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(title, marginX, y);
        y += 4;
        doc.setDrawColor(220);
        doc.line(marginX, y, marginX + contentWidth, y);
        y += 4;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        for (const line of lines) {
          const v = this.variantsById.get(Number(line.variantId));
          if (!v) continue;
          ensurePageRoom(7);
          const qty = Number(line.quantity) || 0;
          const lineTotal = (Number(v.priceCents) || 0) * qty;
          const names = this._lineNames(v);
          const titleText = `${names.product} — ${names.variant}`;
          // Tronque visuellement les titres très longs
          const titleLines = doc.splitTextToSize(titleText, contentWidth - 50);
          doc.text(titleLines, marginX, y);
          const qtyText = `× ${qty}`;
          const priceText = formatEurosPdf(lineTotal);
          doc.text(qtyText, marginX + contentWidth - 35, y, { align: 'right' });
          doc.text(priceText, marginX + contentWidth, y, { align: 'right' });
          // Section offerte : on barre le prix, comme à l'écran.
          if (offered) {
            doc.setDrawColor(120);
            strikeRightAligned(priceText, marginX + contentWidth, y);
            doc.setDrawColor(220);
          }
          y += Math.max(5, titleLines.length * 4);
        }
        y += 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(subtotalLabel, marginX, y);
        doc.text(formatEurosPdf(subtotalCents), marginX + contentWidth, y, { align: 'right' });
        y += 8;
        doc.setFont('helvetica', 'normal');
      };

      const purchaseLines = selectionToLines(this.purchaseSelection);
      const plvLines      = selectionToLines(this.plvSelection);
      const giftsLines    = selectionToLines(this.giftsSelection);
      const purchaseHt = computeTotalCents(purchaseLines, this.variantsById);
      const plvHt      = computeTotalCents(plvLines, this.variantsById);
      const giftsHt    = computeTotalCents(giftsLines, this.variantsById);

      const subtotalLabel = this._t('review.subtotal', 'Sous-total {tax}', { tax: this.taxLabel });
      const offeredLabel = this._t('review.offered_value', 'Valeur offerte');
      drawSection(this.reviewTitles.purchase, purchaseLines, purchaseHt, subtotalLabel);
      drawSection(this.reviewTitles.pdfPlv, plvLines, plvHt, offeredLabel, true);
      drawSection(this.reviewTitles.pdfGifts, giftsLines, giftsHt, offeredLabel, true);

      // Cadeaux automatiques (seuil, réassort) : dessinés à part de
      // drawSection() car ce ne sont PAS des sélections — ils n'ont pas de
      // variantId dans `variantsById`, c'est le serveur qui les pose sur le
      // devis à partir des SKU configurés. Même contenu et même état
      // (acquis / reste à atteindre) que le récap à l'écran, pour que le PDF
      // que la cliente télécharge corresponde à ce qu'elle vient de voir.
      const autoGiftLines = this._autoGiftLines();
      if (autoGiftLines.length && this.autoGiftMinCents > 0) {
        const discountedHt = this._discountedHtCents(purchaseHt);
        const reached = discountedHt >= this.autoGiftMinCents;
        const autoGiftTotal = autoGiftLines.reduce((sum, g) => sum + (Number(g.priceCents) || 0), 0);

        ensurePageRoom(28);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(11);
        doc.text(this.autoGiftLabel, marginX, y);
        y += 4;
        doc.setDrawColor(220);
        doc.line(marginX, y, marginX + contentWidth, y);
        y += 4;

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(8);
        doc.setTextColor(110);
        const note = this._autoGiftNote(reached, Math.max(0, this.autoGiftMinCents - discountedHt), formatEurosPdf);
        const noteLines = doc.splitTextToSize(note, contentWidth);
        doc.text(noteLines, marginX, y);
        y += noteLines.length * 4 + 1;
        doc.setTextColor(30);

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        for (const g of autoGiftLines) {
          ensurePageRoom(7);
          const titleText = `${g.productTitle || ''} — ${g.variantTitle || ''}`;
          const titleLines = doc.splitTextToSize(titleText, contentWidth - 50);
          doc.text(titleLines, marginX, y);
          doc.text('× 1', marginX + contentWidth - 35, y, { align: 'right' });
          const giftPriceText = formatEurosPdf(Number(g.priceCents) || 0);
          doc.text(giftPriceText, marginX + contentWidth, y, { align: 'right' });
          doc.setDrawColor(120);
          strikeRightAligned(giftPriceText, marginX + contentWidth, y);
          doc.setDrawColor(220);
          y += Math.max(5, titleLines.length * 4);
        }

        y += 2;
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(10);
        doc.text(offeredLabel, marginX, y);
        doc.text(formatEurosPdf(autoGiftTotal), marginX + contentWidth, y, { align: 'right' });
        y += 8;
        doc.setFont('helvetica', 'normal');
      }

      // Remises : celles de l'écran, calculées par `_cartTotals` (remises
      // clientes du réassort, paliers de la remise entreprise, remise
      // distributeur par ligne). Le PDF n'appliquait que les remises
      // clientes : sur les pages distributeur et cadeaux d'entreprise, son
      // total était le prix avant remise, plus haut que celui de l'écran.
      // Sans remise (ouverture), rien n'est dessiné et le total vaut le HT.
      const { rows: discountRows, finalHt } = this._cartTotals();
      if (discountRows.length && purchaseHt > 0) {
        const lineHeight = 6;
        // Réserve l'espace : 1 ligne sous-total + N lignes remise + total + footer.
        ensurePageRoom(8 + lineHeight * (discountRows.length + 2) + 16);
        // Sous-total HT (avant remise)
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(11);
        doc.setTextColor(60);
        doc.text(subtotalLabel, marginX, y);
        doc.text(formatEurosPdf(purchaseHt), marginX + contentWidth, y, { align: 'right' });
        y += lineHeight;
        // Une ligne par remise
        doc.setTextColor(168, 134, 78); // accent doré (cf. CSS pd-accent-dark)
        for (const row of discountRows) {
          doc.text(row.label, marginX, y);
          doc.text(`-${formatEurosPdf(row.cents)}`, marginX + contentWidth, y, { align: 'right' });
          y += lineHeight;
        }
        doc.setTextColor(30);
        y += 2;
      }

      // Total final
      ensurePageRoom(28);
      doc.setDrawColor(120);
      doc.setLineWidth(0.5);
      doc.line(marginX, y, marginX + contentWidth, y);
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
      doc.text(this._t('review.total', 'Total {tax}', { tax: this.taxLabel }), marginX, y);
      doc.text(formatEurosPdf(finalHt), marginX + contentWidth, y, { align: 'right' });
      y += 6;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(110);
      doc.text(this.taxNote, marginX, y);
      doc.setTextColor(30);
      y += 10;

      // Pied de page
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(140);
      const footer = this.devisResult?.draftOrderId
        ? this._t('pdf.field', '{label} : {value}', { label: this.texts.pdfRefLabel, value: this.devisResult.draftOrderId })
        : this.texts.pdfFooter;
      doc.text(footer, pageWidth / 2, 290, { align: 'center' });

      // Nom de fichier : YYYY-MM-DD-devis-green-spa-<draft>.pdf
      const isoDate = new Date().toISOString().slice(0, 10);
      const suffix = this.devisResult?.draftOrderId ? `-${this.devisResult.draftOrderId}` : '';
      doc.save(`${isoDate}-${this.texts.pdfFilePrefix}${suffix}.pdf`);
    }

    _showToast(message, durationMs = 7000) {
      if (!this.toastEl) {
        console.warn('[prodevis]', message);
        return;
      }
      this.toastEl.querySelector('.pd-toast__message').textContent = message;
      this.toastEl.hidden = false;
      clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => this._hideToast(), durationMs);
    }

    _hideToast() {
      if (this.toastEl) this.toastEl.hidden = true;
    }

    /* ── Avertissement chaleur (popup) ──
       Affiché à la première transition 0→positif sur une variante d'un
       produit qui matche heatWarningRegex. Modal portalé dans <body> par
       _portalFloatingElements (cf. id "pd-heat-warning-…" dans la section
       Liquid). On stocke (kind, variantId) en dataset pour pouvoir
       revenir en arrière sur "Annuler". */
    _showHeatWarning(kind, variantId) {
      if (!this.heatWarningEl) return;
      const hw = this.cfg.heatWarning || {};
      const titleEl = this.heatWarningEl.querySelector('[data-role="heat-warning-title"]');
      const textEl = this.heatWarningEl.querySelector('[data-role="heat-warning-text"]');
      // Réglages de la section : leur défaut français se traduit par le
      // dictionnaire (`sources`), une valeur personnalisée reste telle quelle.
      if (titleEl) titleEl.textContent = this._setting(hw.title || '');
      if (textEl) textEl.textContent = this._setting(hw.text || '');
      this.heatWarningEl.dataset.kind = kind;
      this.heatWarningEl.dataset.variantId = String(variantId);
      this.heatWarningEl.hidden = false;
    }

    _acceptHeatWarning() {
      if (!this.heatWarningEl) return;
      const variantId = Number(this.heatWarningEl.dataset.variantId || 0);
      const variant = this.variantsById.get(variantId);
      // On marque le PRODUIT (pas la variante) comme déjà vu → ne pas
      // re-popper si la cliente ajoute une autre variante du même beurre.
      if (variant && variant.productId != null) {
        this.heatWarningShownProducts.add(variant.productId);
      }
      this.heatWarningEl.hidden = true;
    }

    _cancelHeatWarning() {
      if (!this.heatWarningEl) return;
      const kind = this.heatWarningEl.dataset.kind;
      const variantId = Number(this.heatWarningEl.dataset.variantId || 0);
      this.heatWarningEl.hidden = true;
      // Revert à 0. Pas dans le set "shown" → si la cliente retente plus
      // tard, le popup re-déclenche (elle peut changer d'avis).
      if (kind && variantId) this._setQty(kind, variantId, 0);
    }

    /* ── Popup de blocage chaleur ──
       Ouvert quand la cliente tente d'ajouter un produit bloqué (option
       "block"). Purement informatif : un seul bouton "J'ai compris", aucun
       ajout possible. Le texte vient de cfg.heatWarning.blockText. Modal
       portalé dans <body> (id "pd-heat-block-…"). */
    _showHeatBlock() {
      if (!this.heatBlockEl) return;
      const hw = this.cfg.heatWarning || {};
      const textEl = this.heatBlockEl.querySelector('[data-role="heat-block-text"]');
      if (textEl) textEl.textContent = this._setting(hw.blockText || '');
      this.heatBlockEl.hidden = false;
    }

    _hideHeatBlock() {
      if (this.heatBlockEl) this.heatBlockEl.hidden = true;
    }

    /* ── Tiroir panier (lecture seule) ──────────────────────────────── */

    /**
     * Bouton flottant + tiroir récapitulatif, construits en JS et accrochés
     * à <body>.
     *
     * Pourquoi pas dans le Liquid : il faudrait le dupliquer dans les QUATRE
     * sections (ouverture, réassort, et les deux distributeur) alors qu'il
     * n'a aucun texte configurable par le marchand — les titres de groupes
     * sont lus sur le stepper de la page, donc ils suivent déjà les réglages
     * de chaque flow. Même raisonnement que la lightbox : un fichier de
     * moins à coller dans le thème, et zéro risque de désynchronisation
     * entre les quatre copies.
     *
     * Le tiroir est en LECTURE SEULE, volontairement : les quantités se
     * règlent dans les étapes, où la cliente voit les budgets PLV/cadeaux,
     * les ruptures et les blocages chaleur. Rejouer ces règles dans un
     * tiroir, c'est deux chemins à garder d'accord pour rien.
     */
    _ensureCartUi() {
      if (this.cartFab) return;

      const fab = document.createElement('button');
      fab.type = 'button';
      fab.className = 'pd-cart-fab';
      fab.id = `pd-cart-fab-${this.sid}`;
      fab.setAttribute('aria-haspopup', 'dialog');
      fab.setAttribute('aria-expanded', 'false');
      fab.setAttribute('aria-controls', `pd-cart-drawer-${this.sid}`);
      fab.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/>
          <path d="M3 6h18"/>
          <path d="M16 10a4 4 0 0 1-8 0"/>
        </svg>
        <span class="pd-cart-fab__badge" data-role="cart-count" hidden>0</span>
      `;
      fab.addEventListener('click', () => this._openCartDrawer());

      const drawer = document.createElement('div');
      drawer.className = 'pd-cart-drawer';
      drawer.id = `pd-cart-drawer-${this.sid}`;
      drawer.hidden = true;
      drawer.setAttribute('role', 'dialog');
      drawer.setAttribute('aria-modal', 'true');
      drawer.setAttribute('aria-labelledby', `pd-cart-drawer-title-${this.sid}`);
      drawer.innerHTML = `
        <div class="pd-cart-drawer__backdrop" data-action="cart-close"></div>
        <aside class="pd-cart-drawer__panel" tabindex="-1">
          <header class="pd-cart-drawer__head">
            <div>
              <h3 class="pd-cart-drawer__title" id="pd-cart-drawer-title-${this.sid}">${escapeHtml(this._t('cart.title', 'Votre panier'))}</h3>
              <p class="pd-cart-drawer__note">${escapeHtml(this._t('cart.note', 'Pour modifier les quantités, revenez aux étapes.'))}</p>
            </div>
            <button type="button" class="pd-cart-drawer__close" data-action="cart-close" aria-label="${escapeHtml(this._t('cart.close', 'Fermer le panier'))}">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
            </button>
          </header>
          <div class="pd-cart-drawer__body" data-role="cart-body"></div>
          <footer class="pd-cart-drawer__foot" data-role="cart-foot"></footer>
        </aside>
      `;
      drawer.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="cart-close"]')) this._closeCartDrawer();
      });

      // Bulle « précommande », posée sous le bouton : seulement si la
      // section en règle le texte (ouverture et réassort). Cachée tant que
      // le panier n'a pas de produit en précommande (_updatePreorderNote).
      let note = null;
      if (this.preorderNoteText) {
        note = document.createElement('div');
        note.className = 'pd-preorder-note';
        note.id = `pd-preorder-note-${this.sid}`;
        note.hidden = true;
        note.setAttribute('role', 'status');
        note.innerHTML = `
          <svg class="pd-preorder-note__icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>
          <p class="pd-preorder-note__text">${escapeHtml(this.preorderNoteText)}</p>
          <button type="button" class="pd-preorder-note__close" aria-label="${escapeHtml(this._t('preorder.close', 'Masquer ce message'))}">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        `;
        note.querySelector('.pd-preorder-note__close').addEventListener('click', () => {
          // Fermée pour les précommandes déjà au panier : elle revient à
          // l'ajout d'une autre, ou au prochain chargement de la page. Rien
          // n'est mémorisé : fermée pour tout l'onglet, et sur les deux
          // pages à la fois, elle passait pour une panne.
          this._preorderNoteDismissed = new Set(this._preorderIdsInCart());
          this._updatePreorderNote();
        });
      }

      // Variables CSS : le bouton et le tiroir vivent hors de
      // .prodevis-section après le déplacement dans <body>, donc ils
      // n'héritent plus de la palette. Même traitement que les autres
      // éléments portalés (cf. _portalFloatingElements).
      const cs = getComputedStyle(this.root);
      ['--pd-primary', '--pd-primary-hover', '--pd-accent-dark', '--pd-white',
       '--pd-text', '--pd-text-muted', '--pd-border', '--pd-bg',
       '--pd-radius', '--pd-radius-sm', '--pd-transition'].forEach((v) => {
        const val = cs.getPropertyValue(v).trim();
        if (!val) return;
        fab.style.setProperty(v, val);
        drawer.style.setProperty(v, val);
        if (note) note.style.setProperty(v, val);
      });

      document.body.appendChild(fab);
      document.body.appendChild(drawer);
      if (note) document.body.appendChild(note);
      this.cartFab = fab;
      this.cartDrawer = drawer;
      this.preorderNote = note;

      this._themeCartIcon = this._findThemeCartIcon();
      this._positionCartFab();

      // Suivi de position : rAF pour ne pas lire le layout à chaque event
      // de scroll (reflow forcé sur mobile bas de gamme).
      let ticking = false;
      const reposition = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          ticking = false;
          this._positionCartFab();
        });
      };
      window.addEventListener('scroll', reposition, { passive: true });
      window.addEventListener('resize', reposition, { passive: true });
      // Le header met parfois une frame à se stabiliser (polices, barre
      // d'annonce, apps) : une mesure différée rattrape le décalage.
      setTimeout(() => this._positionCartFab(), 400);
    }

    /**
     * Icône panier du thème, que notre bouton vient recouvrir.
     *
     * Elle ne sert à rien sur ces pages : le parcours ne passe pas par le
     * panier Shopify, il produit un devis. La cliente qui cliquait dessus
     * tombait sur un panier vide — autant mettre le nôtre par-dessus.
     *
     * Recherche tolérante (les thèmes ne nomment pas pareil) mais bornée au
     * haut du document, sinon on attraperait un lien « Panier » de pied de
     * page.
     */
    _findThemeCartIcon() {
      const SELECTORS = [
        '#cart-icon-bubble',
        '[data-cart-icon]',
        '.header__icon--cart',
        '.site-header__cart',
        'a[href$="/cart"]',
        'a[href*="/cart?"]',
      ].join(',');
      let best = null;
      for (const el of document.querySelectorAll(SELECTORS)) {
        if (el.closest('.pd-cart-fab, .prodevis-section')) continue;
        const r = el.getBoundingClientRect();
        if (!r.width || !r.height) continue;            // masqué (menu replié…)
        const docTop = r.top + window.scrollY;
        if (docTop > 400) continue;                     // pas dans le header
        if (!best || docTop < best.docTop) best = { el, docTop };
      }
      return best ? best.el : null;
    }

    /**
     * Bas du header du thème, en coordonnées écran. 0 si on ne le trouve
     * pas ou s'il a déjà défilé hors de vue. Sert uniquement de repli de
     * placement quand le thème n'expose pas d'icône panier.
     */
    _headerBottom() {
      const header = document.querySelector('header, .site-header, #shopify-section-header, [role="banner"]');
      if (!header) return 0;
      const r = header.getBoundingClientRect();
      // Un header très haut (hero plein écran) ne doit pas expédier le
      // bouton au milieu de la page.
      if (r.bottom <= 0 || r.bottom > window.innerHeight / 3) return 0;
      return r.bottom;
    }

    /**
     * Aligne le bouton flottant sur l'icône panier du thème tant qu'elle est
     * à l'écran — c'est ce qu'on voit en arrivant sur la page. Header sticky :
     * le bouton la suit. Header qui défile : le bouton se range dans le coin
     * haut-droit et reste atteignable.
     */
    _positionCartFab() {
      const fab = this.cartFab;
      if (!fab || fab.hidden) return;

      const size = fab.offsetWidth || 44;
      const icon = this._themeCartIcon;
      let docked = true;

      if (icon && icon.isConnected) {
        const r = icon.getBoundingClientRect();
        if (r.width && r.height) {
          // Position horizontale : mémorisée dès la première mesure utile,
          // elle ne bouge pas au scroll — éviter de la recalculer évite
          // les micro-sauts quand le header change de hauteur.
          this._fabRight = Math.max(8, Math.round(window.innerWidth - (r.left + r.width / 2) - size / 2));
          // Verticale : on ne suit l'icône que si elle est encore visible
          // dans le tiers haut de l'écran.
          const top = Math.round(r.top + r.height / 2 - size / 2);
          if (r.bottom > 0 && top < window.innerHeight / 3) {
            fab.style.setProperty('--pd-fab-top', `${Math.max(6, top)}px`);
            docked = false;
          }
        }
      }

      if (docked) {
        // Coin haut-droit, sous l'encoche iOS le cas échéant. Si le thème
        // n'a AUCUNE icône panier à recouvrir, on se pose plutôt juste sous
        // le header : le coin y est déjà pris par le menu burger.
        const under = icon ? 0 : this._headerBottom();
        fab.style.setProperty('--pd-fab-top', under > 0
          ? `${Math.round(under) + 8}px`
          : 'calc(10px + env(safe-area-inset-top, 0px))');
      }
      fab.style.setProperty('--pd-fab-right', `${this._fabRight != null ? this._fabRight : 12}px`);

      // La bulle « précommande » suit le bouton : juste dessous, bord droit
      // aligné, sa pointe sur le centre du bouton.
      const note = this.preorderNote;
      if (note && !note.hidden) {
        const fabBox = fab.getBoundingClientRect();
        note.style.setProperty('--pd-note-top', `${Math.round(fabBox.bottom + 10)}px`);
        note.style.setProperty('--pd-note-right', `${this._fabRight != null ? this._fabRight : 12}px`);
      }
    }

    /**
     * Bulle « précommande » : visible quand le panier contient au moins un
     * produit en précommande que la cliente n'a pas déjà vu (croix cliquée
     * alors qu'il y était). Seule l'étape d'achat compte : la PLV et les
     * cadeaux n'acceptent que du stock réel (cf. _isOfferable). Avant la
     * réponse de /inventory, tout passe pour en stock : la bulle attend donc
     * ce retour (_fetchInventory la rappelle).
     */
    _updatePreorderNote() {
      const note = this.preorderNote;
      if (!note) return;
      const ids = this._preorderIdsInCart();
      // Plus de précommande au panier : la prochaine refera venir la bulle.
      if (!ids.length) this._preorderNoteDismissed = null;
      const seen = this._preorderNoteDismissed;
      const hasNew = ids.some((id) => !(seen && seen.has(id)));
      const show = hasNew && !(this.cartFab && this.cartFab.hidden);
      if (note.hidden === !show) return;
      note.hidden = !show;
      if (show) this._positionCartFab();
    }

    /** Variantes en précommande dans le panier d'achat. */
    _preorderIdsInCart() {
      return [...this.purchaseSelection.keys()]
        .map(Number)
        .filter((id) => this._variantStockState(id) === 'preorder');
    }

    /**
     * Les 3 sélections, titrées comme le stepper de CETTE page — déjà
     * traduit au démarrage s'il affichait encore ses défauts français.
     */
    _cartGroups() {
      const stepLabel = (target, fallback) => {
        const el = this.root.querySelector(`.pd-stepper__step[data-target="${target}"] .pd-stepper__label`);
        return (el?.textContent || '').trim() || fallback;
      };
      return buildCartGroups([
        { key: 'purchase', title: stepLabel(1, this._t('step.products', 'Vos produits')), selection: this.purchaseSelection },
        { key: 'plv', title: stepLabel(2, this._t('step.plv', 'Votre PLV offerte')), selection: this.plvSelection, offered: true },
        { key: 'gifts', title: stepLabel(3, this._t('step.gifts', 'Vos cadeaux')), selection: this.giftsSelection, offered: true },
      ], this.variantsById);
    }

    /**
     * Totaux du tiroir.
     *
     * ⚠ Même calcul que `_updateDiscountRow` (summary bar) et que
     * `_renderReview` (récap étape 4) : remise arrondie AU CENT sur le HT
     * d'achat, remises cumulées, total plancher à 0. Si tu touches à la
     * règle, touche aux trois — un tiroir qui annonce un autre total que
     * la barre du bas, c'est un appel au SAV.
     */
    _cartTotals() {
      const purchaseHt = computeTotalCents(selectionToLines(this.purchaseSelection), this.variantsById);
      const rows = (this.applicableDiscounts || []).map((d) => ({
        label: `${d.title} (-${d.percentage.toFixed(2).replace('.', ',')} %)`,
        cents: Math.round(purchaseHt * d.percentage / 100),
      }));
      const tierRow = this._tierDiscountRow(purchaseHt);
      if (tierRow) rows.push(tierRow);
      const distributorCents = this._distributorDiscountCents();
      if (distributorCents > 0) rows.push({ label: this._t('discount.distributor', 'Remise distributeur'), cents: distributorCents });
      const finalHt = Math.max(0, rows.reduce((sum, r) => sum - r.cents, purchaseHt));
      return { purchaseHt, rows, finalHt };
    }

    /**
     * Pastille du bouton + contenu du tiroir s'il est ouvert. Appelé par
     * `_updateSummary`, qui est déjà le point de passage de TOUT changement
     * de panier — pas besoin d'accrocher quoi que ce soit ailleurs.
     */
    _updateCartUi() {
      if (!this.cartFab) return;
      const { itemCount } = this._cartGroups();
      const badge = this.cartFab.querySelector('[data-role="cart-count"]');
      if (badge) {
        badge.textContent = itemCount > 99 ? '99+' : String(itemCount);
        badge.hidden = itemCount === 0;
      }
      this.cartFab.setAttribute('aria-label',
        itemCount === 0 ? this._t('cart.open_empty', 'Voir mon panier (vide)')
          : this._t('cart.open', {
            one: 'Voir mon panier ({count} article)',
            other: 'Voir mon panier ({count} articles)',
          }, { count: itemCount }));
      if (this.cartDrawer && !this.cartDrawer.hidden) this._renderCartDrawer();
      this._updatePreorderNote();
    }

    _renderCartDrawer() {
      const body = this.cartDrawer?.querySelector('[data-role="cart-body"]');
      const foot = this.cartDrawer?.querySelector('[data-role="cart-foot"]');
      if (!body || !foot) return;

      const { groups, itemCount } = this._cartGroups();

      if (!itemCount) {
        body.innerHTML = `
          <div class="pd-cart-drawer__empty">
            <p>${escapeHtml(this._t('cart.empty', 'Votre panier est vide.'))}</p>
            <p>${escapeHtml(this._t('cart.empty_hint', 'Ajoutez des produits depuis les étapes pour les retrouver ici.'))}</p>
          </div>
        `;
        foot.innerHTML = '';
        return;
      }

      body.innerHTML = groups.filter((g) => g.lines.length).map((group) => {
        const linesHtml = group.lines.map((line) => {
          // Noms affichés : buildCartGroups ne porte que les titres français.
          const v = this.variantsById.get(line.variantId);
          const names = v ? this._lineNames(v) : { product: line.productTitle, variant: line.variantTitle };
          return `
          <li class="pd-cart-line">
            ${isSafeHttpUrl(line.image)
              ? `<img class="pd-cart-line__img" src="${escapeHtml(line.image)}" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer">`
              : '<div class="pd-cart-line__img pd-cart-line__img--empty" aria-hidden="true"></div>'}
            <div class="pd-cart-line__text">
              <span class="pd-cart-line__product">${escapeHtml(names.product)}</span>
              <span class="pd-cart-line__variant">${escapeHtml(names.variant)}</span>
            </div>
            <div class="pd-cart-line__amounts">
              <span class="pd-cart-line__qty">×${line.quantity}</span>
              <span class="pd-cart-line__price${group.offered ? ' pd-cart-line__price--offered' : ''}">${formatEuros(line.lineCents)}</span>
            </div>
          </li>
        `;
        }).join('');
        // Un groupe offert n'a pas de sous-total ici : ses prix sont barrés,
        // en faire la somme remettrait un montant à lire alors que la
        // cliente ne paye rien. Le récap de l'étape 4, lui, garde sa ligne
        // « Valeur offerte » — c'est la pièce qui chiffre ce que l'offre
        // représente, et le devis Shopify la reprend.
        return `
          <section class="pd-cart-group${group.offered ? ' pd-cart-group--offered' : ''}">
            <h4 class="pd-cart-group__title">
              <span>${escapeHtml(group.title)}</span>
              ${group.offered ? `<span class="pd-cart-group__tag">${escapeHtml(this._t('cart.offered', 'Offert'))}</span>` : ''}
            </h4>
            <ul class="pd-cart-lines">${linesHtml}</ul>
            ${group.offered ? '' : `
            <div class="pd-cart-group__subtotal">
              <span>${escapeHtml(this._t('review.subtotal', 'Sous-total {tax}', { tax: this.taxLabel }))}</span>
              <strong>${formatEuros(group.subtotalCents)}</strong>
            </div>`}
          </section>
        `;
      }).join('');

      const { purchaseHt, rows, finalHt } = this._cartTotals();
      const rowsHtml = rows.map((r) => `
        <div class="pd-cart-total__row pd-cart-total__row--discount">
          <span>${escapeHtml(r.label)}</span>
          <strong>-${formatEuros(r.cents)}</strong>
        </div>
      `).join('');
      foot.innerHTML = `
        ${rows.length ? `
          <div class="pd-cart-total__row">
            <span>${escapeHtml(this._t('review.subtotal', 'Sous-total {tax}', { tax: this.taxLabel }))}</span>
            <strong>${formatEuros(purchaseHt)}</strong>
          </div>
          ${rowsHtml}` : ''}
        <div class="pd-cart-total__row pd-cart-total__row--final">
          <span>${escapeHtml(this._t('review.total', 'Total {tax}', { tax: this.taxLabel }))}</span>
          <strong>${formatEuros(finalHt)}</strong>
        </div>
        <div class="pd-cart-total__note">${escapeHtml(this.taxNote)}</div>
        <button type="button" class="pd-btn pd-btn--primary pd-cart-drawer__continue" data-action="cart-close">
          ${escapeHtml(this._t('cart.continue', 'Continuer ma commande'))}
        </button>
      `;
    }

    _openCartDrawer() {
      this._ensureCartUi();
      const drawer = this.cartDrawer;
      // La classe fait foi, pas `hidden` : pendant les 260 ms de glissade de
      // fermeture le tiroir est encore visible mais plus ouvert. Se fier à
      // `hidden` ferait ignorer une réouverture dans cet intervalle, et le
      // timer de fermeture finissait par le masquer — bouton mort.
      if (!drawer || drawer.classList.contains('pd-cart-drawer--open')) return;
      clearTimeout(this._cartCloseTimer);

      this._renderCartDrawer();
      drawer.hidden = false;
      // Une frame avant d'armer la transition, sinon le panneau apparaît
      // déjà en place (le navigateur n'a pas d'état de départ à animer).
      requestAnimationFrame(() => drawer.classList.add('pd-cart-drawer--open'));
      document.documentElement.classList.add('pd-cart-drawer-open');
      this.cartFab?.setAttribute('aria-expanded', 'true');

      if (!this._cartKeyHandler) {
        this._cartKeyHandler = (e) => {
          if (e.key === 'Escape') return this._closeCartDrawer();
          if (e.key !== 'Tab') return;
          // Piège à focus : sans ça, Tab sort du tiroir et part naviguer
          // dans la page cachée derrière — le lecteur d'écran suit, la
          // cliente est perdue.
          const focusables = drawer.querySelectorAll('button:not([disabled]), [href], input, [tabindex]:not([tabindex="-1"])');
          if (!focusables.length) return;
          const first = focusables[0];
          const last = focusables[focusables.length - 1];
          if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        };
      }
      document.addEventListener('keydown', this._cartKeyHandler);
      // Focus sur le panneau, pas sur la croix : le lecteur d'écran annonce
      // le tiroir et la première tabulation tombe sur « Fermer », sans
      // afficher un anneau de focus à celle qui a ouvert à la souris.
      drawer.querySelector('.pd-cart-drawer__panel')?.focus();
    }

    _closeCartDrawer() {
      const drawer = this.cartDrawer;
      if (!drawer || drawer.hidden) return;
      drawer.classList.remove('pd-cart-drawer--open');
      document.documentElement.classList.remove('pd-cart-drawer-open');
      this.cartFab?.setAttribute('aria-expanded', 'false');
      if (this._cartKeyHandler) document.removeEventListener('keydown', this._cartKeyHandler);
      // `hidden` seulement à la fin de la glissade, sinon le panneau
      // disparaît d'un coup au lieu de sortir par la droite.
      clearTimeout(this._cartCloseTimer);
      this._cartCloseTimer = setTimeout(() => { drawer.hidden = true; }, 260);
      this.cartFab?.focus();
    }

    /* ── Lightbox photo produit ─────────────────────────────────────── */

    /**
     * Crée (une seule fois) la lightbox et l'accroche à <body>.
     *
     * Contrairement aux autres popups du module, elle est construite en JS
     * plutôt que déclarée dans les deux sections Liquid : elle n'a aucun
     * texte configurable par le marchand, donc la dupliquer dans les deux
     * sections n'apporterait rien — et ça évite un fichier de plus à coller
     * dans le thème.
     */
    _ensureLightbox() {
      if (this.lightboxEl) return this.lightboxEl;

      const el = document.createElement('div');
      el.className = 'pd-lightbox';
      el.hidden = true;
      el.setAttribute('role', 'dialog');
      el.setAttribute('aria-modal', 'true');
      el.setAttribute('aria-label', this._t('lightbox.label', 'Photo du produit'));
      el.innerHTML = `
        <div class="pd-lightbox__backdrop" data-action="lightbox-close"></div>
        <div class="pd-lightbox__panel">
          <button class="pd-lightbox__close" type="button" data-action="lightbox-close" aria-label="${escapeHtml(this._t('common.close', 'Fermer'))}">&times;</button>
          <img class="pd-lightbox__img" alt="" decoding="async">
          <p class="pd-lightbox__caption"></p>
        </div>
      `;
      el.addEventListener('click', (e) => {
        if (e.target.closest('[data-action="lightbox-close"]')) this._closeLightbox();
      });
      document.body.appendChild(el);

      this.lightboxEl = el;
      return el;
    }

    /**
     * Ouvre la photo actuellement affichée sur une carte — donc celle de la
     * variante si la cliente vient d'en choisir une (cf.
     * _previewVariantImage). La légende reprend le `alt`, qui vaut déjà
     * « Produit — Variante » dans ce cas.
     */
    _openLightbox(img) {
      const src = img.getAttribute('src');
      if (!src) return;

      const el = this._ensureLightbox();
      const large = el.querySelector('.pd-lightbox__img');
      const caption = el.querySelector('.pd-lightbox__caption');
      const alt = img.getAttribute('alt') || '';
      large.setAttribute('src', src);
      large.setAttribute('alt', alt);
      caption.textContent = alt;

      this._lightboxOpener = img;
      el.hidden = false;
      // Bloque le scroll de la page derrière la photo plein écran (les
      // popups chaleur ne le font pas, mais eux tiennent dans un panneau
      // court : ici l'image occupe tout l'écran, scroller dessous est
      // désorientant).
      document.documentElement.classList.add('pd-lightbox-open');

      if (!this._lightboxKeyHandler) {
        this._lightboxKeyHandler = (e) => {
          if (e.key === 'Escape') this._closeLightbox();
        };
      }
      document.addEventListener('keydown', this._lightboxKeyHandler);
      el.querySelector('.pd-lightbox__close')?.focus();
    }

    _closeLightbox() {
      if (!this.lightboxEl || this.lightboxEl.hidden) return;
      this.lightboxEl.hidden = true;
      document.documentElement.classList.remove('pd-lightbox-open');
      if (this._lightboxKeyHandler) {
        document.removeEventListener('keydown', this._lightboxKeyHandler);
      }
      // Rend le focus à la photo cliquée : sans ça il repart en début de
      // page et la cliente perd sa position dans le catalogue.
      this._lightboxOpener?.focus?.();
      this._lightboxOpener = null;
    }
  }

  /* ── Boot ────────────────────────────────────────────────────────── */

  /**
   * Supprime les widgets des apps tierces "Sales Conversion Co — Free Gifts /
   * Bogos" qui injectent une bulle flottante + popups. La règle CSS dans
   * prodevis.css ne suffit pas : l'app force display:flex via un style inline
   * !important au moment où le widget devient visible, ce qui défait n'importe
   * quel display:none de stylesheet (l'inline !important gagne sur l'auteur
   * !important). On les retire donc carrément du DOM, et on observe pour
   * intercepter les ré-injections async de l'app.
   */
  function removeThirdPartyGiftWidgets() {
    const SELECTORS = '[id^="sca-fg-"], [id^="bogos-"], [id^="freegifts-"]';
    const sweep = (root) => {
      if (!root || root.nodeType !== 1) return;
      if (root.matches && root.matches(SELECTORS)) {
        root.remove();
        return;
      }
      root.querySelectorAll?.(SELECTORS).forEach((el) => el.remove());
    };
    sweep(document.body);

    // L'app peut ré-injecter ses nodes plus tard (lazy init, après DOM ready).
    // MutationObserver continue à les retirer dès qu'ils réapparaissent.
    if (!('MutationObserver' in window)) return;
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        m.addedNodes.forEach(sweep);
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function boot() {
    const cfg = window.proDevis;
    if (!cfg) return;
    Object.keys(cfg).forEach((sectionId) => {
      try {
        new ProDevis(sectionId);
      } catch (e) {
        console.error('[prodevis] init error', sectionId, e);
      }
    });
    // Cleanup widgets tiers — uniquement quand la section prodevis est sur
    // la page (boot est appelé seulement si window.proDevis existe).
    removeThirdPartyGiftWidgets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();